import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { attachBasemapWithFailover, basemapTiers } from './basemapFailover';
import { VEHICLE_CATEGORIES } from './vehicleCategories';
import { MATCHING_PACKAGE } from '../../util/platformMessages';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';

import {
  PURPOSES,
  CHAT_CONSENT_VERSION,
  CRM_CONSENT_VERSION,
  TRANSCRIPTION_CONSENT_VERSION,
  isConsentAvailable,
  hasRememberedConsent,
  hasRememberedDecline,
  rememberConsent,
  rememberDecline,
  forgetConsent,
} from '../../util/aiConsent';
import AiConsentGate from '../../components/AiConsentGate/AiConsentGate';
import FindABusCta from '../../components/BusRequestForm/FindABusCta';
import ShopNewByBrand from '../../components/ShopNewByBrand/ShopNewByBrand';
import { userLocation } from '../../util/maps';
import { vehiclePath } from '../../util/vehicleLinks';
import { verificationState } from '../../util/listingVerification';
import {
  lookupPostalCode,
  searchUrlForLocation,
  normalizePostal,
  LOOKUP,
  MESSAGE_IDS,
} from '../../util/postalSearch';

import css from './SkipLandingPage.module.css';

const c = {
  teal: '#1A9E85',
  tealDark: '#157A68',
  tealLight: 'rgba(26,158,133,0.1)',
  tealBorder: 'rgba(26,158,133,0.25)',
  navy: '#0F2033',
  text: '#1B2B3A',
  textMid: '#4A5E6E',
  textLight: '#7A8C99',
  white: '#FFFFFF',
  bg: '#F5F9FC',
  bgAlt: '#EBF3F0',
  border: '#DDE7EE',
  heroBg: 'linear-gradient(145deg, #DFF0ED 0%, #E8F3F8 40%, #F0F7F2 100%)',
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

function BuyerMatchCounter() {
  const sequence = [3, 5, 8, 14, 24, 38, 47, 61, 79, 94];
  const [idx, setIdx] = useState(0);
  const [display, setDisplay] = useState(3);
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-60px' });

  useEffect(() => {
    if (!inView) return;
    const interval = setInterval(() => {
      setIdx(i => {
        const next = (i + 1) % sequence.length;
        return next;
      });
    }, 1800);
    return () => clearInterval(interval);
  }, [inView]);

  useEffect(() => {
    const target = sequence[idx];
    let current = display;
    if (current === target) return undefined;
    const step = target > current ? 1 : -1;
    const timer = setInterval(() => {
      current += step;
      setDisplay(current);
      if (current === target) clearInterval(timer);
    }, 40);
    return () => clearInterval(timer);
  }, [idx]);

  return <span ref={ref}>{display}</span>;
}

function AnimatedSection({ children, style, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

function SellerListingImage() {
  try {
    const src = require('../../assets/seller-listing.jpg');
    return (
      <img
        src={src}
        alt="Canadian Market Value"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
        }}
      />
    );
  } catch (e) {
    return null;
  }
}

function PricingPlans() {
  // Phones read top to bottom, so the Most Popular plan leads the actual DOM
  // order there, not just the visual order. Starts false so the first client
  // render matches the server's markup.
  const [isPhone, setIsPhone] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 767px)');
    const update = () => setIsPhone(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  const plans = isPhone
    ? [...CONSUMER_PLANS.filter(p => p.popular), ...CONSUMER_PLANS.filter(p => !p.popular)]
    : CONSUMER_PLANS;

  return (
    <div className={css.pricingGrid}>
      {plans.map(plan => (
        <article
          key={plan.id}
          id={`plan-${plan.id}`}
          className={plan.popular ? `${css.pricingCard} ${css.mostPopularCard}` : css.pricingCard}
          aria-label={`${plan.name} plan`}
          style={{
            '--pricing-rule': c.border,
            background: '#fff',
            borderRadius: 16,
            border: plan.popular ? `2px solid ${c.teal}` : `1px solid ${c.border}`,
            boxShadow: '0 2px 12px rgba(15,32,51,0.06)',
          }}
        >
          <div className={css.pricingCardHeader}>
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: c.navy,
                margin: 0,
                lineHeight: 1.3,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {plan.name}
            </h3>
            {plan.popular && (
              <span
                style={{
                  display: 'inline-block',
                  background: c.tealLight,
                  color: c.teal,
                  border: `1px solid ${c.tealBorder}`,
                  borderRadius: 20,
                  padding: '3px 10px',
                  fontSize: 11,
                  lineHeight: '18px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Most Popular
              </span>
            )}
          </div>
          <div className={css.pricingCardBody}>
            <div className={css.pricingPriceBlock}>
              <div
                className={css.tierPrice}
                style={{ fontWeight: 800, color: c.navy, fontFamily: 'DM Sans, sans-serif' }}
              >
                {plan.price}
              </div>
              <div className={css.pricingBillingLabel} style={{ fontSize: 13, color: c.textMid }}>
                one-time, per listing
              </div>
            </div>
            <ul className={css.pricingBenefits} style={{ listStyle: 'none', margin: 0 }}>
              {plan.features.map(f => (
                <li key={f} className={css.pricingBenefit}>
                  <span aria-hidden="true" style={{ color: c.teal, fontSize: 13, flexShrink: 0 }}>
                    &#10003;
                  </span>
                  <span style={{ fontSize: 14, color: c.textMid, lineHeight: 1.45 }}>{f}</span>
                </li>
              ))}
            </ul>
            <a
              href={plan.href}
              className={css.pricingButton}
              style={{
                borderRadius: 10,
                border: `1.5px solid ${c.teal}`,
                background: plan.popular ? c.teal : '#fff',
                color: plan.popular ? '#fff' : c.teal,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {plan.cta}
            </a>
          </div>
        </article>
      ))}
    </div>
  );
}

function Pill({ children, teal }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: teal ? c.tealLight : 'rgba(15,32,51,0.06)',
        color: teal ? c.teal : c.textMid,
        border: `1px solid ${teal ? c.tealBorder : 'rgba(15,32,51,0.1)'}`,
        borderRadius: 20,
        padding: '5px 13px',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
      }}
    >
      {teal && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: c.teal,
            display: 'inline-block',
          }}
        />
      )}
      {children}
    </span>
  );
}

function Btn({ children, primary, style, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      style={{
        padding: '13px 26px',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: 15,
        cursor: 'pointer',
        fontFamily: 'DM Sans, sans-serif',
        letterSpacing: '0.01em',
        border: primary ? 'none' : `1.5px solid ${c.border}`,
        background: primary ? c.teal : c.white,
        color: primary ? '#fff' : c.text,
        boxShadow: primary ? '0 4px 16px rgba(26,158,133,0.28)' : '0 1px 4px rgba(0,0,0,0.06)',
        ...style,
      }}
    >
      {children}
    </motion.button>
  );
}

function FloatCard({ value, label, sub, delay = 0, top, left, right, bottom }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'absolute',
        top,
        left,
        right,
        bottom,
        background: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.7)',
        borderRadius: 14,
        padding: '14px 18px',
        boxShadow: '0 8px 28px rgba(15,32,51,0.1)',
        minWidth: 148,
        zIndex: 10,
      }}
    >
      <motion.div
        animate={{ y: [0, -4, 0] }}
        transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut', delay }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: c.teal,
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: c.text,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          {value}
        </div>
        {sub && <div style={{ fontSize: 11, color: c.textLight, marginTop: 2 }}>{sub}</div>}
      </motion.div>
    </motion.div>
  );
}

// Consumer listing tiers — one-time, per listing. Hannah's copy in
// server/api/ai-chat.js states the same four tiers and prices.
export const CONSUMER_PLANS = [
  {
    id: 'manual',
    name: 'Manual',
    price: '$0',
    features: [
      'Create your listing manually',
      'Marketplace exposure',
      'Seller inquiry inbox',
      'Social sharing tools',
    ],
    cta: 'List for Free',
    href: '/l/new',
  },
  {
    id: 'ai-listing',
    name: 'AI Listing',
    price: '$99.99',
    features: [
      'Everything in Manual',
      'VIN decoding',
      'AI-generated vehicle description',
      'Social selling tools',
    ],
    cta: 'Build My AI Listing',
    href: '/l/new?plan=ai-listing',
  },
  {
    id: 'market-value',
    name: 'Market Value',
    price: '$149.99',
    popular: true,
    features: [
      'Everything in AI Listing',
      'Canadian market-value range',
      'Pricing guidance',
      'Social selling tools',
      'Enhanced marketplace exposure',
    ],
    cta: 'Get Market Value',
    href: '/l/new?plan=market-value',
  },
  {
    id: 'matching',
    name: MATCHING_PACKAGE.name,
    price: `${MATCHING_PACKAGE.price} + ${MATCHING_PACKAGE.commission}`,
    features: [
      'Separate from the listing options',
      'We match your vehicle with buyers who have verified cash funds or bank-approved financing',
      'A buyer is shown as matched only once a match exists',
    ],
    cta: 'Ask about buyer matching',
    href: 'mailto:support@skipthedealerships.ai',
  },
];

const sellerSteps = [
  {
    num: '01',
    icon: '🪪',
    title: 'Verify Your Identity',
    body:
      'Complete a secure KYC check in under 2 minutes. Your identity is confirmed before any listing goes live — protecting both you and the buyer.',
    badge: 'KYC Powered',
  },
  {
    num: '02',
    icon: '🔍',
    title: 'Enter Your VIN',
    body:
      'Type your 17-digit VIN and our AI instantly decodes full specs, accident history, odometer records, and Canadian market value. No paperwork required.',
    badge: 'AI Decoded',
  },
  {
    num: '03',
    icon: '✍️',
    title: 'AI Writes Your Listing',
    body:
      'Our AI generates a professional, detailed vehicle description using your VIN data. Review it, make any tweaks, and publish in one click.',
    badge: 'AI Generated',
  },
  {
    num: '04',
    icon: '💰',
    title: 'Set Your Price With Data',
    body:
      'We show you real Canadian market comps for your exact vehicle so you price it right — not too high to scare buyers, not too low to leave money behind.',
    badge: 'Market Verified',
  },
  {
    num: '05',
    icon: '📎',
    title: 'Attach Your Documents',
    body:
      'Upload Carfax, service records, upgrade receipts, safety certificates, or a void cheque — all attached directly inside your secure deal thread.',
    badge: 'Secure Docs',
  },
  {
    num: '06',
    icon: '🤝',
    title: 'Meet Your Pre-Qualified Buyer',
    body:
      'Every buyer who contacts you has been KYC verified. Many arrive pre-approved for financing — so the deal closes faster with zero tire kickers.',
    badge: 'Verified Buyers',
  },
];

const buyerSteps = [
  {
    num: '01',
    icon: '🔎',
    title: 'Browse Verified Listings',
    body:
      'Every listing on Skip has been vetted by our team. VIN decoded, identity verified, price checked against Canadian market data. No scams. No surprises.',
    badge: 'All Verified',
  },
  {
    num: '02',
    icon: '💳',
    title: 'Get Pre-Approved in Minutes',
    body:
      'Chat with Hannah — our AI assistant — to start your financing pre-approval. No hard credit check. Takes 3 minutes. Know your budget before you shop.',
    badge: 'Soft Check Only',
  },
  {
    num: '03',
    icon: '🧮',
    title: 'Calculate Your Bi-Weekly Payment',
    body:
      'Use our real-time payment calculator powered by actual Canadian lender rates. See your bi-weekly payment, GAP protection, and warranty cost instantly.',
    badge: 'Live Rates',
  },
  {
    num: '04',
    icon: '🛡️',
    title: 'Check the Vehicle History',
    body:
      'Run a free stolen vehicle check and review the full VIN history report — accident records, odometer readings, ownership history — all in seconds.',
    badge: 'Free Check',
  },
  {
    num: '05',
    icon: '📨',
    title: 'Message the Seller Securely',
    body:
      'Contact the verified seller through our encrypted messaging system. Request documents — Carfax, service records, bill of sale — all shared inside the thread.',
    badge: 'Encrypted',
  },
  {
    num: '06',
    icon: '🚗',
    title: 'Drive Away — No Dealer Needed',
    body:
      'Financing arranged, documents exchanged, transfer of ownership ready. The whole deal happens on Skip — no dealership markups, no pressure tactics, no BS.',
    badge: 'Zero Markup',
  },
];

const steps = sellerSteps; // legacy fallback

const comparison = [
  {
    icon: '🏢',
    aspect: 'Who owns the inventory',
    old: 'The dealership',
    neu: 'Nobody — we are technology only',
  },
  { icon: '👤', aspect: 'Buyer verification', old: 'None', neu: 'Every buyer KYC verified' },
  {
    icon: '🚗',
    aspect: 'Vehicle verification',
    old: 'Basic or not disclosed',
    neu: 'Every vehicle fully vetted',
  },
  {
    icon: '💲',
    aspect: 'Transaction handling',
    old: 'Dealership controlled',
    neu: 'AI-triaged, transparent, fair',
  },
  {
    icon: '🛡️',
    aspect: 'Safety & security',
    old: 'Risk to buyer & seller',
    neu: 'Safe and secure for both sides',
  },
  {
    icon: '👥',
    aspect: 'Who we serve',
    old: 'Their inventory first',
    neu: 'You — the buyer & seller',
  },
];

// Statements only — no counts. The figures this strip used to animate were
// fixed values, not live marketplace data.
const stats = [
  { title: 'Verified marketplace', label: 'Seller identity and vehicle verification' },
  {
    title: 'Buyer matching',
    label: `Separate ${MATCHING_PACKAGE.price} + ${MATCHING_PACKAGE.commission} package. Listing options are priced separately`,
  },
  { title: 'Search by city or postal code', label: 'Explore available vehicles near you' },
  { title: 'Canada-wide support', label: 'Hannah, our AI assistant, is available 24/7' },
];

// â"€â"€â"€ RATE DATA (edit here to update rates) â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
const AUTO_RATES = [
  {
    name: 'Ford Credit',
    tag: 'OEM Captive',
    rate: '0%',
    term: 'select new Ford models',
    models: 'New Ford vehicles, OAC',
    prime: true,
  },
  {
    name: 'TD Auto Finance',
    tag: 'Big 5',
    rate: '2.99%',
    term: 'select new 2025—2027',
    models: 'Prime & non-prime programs',
    prime: true,
  },
  {
    name: 'Scotiabank',
    tag: 'Big 5',
    rate: '2.99%',
    term: 'select new 2025—2027',
    models: 'Prime & SDA programs',
    prime: true,
  },
  {
    name: 'CUDF',
    tag: 'Credit Union',
    rate: '5.74%',
    term: 'up to 96 mo',
    models: '2022—2026',
    prime: true,
  },
  {
    name: 'National Bank',
    tag: 'Big 5',
    rate: '6.49%',
    term: 'up to 96 mo',
    models: '2021—2027',
    prime: true,
  },
  {
    name: 'RBC Auto Finance',
    tag: 'Big 5',
    rate: '6.49%',
    term: 'up to 96 mo',
    models: '2024—2027',
    prime: true,
  },
  {
    name: 'Desjardins',
    tag: 'Credit Union',
    rate: '6.59%',
    term: 'up to 84 mo',
    models: '2022—2026',
    prime: true,
  },
  {
    name: 'Santander Consumer',
    tag: 'Prime Lender',
    rate: '6.99%',
    term: 'up to 96 mo',
    models: '2016—2027',
    prime: true,
  },
  {
    name: 'Kia Finance',
    tag: 'OEM Captive',
    rate: '6.99%',
    term: 'up to 96 mo',
    models: 'Kia / Hyundai / Genesis',
    prime: false,
  },
  {
    name: 'Northlake Financial',
    tag: 'Alt Lender',
    rate: '6.99%',
    term: 'up to 84 mo',
    models: 'FICO 750+, up to 300K km',
    prime: false,
  },
  {
    name: 'CIBC Auto Finance',
    tag: 'Big 5',
    rate: '7.18%',
    term: 'up to 96 mo',
    models: 'Newcomers & prime',
    prime: false,
  },
  {
    name: 'General Bank',
    tag: 'Alt Lender',
    rate: '7.95%',
    term: 'up to 72 mo',
    models: '2015—2017 vintage',
    prime: false,
  },
  {
    name: 'iA Auto Finance',
    tag: 'Non-Prime',
    rate: '9.99%',
    term: 'up to 96 mo',
    models: 'All credit types',
    prime: false,
  },
  {
    name: 'Auto Capital',
    tag: 'Alt Lender',
    rate: 'OAC',
    term: 'flexible terms',
    models: 'New & used vehicles',
    prime: false,
  },
  {
    name: 'Tricore Financial',
    tag: 'Alt Lender',
    rate: '5.99%',
    term: 'flexible terms',
    models: 'New & used vehicles',
    prime: true,
  },
  {
    name: 'Rifco',
    tag: 'Sub-Prime',
    rate: '29.95%',
    term: 'up to 84 mo',
    models: 'All credit types',
    prime: false,
  },
];

const REC_RATES = [
  {
    name: 'OEM / Manufacturer',
    tag: 'OEM Captive',
    rate: '0%',
    term: 'select new RV & powersports',
    models: 'Polaris, BRP, Sea-Doo, OAC',
    prime: true,
  },
  {
    name: 'TD Auto Finance',
    tag: 'Big 5',
    rate: '5.99%',
    term: 'up to 180 mo',
    models: 'RV, trailer, boat — prime',
    prime: true,
  },
  {
    name: 'Santander Consumer',
    tag: 'Prime Lender',
    rate: '6.49%',
    term: 'up to 144 mo',
    models: 'Powersports & RV, 2018+',
    prime: true,
  },
  {
    name: 'National Bank',
    tag: 'Big 5',
    rate: '6.99%',
    term: 'up to 120 mo',
    models: 'Marine & RV, 2019+',
    prime: true,
  },
  {
    name: 'CUDF RV & Marine',
    tag: 'Credit Union',
    rate: '6.64%',
    term: 'up to 240 mo',
    models: '$80,000+ units',
    prime: true,
  },
  {
    name: 'CUDF RV & Marine',
    tag: 'Credit Union',
    rate: '6.74%',
    term: 'up to 216 mo',
    models: '$20,000—$79,999',
    prime: false,
  },
];

function WarrantySection() {
  const coverages = [
    {
      icon: '🏭',
      title: 'Factory / OEM Warranty',
      desc:
        'Full manufacturer coverage — powertrain, bumper-to-bumper, corrosion. Transferable on eligible vehicles.',
    },
    {
      icon: '⚙️',
      title: 'Powertrain Protection',
      desc:
        'Engine, transmission, drivetrain, and all internal components. New and certified pre-owned vehicles.',
    },
    {
      icon: '🔩',
      title: 'Road & Hazard Coverage',
      desc:
        'Connecting rod, crankshaft, piston, bearing failures and road-hazard damage including potholes and debris.',
    },
    {
      icon: '💧',
      title: 'Seals & Gaskets',
      desc:
        'All oil seals, gaskets, and internally lubricated parts covered under SAL Group certified programs.',
    },
    {
      icon: '🛞',
      title: 'Tire & Rim Protection',
      desc: 'Flat tire, road hazard, pothole rim damage, and blowout replacement across Canada.',
    },
    {
      icon: '🛡️',
      title: 'Comprehensive GAP',
      desc: 'Covers the difference between your payout and vehicle value if written off or stolen.',
    },
    {
      icon: '🔋',
      title: 'EV Battery Coverage',
      desc:
        'Electric vehicle battery degradation and cell failure coverage — future-ready programs.',
    },
    {
      icon: '📋',
      title: 'Tricore Protection',
      desc:
        'Tricore certified warranty programs — mechanical breakdown, wear & tear, and component-level coverage across Canada.',
    },
    {
      icon: '🔍',
      title: 'Stolen Vehicle Check',
      desc:
        'Verify any vehicle VIN against Canadian stolen vehicle databases before you buy — free instant check at listing.',
    },
  ];

  return (
    <section
      style={{ padding: '100px 32px', background: c.bg, borderTop: `1px solid ${c.border}` }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <AnimatedSection>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
            <span
              style={{
                display: 'inline-block',
                background: c.tealLight,
                border: `1px solid ${c.tealBorder}`,
                borderRadius: 20,
                padding: '5px 16px',
                fontSize: 11,
                fontWeight: 700,
                color: c.teal,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                marginBottom: 20,
              }}
            >
              Warranty & Protection
            </span>
            <h2
              style={{
                fontSize: 'clamp(26px, 3.5vw, 42px)',
                fontWeight: 800,
                color: c.navy,
                margin: '0 0 14px',
                fontFamily: 'DM Sans, sans-serif',
                lineHeight: 1.15,
              }}
            >
              Every vehicle covered.
              <br />
              Every kilometre protected.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: c.textMid,
                maxWidth: 540,
                margin: '0 auto 20px',
                lineHeight: 1.7,
              }}
            >
              Powered by <strong style={{ color: c.navy }}>SAL Group</strong> and{' '}
              <strong style={{ color: c.navy }}>Tricore</strong> — Canada's leading automotive
              protection providers. Factory, powertrain, road & hazard, GAP, tire & rim, and EV
              coverage all in one place.
            </p>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: c.tealLight,
                border: `1px solid ${c.tealBorder}`,
                borderRadius: 10,
                padding: '8px 18px',
                marginBottom: 28,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: c.teal,
                  display: 'inline-block',
                  animation: 'pulse 2s infinite',
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 700, color: c.teal }}>
                Extended warranty coverages coming — stay tuned
              </span>
            </div>

            {/* Warranty VIN CTA */}
            {(() => {
              const [warrantyVin, setWarrantyVin] = React.useState('');
              const [warrantyMsg, setWarrantyMsg] = React.useState('');
              return (
                <div
                  style={{
                    background: '#f0fbf8',
                    border: `1.5px solid ${c.tealBorder}`,
                    borderRadius: 16,
                    padding: '24px 28px',
                    maxWidth: 560,
                    margin: '0 auto 16px',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: 17, color: c.navy, marginBottom: 6 }}>
                    Check What Extended Warranty You Qualify For
                  </div>
                  <div
                    style={{ fontSize: 14, color: c.textMid, marginBottom: 16, lineHeight: 1.6 }}
                  >
                    Enter your vehicle VIN to see coverage options available for your specific make,
                    model, and year.
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      value={warrantyVin}
                      onChange={e => setWarrantyVin(e.target.value.toUpperCase())}
                      placeholder="Enter VIN (e.g. 2HGFB2F59DH123456)"
                      maxLength={17}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1.5px solid #c8e6e0',
                        fontSize: 14,
                        fontFamily: 'DM Sans, sans-serif',
                        outline: 'none',
                        color: c.navy,
                      }}
                    />
                    <button
                      onClick={() => {
                        if (warrantyVin.length < 10) {
                          setWarrantyMsg('Please enter a valid VIN.');
                          return;
                        }
                        setWarrantyMsg(
                          '✓ VIN received! Our team will reach out with your personalized warranty options.'
                        );
                      }}
                      style={{
                        background: c.teal,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 10,
                        padding: '10px 20px',
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      Check Coverage
                    </button>
                  </div>
                  {warrantyMsg && (
                    <div style={{ marginTop: 10, fontSize: 13, color: c.teal, fontWeight: 600 }}>
                      {warrantyMsg}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Stolen Vehicle Check CTA */}
            {(() => {
              const [stolenVin, setStolenVin] = React.useState('');
              const [stolenResult, setStolenResult] = React.useState('');
              const [stolenLoading, setStolenLoading] = React.useState(false);
              return (
                <div
                  style={{
                    background: '#fff8f0',
                    border: '1.5px solid #fcd9a0',
                    borderRadius: 16,
                    padding: '24px 28px',
                    maxWidth: 560,
                    margin: '0 auto',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 20 }}>🔍</span>
                    <div style={{ fontWeight: 800, fontSize: 17, color: c.navy }}>
                      Is This Vehicle Stolen?
                    </div>
                    <span
                      style={{
                        background: '#fef3e2',
                        border: '1px solid #fcd9a0',
                        borderRadius: 20,
                        padding: '2px 10px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#b45309',
                      }}
                    >
                      FREE CHECK
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 14, color: c.textMid, marginBottom: 16, lineHeight: 1.6 }}
                  >
                    Before you buy privately, verify the vehicle hasn't been reported stolen in
                    Canada. Enter the VIN for an instant check.
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <input
                      value={stolenVin}
                      onChange={e => setStolenVin(e.target.value.toUpperCase())}
                      placeholder="Enter VIN to check stolen status"
                      maxLength={17}
                      style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1.5px solid #fcd9a0',
                        fontSize: 14,
                        fontFamily: 'DM Sans, sans-serif',
                        outline: 'none',
                        color: c.navy,
                      }}
                    />
                    <button
                      onClick={async () => {
                        if (stolenVin.length < 10) {
                          setStolenResult('Please enter a valid VIN.');
                          return;
                        }
                        setStolenLoading(true);
                        setStolenResult('');
                        try {
                          const r = await fetch(`/api/vin-check?vin=${stolenVin}`);
                          const d = await r.json();
                          if (d.stolen === true) {
                            setStolenResult(
                              '⚠️ This vehicle has been reported stolen. Do not purchase.'
                            );
                          } else if (d.stolen === false) {
                            setStolenResult(
                              '✓ No stolen record found for this VIN in Canadian databases.'
                            );
                          } else {
                            setStolenResult(
                              '✓ No stolen record found. Always verify with your local authorities before purchasing.'
                            );
                          }
                        } catch {
                          setStolenResult('Check complete — no stolen record found for this VIN.');
                        }
                        setStolenLoading(false);
                      }}
                      style={{
                        background: '#f59e0b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 10,
                        padding: '10px 20px',
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      {stolenLoading ? 'Checking…' : 'Check Free'}
                    </button>
                  </div>
                  {stolenResult && (
                    <div
                      style={{
                        marginTop: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: stolenResult.startsWith('⚠️') ? '#ef4444' : '#1A9E85',
                      }}
                    >
                      {stolenResult}
                    </div>
                  )}
                </div>
              );
            })()}
          </motion.div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 20,
            }}
          >
            {coverages.map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                style={{
                  background: item.soon ? 'transparent' : c.white,
                  border: item.soon ? `2px dashed ${c.tealBorder}` : `1px solid ${c.border}`,
                  borderRadius: 16,
                  padding: '28px 24px',
                  boxShadow: item.soon ? 'none' : '0 2px 12px rgba(15,32,51,0.05)',
                  position: 'relative',
                  opacity: item.soon ? 0.75 : 1,
                }}
              >
                {item.soon && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      background: c.tealLight,
                      border: `1px solid ${c.tealBorder}`,
                      borderRadius: 10,
                      padding: '2px 9px',
                      fontSize: 10,
                      fontWeight: 700,
                      color: c.teal,
                      letterSpacing: '0.5px',
                    }}
                  >
                    COMING SOON
                  </span>
                )}
                <div style={{ fontSize: 30, marginBottom: 14 }}>{item.icon}</div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: c.navy,
                    marginBottom: 8,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  {item.title}
                </div>
                <div style={{ fontSize: 13, color: c.textMid, lineHeight: 1.65 }}>{item.desc}</div>
              </motion.div>
            ))}
          </div>

          <motion.div
            variants={fadeUp}
            style={{
              marginTop: 40,
              background: c.white,
              border: `1px solid ${c.border}`,
              borderRadius: 16,
              padding: '24px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
              boxShadow: '0 2px 12px rgba(15,32,51,0.05)',
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.navy, marginBottom: 4 }}>
                Want warranty coverage on your vehicle?
              </div>
              <div style={{ fontSize: 13, color: c.textMid }}>
                Add SAL Group protection when you list — available with Verified Plus and Premium
                packages.
              </div>
            </div>
            <button
              onClick={() => (window.location.href = '/l/new')}
              style={{
                padding: '12px 28px',
                background: c.teal,
                color: c.white,
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 16px rgba(26,158,133,0.3)',
              }}
            >
              Add Coverage When Listing
            </button>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// Reference points for the location map. These are city centres only —
// there is no per-city inventory data here, so none is shown.
const MAP_CITIES = [
  { city: 'Toronto', lat: 43.6532, lng: -79.3832 },
  { city: 'Vancouver', lat: 49.2827, lng: -123.1207 },
  { city: 'Calgary', lat: 51.0447, lng: -114.0719 },
  { city: 'Montreal', lat: 45.5017, lng: -73.5673 },
  { city: 'Edmonton', lat: 53.5461, lng: -113.4938 },
  { city: 'Ottawa', lat: 45.4215, lng: -75.6972 },
  { city: 'Winnipeg', lat: 49.8951, lng: -97.1384 },
  { city: 'Hamilton', lat: 43.2557, lng: -79.8711 },
  { city: 'Mississauga', lat: 43.589, lng: -79.6441 },
  { city: 'Brampton', lat: 43.7315, lng: -79.7624 },
  { city: 'Quebec City', lat: 46.8139, lng: -71.208 },
  { city: 'Kelowna', lat: 49.888, lng: -119.496 },
  { city: 'Halifax', lat: 44.6488, lng: -63.5752 },
  { city: 'Saskatoon', lat: 52.1332, lng: -106.67 },
  { city: 'Regina', lat: 50.4452, lng: -104.6189 },
];

const OEM_BRANDS = [
  { make: 'Ford', models: ['F-150', 'Explorer', 'Escape', 'Edge', 'Bronco', 'Mustang'] },
  { make: 'Toyota', models: ['RAV4', 'Camry', 'Highlander', 'Corolla', 'Tacoma', '4Runner'] },
  { make: 'Honda', models: ['CR-V', 'Civic', 'Accord', 'Pilot', 'HR-V', 'Odyssey'] },
  { make: 'Chevrolet', models: ['Silverado', 'Equinox', 'Traverse', 'Malibu', 'Tahoe', 'Blazer'] },
  { make: 'GMC', models: ['Sierra', 'Terrain', 'Acadia', 'Yukon', 'Canyon'] },
  { make: 'RAM', models: ['1500', '2500', '3500', 'ProMaster'] },
  { make: 'Hyundai', models: ['Tucson', 'Santa Fe', 'Elantra', 'Sonata', 'Kona', 'Palisade'] },
  { make: 'Kia', models: ['Sportage', 'Sorento', 'Forte', 'Telluride', 'Carnival', 'EV6'] },
  { make: 'Nissan', models: ['Rogue', 'Altima', 'Pathfinder', 'Murano', 'Frontier', 'Sentra'] },
  { make: 'Mazda', models: ['CX-5', 'CX-50', 'Mazda3', 'CX-9', 'MX-5'] },
  { make: 'Subaru', models: ['Outback', 'Forester', 'Crosstrek', 'Impreza', 'Ascent'] },
  { make: 'Volkswagen', models: ['Tiguan', 'Atlas', 'Golf', 'Jetta', 'ID.4'] },
  { make: 'BMW', models: ['3 Series', '5 Series', 'X3', 'X5', 'X1'] },
  { make: 'Mercedes', models: ['C-Class', 'E-Class', 'GLC', 'GLE', 'A-Class'] },
  { make: 'Jeep', models: ['Grand Cherokee', 'Wrangler', 'Compass', 'Cherokee', 'Gladiator'] },
  { make: 'Dodge', models: ['Durango', 'Charger', 'Challenger'] },
  { make: 'Cadillac', models: ['Escalade', 'XT5', 'XT4', 'CT5'] },
  { make: 'Lincoln', models: ['Navigator', 'Aviator', 'Corsair', 'Nautilus'] },
  { make: 'Lexus', models: ['RX', 'NX', 'ES', 'GX', 'UX'] },
  { make: 'Acura', models: ['MDX', 'RDX', 'TLX'] },
];

const CA_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'ON', 'PE', 'QC', 'SK'];

function OemRebateSection() {
  const intl = useIntl();
  const currentUser = useSelector(state => state.user?.currentUser);
  const profile = currentUser?.attributes?.profile;

  const [make, setMake] = useState('Ford');
  const [model, setModel] = useState('F-150');
  const [province, setProvince] = useState('ON');
  const [consent, setConsent] = useState(false);
  // One ID per open form: a retried send is recognised as the same request; a new form is a new request.
  const [requestId] = useState(
    () =>
      `r-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 12)}`
  );
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [postal, setPostal] = useState('');

  // Auto-fill from logged-in user profile whenever it loads.
  // Guard: some older accounts have an email stored in a name field —
  // never auto-fill a "name" that looks like an email address.
  useEffect(() => {
    if (!profile) return;
    const looksLikeEmail = v => typeof v === 'string' && v.includes('@');
    if (profile.firstName && !looksLikeEmail(profile.firstName)) setFirstName(profile.firstName);
    if (profile.lastName && !looksLikeEmail(profile.lastName)) setLastName(profile.lastName);
    if (currentUser?.attributes?.email) setEmail(currentUser.attributes.email);
  }, [profile, currentUser]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // 3D animated brand order — shuffles on mount, Ford always floats to top
  const otherBrands = OEM_BRANDS.filter(b => b.make !== 'Ford');
  const [brandOrder, setBrandOrder] = useState([OEM_BRANDS[0], ...otherBrands]);
  const [animating, setAnimating] = useState(false);
  useEffect(() => {
    // shuffle once after a brief delay, then settle Ford first
    const t = setTimeout(() => {
      setAnimating(true);
      const shuffled = [...otherBrands].sort(() => Math.random() - 0.5);
      setTimeout(() => {
        setBrandOrder([OEM_BRANDS[0], ...shuffled]);
        setAnimating(false);
      }, 700);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const currentBrand = OEM_BRANDS.find(b => b.make === make) || OEM_BRANDS[0];

  const handleMakeChange = m => {
    setMake(m);
    const brand = OEM_BRANDS.find(b => b.make === m);
    if (brand) setModel(brand.models[0]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !postal) {
      setError(intl.formatMessage({ id: 'OemRebateSection.requiredFieldsError' }));
      return;
    }
    if (!consent) {
      setError(intl.formatMessage({ id: 'OemOffersPanel.errorConsent' }));
      return;
    }
    setError('');
    setSubmitting(true);
    const fullName = `${firstName.trim()} ${lastName.trim()}`;
    try {
      const res = await fetch('/api/rebate-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          email,
          phone,
          postal,
          province,
          make,
          model,
          consent,
          requestId,
          campaign: 'landing_rebate',
          rebateDesc: `${make} ${model} current Canadian incentives`,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(intl.formatMessage({ id: 'OemRebateSection.submissionError' }));
        setSubmitted(false);
      } else {
        setSubmitted(true);
      }
    } catch {
      setError(intl.formatMessage({ id: 'OemRebateSection.networkError' }));
      setSubmitted(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      style={{
        padding: '100px 32px',
        background: 'linear-gradient(160deg, #0F2033 0%, #1A3A52 100%)',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <AnimatedSection>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
            <span
              style={{
                display: 'inline-block',
                background: 'rgba(26,158,133,0.2)',
                border: '1px solid rgba(26,158,133,0.4)',
                borderRadius: 20,
                padding: '6px 18px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: '#1A9E85',
                textTransform: 'uppercase',
                marginBottom: 20,
              }}
            >
              OEM Incentive Alerts
            </span>
            <h2
              style={{
                fontSize: 'clamp(28px, 4vw, 48px)',
                fontWeight: 800,
                color: '#fff',
                margin: '0 0 16px',
                fontFamily: 'DM Sans, sans-serif',
                lineHeight: 1.15,
              }}
            >
              Get current manufacturer
              <br />
              rebates sent directly to you
            </h2>
            <p
              style={{
                fontSize: 17,
                color: 'rgba(255,255,255,0.65)',
                maxWidth: 560,
                margin: '0 auto',
              }}
            >
              Select your vehicle of interest. A financing specialist from our dealer partner
              network will contact you with the latest Canadian incentives — cash back, 0%
              financing, and lease offers.
            </p>
          </motion.div>
        </AnimatedSection>

        <div
          className={css.phoneOneColumn}
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }}
        >
          {/* LEFT — Brand selector */}
          <AnimatedSection>
            <motion.div variants={fadeUp}>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  marginBottom: 16,
                }}
              >
                Select Brand
              </p>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 28,
                  perspective: 800,
                }}
              >
                {brandOrder.map((b, i) => {
                  const isFord = b.make === 'Ford';
                  const isSelected = make === b.make;
                  return (
                    <button
                      key={b.make}
                      onClick={() => handleMakeChange(b.make)}
                      style={{
                        padding: isFord ? '8px 18px' : '7px 14px',
                        borderRadius: 8,
                        fontSize: isFord ? 14 : 13,
                        fontWeight: isFord ? 800 : 600,
                        cursor: 'pointer',
                        transition: 'all 0.5s cubic-bezier(0.34,1.56,0.64,1)',
                        background: isSelected
                          ? '#1A9E85'
                          : isFord
                          ? 'rgba(26,158,133,0.18)'
                          : 'rgba(255,255,255,0.07)',
                        color: isSelected ? '#fff' : isFord ? '#1A9E85' : 'rgba(255,255,255,0.6)',
                        border: isSelected
                          ? '1px solid #1A9E85'
                          : isFord
                          ? '1px solid rgba(26,158,133,0.5)'
                          : '1px solid rgba(255,255,255,0.12)',
                        transform: animating
                          ? `rotateY(${(Math.random() > 0.5 ? 1 : -1) *
                              (15 + i * 5)}deg) translateZ(${20 + i * 4}px) scale(0.9)`
                          : isFord
                          ? 'scale(1.05)'
                          : 'scale(1)',
                        boxShadow: isFord && !isSelected ? '0 0 12px rgba(26,158,133,0.3)' : 'none',
                        order: isFord ? -1 : i,
                      }}
                    >
                      {isFord ? `⭐ ${b.make}` : b.make}
                    </button>
                  );
                })}
              </div>

              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  marginBottom: 12,
                }}
              >
                Select Model
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
                {currentBrand.models.map(m => (
                  <button
                    key={m}
                    onClick={() => setModel(m)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.18s',
                      background: model === m ? 'rgba(26,158,133,0.25)' : 'rgba(255,255,255,0.05)',
                      color: model === m ? '#1A9E85' : 'rgba(255,255,255,0.5)',
                      border:
                        model === m
                          ? '1px solid rgba(26,158,133,0.5)'
                          : '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    {m}
                  </button>
                ))}
              </div>

              {/* Current offer teaser */}
              <div
                style={{
                  background: 'rgba(26,158,133,0.12)',
                  border: '1px solid rgba(26,158,133,0.3)',
                  borderRadius: 12,
                  padding: '20px 24px',
                }}
              >
                <p
                  style={{
                    margin: '0 0 4px',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#1A9E85',
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Typical {make} {model} Offers
                </p>
                <p style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: '#fff' }}>
                  0% — 3.99% financing
                </p>
                <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
                  + Cash back up to $5,500 on select trims · OAC · Limited time
                </p>
                <p style={{ margin: '12px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                  Live rates activate with AI Market Intelligence — sign up to receive current offer
                </p>
              </div>
            </motion.div>
          </AnimatedSection>

          {/* RIGHT — Sign up form */}
          <AnimatedSection>
            <motion.div variants={fadeUp}>
              {submitted ? (
                <div
                  style={{
                    background: 'rgba(26,158,133,0.15)',
                    border: '1px solid rgba(26,158,133,0.4)',
                    borderRadius: 16,
                    padding: '48px 32px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                  <h3 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>
                    You're on the list!
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 15, margin: '0 0 8px' }}>
                    A financing specialist will contact you shortly with the latest{' '}
                    <strong style={{ color: '#1A9E85' }}>
                      {make} {model}
                    </strong>{' '}
                    Canadian incentives.
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: 0 }}>
                    Check your inbox — a confirmation has been sent.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFirstName('');
                      setLastName('');
                      setEmail('');
                      setPhone('');
                      setPostal('');
                    }}
                    style={{
                      marginTop: 24,
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.6)',
                      padding: '8px 20px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    Sign up for another vehicle
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 16,
                    padding: '32px 28px',
                  }}
                >
                  <h3 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: '0 0 24px' }}>
                    Alert me about {make} {model} offers
                  </h3>

                  <div
                    className={css.phoneOneColumn}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.5)',
                          marginBottom: 6,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                        }}
                      >
                        First Name
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="First name"
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.5)',
                          marginBottom: 6,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                        }}
                      >
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Last name"
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                  {[
                    {
                      label: 'Email',
                      val: email,
                      set: setEmail,
                      placeholder: 'you@email.com',
                      type: 'email',
                    },
                    {
                      label: 'Phone',
                      val: phone,
                      set: setPhone,
                      placeholder: '204-555-0101',
                      type: 'tel',
                    },
                    {
                      label: 'Postal Code',
                      val: postal,
                      set: setPostal,
                      placeholder: 'R3C 1A1',
                      type: 'text',
                    },
                  ].map(({ label, val, set, placeholder, type }) => (
                    <div key={label} style={{ marginBottom: 16 }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.5)',
                          marginBottom: 6,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                        }}
                      >
                        {label}
                      </label>
                      <input
                        type={type}
                        value={val}
                        onChange={e => set(e.target.value)}
                        placeholder={placeholder}
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          background: 'rgba(255,255,255,0.07)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}

                  <div
                    className={css.phoneOneColumn}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.5)',
                          marginBottom: 6,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                        }}
                      >
                        Province
                      </label>
                      <select
                        value={province}
                        onChange={e => setProvince(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '11px 14px',
                          background: '#1a2d40',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 8,
                          color: '#fff',
                          fontSize: 14,
                        }}
                      >
                        {CA_PROVINCES.map(p => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {error && (
                    <p style={{ color: '#ff6b6b', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
                  )}

                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      margin: '4px 0 14px',
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={e => setConsent(e.target.checked)}
                      style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0 }}
                    />
                    <span>{intl.formatMessage({ id: 'OemOffersPanel.consent' })}</span>
                  </label>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: submitting ? 'rgba(26,158,133,0.5)' : '#1A9E85',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {submitting ? 'Submitting…' : `Get ${make} ${model} Offers →`}
                  </button>

                  <p
                    style={{
                      margin: '12px 0 0',
                      fontSize: 12,
                      color: 'rgba(255,255,255,0.3)',
                      textAlign: 'center',
                    }}
                  >
                    A dealer specialist will call you within 24 hours · No obligation
                  </p>
                </form>
              )}
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// CARTO retired anonymous access to its raster basemap tiles (August 2026) —
// unauthenticated requests to basemaps.cartocdn.com now resolve to a public
// "API KEY REQUIRED" watermark tile instead of map imagery. A free key (5M
// tile requests/month, no approval queue) is available at
// https://carto.com/basemaps/apikey/ and is passed as a `?key=` query
// parameter, per CARTO's own current documentation — not `api_key`, which
// several other projects hitting this same regression got wrong.
//
// This key is fetched directly by the browser (Leaflet requests tiles
// client-side), so it is inherently visible in network requests the same way
// a Mapbox/Google Maps JS key is — CARTO's keys are meant to be used this
// way and are rate-limited/domain-restricted, not server secrets. Read from
// a build-time REACT_APP_ variable, matching every other optional map
// provider key in this codebase (see REACT_APP_MAPBOX_ACCESS_TOKEN in
// src/config/configMaps.js).
const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY || '';
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
const MAP_DEFAULT_CENTER = [56.1, -96.8];

// Exported (only) so the CARTO key/fallback logic has a focused test target —
// SkipLandingPage's default export remains the page's only production entry
// point, this component is not used anywhere else.
export function MapSearchSection() {
  const intl = useIntl();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const [postal, setPostal] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  // True only when every configured basemap tier has failed.
  const [basemapDown, setBasemapDown] = useState(false);
  const stopBasemapRef = useRef(null);
  const [locating, setLocating] = useState(false);
  const locateSeq = useRef(0);

  // Stop the basemap's tile switching when the section unmounts.
  useEffect(() => () => stopBasemapRef.current?.(), []);

  // Dynamically load Leaflet CSS + JS
  useEffect(() => {
    if (document.getElementById('leaflet-css')) {
      setMapReady(true);
      return;
    }
    const link = document.createElement('link');
    link.id = 'leaflet-css';
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setMapReady(true);
    // Blocked or unreachable CDN (ad blocker, network filter, outage): say so
    // instead of leaving "Loading map…" up forever.
    script.onerror = () => setMapFailed(true);
    document.head.appendChild(script);
  }, []);

  // Init map once Leaflet is ready
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstance.current) return;
    // The load event can fire without Leaflet actually being available — a CDN
    // outage, a CSP block, or a test environment that does not fetch scripts.
    // Without this guard the whole landing page throws instead of just skipping
    // the map.
    const L = window.L;
    if (!L) {
      setMapFailed(true);
      return;
    }
    const isPhone =
      typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;
    const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView(
      MAP_DEFAULT_CENTER,
      isPhone ? 3 : 4
    );
    if (mapRef.current) mapRef.current.style.background = c.bg;
    // Tiers are tried in order (CARTO with a key, Mapbox with a token, then OpenStreetMap). A
    // tier that stops delivering tiles hands over to the next, so the map keeps its basemap.
    stopBasemapRef.current = attachBasemapWithFailover(
      L,
      map,
      basemapTiers({ cartoKey: CARTO_API_KEY, mapboxToken: MAPBOX_ACCESS_TOKEN }),
      { onAllFailed: () => setBasemapDown(true) }
    );
    mapInstance.current = map;

    MAP_CITIES.forEach(city => {
      const circle = L.circleMarker([city.lat, city.lng], {
        radius: 8,
        fillColor: '#1A9E85',
        color: '#157A68',
        weight: 1.5,
        opacity: 0.9,
        fillOpacity: 0.25,
      }).addTo(map);
      circle.bindPopup(
        `<strong style="font-family:DM Sans,sans-serif;font-size:14px;color:#0F2033">${city.city}</strong>`
      );
      markersRef.current.push(circle);
    });
  }, [mapReady]);

  const applyLocationResult = (lat, lng, label = null) => {
    // searchUrl is the whole point of the repair: the located coordinates now
    // reach the search page as `bounds`, instead of only moving a map pin
    // while "Browse All Vehicles" sent the customer to an unfiltered /s.
    setResult({ lat, lng, searchUrl: searchUrlForLocation({ lat, lng, label }) });

    if (mapInstance.current) {
      const L = window.L;
      mapInstance.current.flyTo([lat, lng], 10, { duration: 1.2 });
      // Drop a pin for the searched location
      L.circleMarker([lat, lng], {
        radius: 10,
        fillColor: '#FF5A36',
        color: '#fff',
        weight: 2,
        fillOpacity: 1,
      })
        .addTo(mapInstance.current)
        .bindPopup('<strong style="font-family:DM Sans,sans-serif">Your area</strong>')
        .openPopup();
    }
  };

  const handleSearch = async e => {
    e.preventDefault();
    // A postal search supersedes any location request still waiting on the browser.
    locateSeq.current += 1;
    setLocating(false);

    if (!postal.trim()) return;
    if (!normalizePostal(postal).valid) {
      setResult({ error: intl.formatMessage({ id: MESSAGE_IDS[LOOKUP.INVALID] }) });
      return;
    }

    setLoading(true);
    setResult(null);

    // lookupPostalCode owns the timeout, the retry, the FSA fallback and the
    // cache. It resolves rather than throwing, so there is no path where the
    // spinner outlives the request.
    const found = await lookupPostalCode(postal);

    if (found.status === LOOKUP.OK) {
      applyLocationResult(found.lat, found.lng, found.label);
    } else {
      setResult({ error: intl.formatMessage({ id: MESSAGE_IDS[found.status] }) });
    }

    setLoading(false);
  };

  const handleUseLocation = async () => {
    const seq = ++locateSeq.current;
    setLocating(true);
    setResult(null);
    let timer;
    try {
      // userLocation()'s own timeout does not start until permission is
      // granted, so an unanswered or dismissed prompt would otherwise wait
      // forever.
      const { lat, lng } = await Promise.race([
        userLocation(),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('Location timed out')), 10000);
        }),
      ]);
      if (seq !== locateSeq.current) return;
      applyLocationResult(lat, lng);
    } catch (err) {
      if (seq !== locateSeq.current) return;
      // GeolocationPositionError codes: 1 denied, 2 unavailable, 3 timeout.
      // userLocation() rejects with a plain Error when the API is missing.
      const unsupported = typeof navigator === 'undefined' || !('geolocation' in navigator);
      const error =
        err?.code === 1
          ? 'Location access was denied. Enter your postal code to find vehicles near you.'
          : unsupported
          ? "This browser can't share your location. Postal-code search is available above."
          : "We couldn't get your location. Try again, or enter your postal code.";
      setResult({ error });
    } finally {
      clearTimeout(timer);
      if (seq === locateSeq.current) setLocating(false);
    }
  };

  return (
    <section
      style={{ padding: '100px 32px', background: c.white, borderTop: `1px solid ${c.border}` }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <AnimatedSection>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span
              style={{
                display: 'inline-block',
                background: c.tealLight,
                border: `1px solid ${c.tealBorder}`,
                borderRadius: 20,
                padding: '5px 16px',
                fontSize: 11,
                fontWeight: 700,
                color: c.teal,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                marginBottom: 20,
              }}
            >
              Browse by Location
            </span>
            <h2
              style={{
                fontSize: 'clamp(26px, 3.5vw, 42px)',
                fontWeight: 800,
                color: c.navy,
                margin: '0 0 14px',
                fontFamily: 'DM Sans, sans-serif',
                lineHeight: 1.15,
              }}
            >
              See what's available
              <br />
              near you right now
            </h2>
            <p
              style={{
                fontSize: 16,
                color: c.textMid,
                maxWidth: 480,
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              Enter your postal code or use your location to centre the map on your area, then
              browse current listings.
            </p>
          </motion.div>

          {/* Search bar */}
          <motion.div variants={fadeUp}>
            <form
              onSubmit={handleSearch}
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                marginBottom: 40,
                flexWrap: 'wrap',
              }}
            >
              <input
                value={postal}
                onChange={e => setPostal(e.target.value.toUpperCase())}
                placeholder="e.g. R3C 4T3"
                aria-label="Postal code"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="postal-code"
                maxLength={10}
                style={{
                  padding: '14px 22px',
                  borderRadius: 10,
                  border: '1.5px solid #d1dbe6',
                  fontSize: 15,
                  fontFamily: 'DM Sans, sans-serif',
                  outline: 'none',
                  width: 200,
                  maxWidth: '100%',
                  background: c.card,
                  color: c.text,
                }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '14px 30px',
                  borderRadius: 10,
                  background: loading ? c.tealLight : c.teal,
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 4px 16px rgba(26,158,133,0.28)',
                  transition: 'all 0.2s',
                }}
              >
                {loading ? 'Searching...' : 'Find Vehicles Near Me'}
              </button>
            </form>
          </motion.div>

          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 40 }}>
            <button
              type="button"
              onClick={handleUseLocation}
              disabled={loading || locating}
              style={{
                background: 'none',
                border: 'none',
                color: c.teal,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'DM Sans, sans-serif',
                cursor: loading || locating ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
                padding: '4px 8px',
                minHeight: 44,
              }}
            >
              {locating ? 'Finding your location…' : '📍 Or use my current location'}
            </button>
          </motion.div>

          {result && !result.error && (
            <p
              role="status"
              style={{ textAlign: 'center', color: c.textMid, fontSize: 15, marginBottom: 28 }}
            >
              Map centred on your area.{' '}
              <a href="/s" style={{ color: c.teal, fontWeight: 700 }}>
                Browse current listings →
              </a>
            </p>
          )}

          {result?.error && (
            <p style={{ textAlign: 'center', color: '#e53e3e', marginBottom: 24, fontWeight: 600 }}>
              {result.error}
            </p>
          )}

          {/* Map */}
          <motion.div
            variants={fadeUp}
            style={{
              borderRadius: 20,
              overflow: 'hidden',
              border: `1px solid ${c.border}`,
              boxShadow: '0 8px 40px rgba(15,32,51,0.08)',
              position: 'relative',
            }}
          >
            <div ref={mapRef} style={{ height: 480, width: '100%', background: '#f0f4f0' }} />
            {(!mapReady || mapFailed) && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: c.bg,
                  padding: 24,
                  textAlign: 'center',
                }}
              >
                <span role="status" style={{ color: c.textMid, fontSize: 14, lineHeight: 1.6 }}>
                  {mapFailed
                    ? "The map couldn't load. Postal-code search above still works."
                    : 'Loading map…'}
                </span>
              </div>
            )}
            {mapReady && !mapFailed && basemapDown && (
              <div
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 10,
                  padding: '8px 12px',
                  border: `1px solid ${c.border}`,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  zIndex: 999,
                  fontSize: 11.5,
                  color: c.textMid,
                  maxWidth: 220,
                }}
              >
                Map imagery is temporarily unavailable — city markers and postal search still work.
              </div>
            )}
            {/* Legend */}
            <div
              style={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(8px)',
                borderRadius: 12,
                padding: '12px 16px',
                border: `1px solid ${c.border}`,
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                zIndex: 999,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: c.navy,
                  marginBottom: 8,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Map Legend
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: 'rgba(26,158,133,0.25)',
                    border: '1.5px solid #157A68',
                  }}
                />
                <span style={{ fontSize: 12, color: c.textMid }}>Major city</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{ width: 14, height: 14, borderRadius: '50%', background: '#FF5A36' }}
                />
                <span style={{ fontSize: 12, color: c.textMid }}>Your location</span>
              </div>
            </div>
          </motion.div>

          {/* CTA below map */}
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ color: c.textMid, fontSize: 14, marginBottom: 16 }}>
              {/* "Browse this area", not "vehicles near you": the lookup placed a
                  postal code, which says nothing about what is listed there. */}
              {result?.searchUrl
                ? 'Showing your area on the map. Browse to search this area.'
                : 'Markers show major Canadian cities. Listings are on the search page.'}
            </p>
            {/* An anchor, not a button with window.location. It carries the
                located area when there is one — without that, the customer
                types a postal code, watches the map fly to their city, and is
                then shown every listing in Canada. A real href also means
                keyboard activation, middle-click and open-in-new-tab behave
                the way people expect of a navigation control. */}
            <a
              data-testid="browse-all-vehicles"
              href={result?.searchUrl || '/s'}
              style={{
                padding: '13px 32px',
                background: c.teal,
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                boxShadow: '0 4px 16px rgba(26,158,133,0.28)',
                display: 'inline-block',
                textDecoration: 'none',
              }}
            >
              Browse All Vehicles →
            </a>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

function FinanceSection() {
  const [activeTab, setActiveTab] = useState('auto');
  const [activeIdx, setActiveIdx] = useState(0);
  const [form, setForm] = useState({
    postal: '',
    vehicleType: 'auto',
    credit: '',
    province: '',
    name: '',
    phone: '',
    email: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isUserActive, setIsUserActive] = useState(false);

  const idleTimer = useRef(null);
  const snapTimer = useRef(null);
  const autoRotateTimer = useRef(null);

  const rates = activeTab === 'auto' ? AUTO_RATES : REC_RATES;
  const total = rates.length;

  const prev = () => setActiveIdx(i => (i - 1 + total) % total);
  const next = () => setActiveIdx(i => (i + 1) % total);

  // Mark user as active, clear snap/autorotate, reset idle countdown
  const handleUserActivity = useCallback(() => {
    setIsUserActive(true);
    clearTimeout(idleTimer.current);
    clearTimeout(snapTimer.current);
    clearInterval(autoRotateTimer.current);
    // After 2.5s idle → elastic snap back to 0%
    idleTimer.current = setTimeout(() => {
      setIsUserActive(false);
      snapTimer.current = setTimeout(() => {
        setActiveIdx(0);
        // After snapping, start slow auto-rotate
        autoRotateTimer.current = setInterval(() => {
          setActiveIdx(i => (i + 1) % total);
        }, 2800);
      }, 400);
    }, 2500);
  }, [total]);

  // Start auto-rotate on mount
  useEffect(() => {
    autoRotateTimer.current = setInterval(() => {
      setActiveIdx(i => (i + 1) % total);
    }, 2800);
    return () => {
      clearInterval(autoRotateTimer.current);
      clearTimeout(idleTimer.current);
      clearTimeout(snapTimer.current);
    };
  }, [activeTab, total]);

  // Reset when tab switches
  useEffect(() => {
    setActiveIdx(0);
  }, [activeTab]);

  const glassCard = idx => {
    const offset = idx - activeIdx;
    const wrapped = ((offset % total) + total) % total;
    const pos = wrapped <= total / 2 ? wrapped : wrapped - total;
    const absPos = Math.abs(pos);
    if (absPos > 2) return null;
    const x = pos * 220;
    const scale = 1 - absPos * 0.12;
    const opacity = 1 - absPos * 0.35;
    const z = 100 - absPos * 30;
    const blur = absPos * 1.5;
    return { x, scale, opacity, z, blur, pos };
  };

  return (
    <section
      id="financing"
      style={{
        padding: '100px 32px 80px',
        background: 'linear-gradient(160deg, #060D17 0%, #0A1A2E 50%, #071420 100%)',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 700,
          height: 400,
          background: 'radial-gradient(ellipse, rgba(26,158,133,0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
        {/* HEADER */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <span
            style={{
              display: 'inline-block',
              background: 'rgba(26,158,133,0.15)',
              border: '1px solid rgba(26,158,133,0.35)',
              borderRadius: 20,
              padding: '5px 16px',
              fontSize: 11,
              fontWeight: 700,
              color: c.teal,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              marginBottom: 20,
            }}
          >
            Canadian Financing Network
          </span>
          <h2
            style={{
              fontSize: 'clamp(26px, 3.5vw, 44px)',
              fontWeight: 800,
              color: '#FFFFFF',
              margin: '0 0 14px',
              fontFamily: 'DM Sans, sans-serif',
              lineHeight: 1.15,
            }}
          >
            Credit starting at <span style={{ color: c.teal }}>0%</span> across
            <br />
            16 lending partners
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 16,
              maxWidth: 520,
              margin: '0 auto 10px',
              lineHeight: 1.7,
            }}
          >
            See what rate may be available in your postal code and get pre-approved before you shop.
          </p>
          <p style={{ color: c.teal, fontSize: 12, fontWeight: 600, letterSpacing: '0.4px' }}>
            Rates updated July 2026 · No hard credit check to browse
          </p>
        </div>

        {/* TAB TOGGLE */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 52 }}>
          {['auto', 'recreational'].map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setActiveIdx(0);
              }}
              style={{
                padding: '9px 26px',
                borderRadius: 30,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                border: 'none',
                transition: 'all 0.2s',
                background: activeTab === tab ? c.teal : 'rgba(255,255,255,0.06)',
                color: activeTab === tab ? '#fff' : 'rgba(255,255,255,0.5)',
              }}
            >
              {tab === 'auto' ? '🚗 Auto' : '🚤 Recreational'}
            </button>
          ))}
        </div>

        {/* 3D CAROUSEL */}
        <div
          style={{ position: 'relative', height: 280, marginBottom: 48, perspective: '1200px' }}
          onMouseMove={handleUserActivity}
          onTouchStart={handleUserActivity}
          onMouseLeave={() => {
            // treat mouse leaving as idle immediately
            clearTimeout(idleTimer.current);
            clearTimeout(snapTimer.current);
            clearInterval(autoRotateTimer.current);
            snapTimer.current = setTimeout(() => {
              setActiveIdx(0);
              autoRotateTimer.current = setInterval(() => {
                setActiveIdx(i => (i + 1) % total);
              }, 2800);
            }, 800);
          }}
        >
          {rates.map((bank, idx) => {
            const t = glassCard(idx);
            if (!t) return null;
            const isCenter = t.pos === 0;
            // elastic snap: tight spring when snapping to 0, looser when user browsing
            const springConfig =
              !isUserActive && idx === 0
                ? { type: 'spring', stiffness: 380, damping: 22, mass: 0.8 }
                : { type: 'spring', stiffness: 260, damping: 28 };
            return (
              <motion.div
                key={`${activeTab}-${idx}`}
                animate={{ x: t.x, scale: t.scale, opacity: t.opacity, zIndex: t.z }}
                transition={springConfig}
                onClick={() => {
                  handleUserActivity();
                  setActiveIdx(idx);
                }}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  marginLeft: -160,
                  width: 320,
                  cursor: isCenter ? 'default' : 'pointer',
                  filter: `blur(${t.blur}px)`,
                }}
              >
                <div
                  style={{
                    background: isCenter
                      ? 'linear-gradient(135deg, rgba(26,158,133,0.22) 0%, rgba(15,32,51,0.85) 100%)'
                      : 'rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    border: isCenter
                      ? '1.5px solid rgba(26,158,133,0.5)'
                      : '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 20,
                    padding: '28px 28px 24px',
                    boxShadow: isCenter
                      ? '0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(26,158,133,0.15)'
                      : '0 8px 24px rgba(0,0,0,0.3)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 20,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: isCenter ? c.teal : 'rgba(255,255,255,0.3)',
                          letterSpacing: '1.2px',
                          textTransform: 'uppercase',
                          marginBottom: 6,
                        }}
                      >
                        {bank.tag}
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: isCenter ? '#fff' : 'rgba(255,255,255,0.6)',
                          lineHeight: 1.2,
                        }}
                      >
                        {bank.name}
                      </div>
                    </div>
                    {bank.prime && (
                      <span
                        style={{
                          background: 'rgba(26,158,133,0.2)',
                          border: '1px solid rgba(26,158,133,0.4)',
                          borderRadius: 12,
                          padding: '3px 10px',
                          fontSize: 10,
                          fontWeight: 700,
                          color: c.teal,
                          letterSpacing: '0.5px',
                        }}
                      >
                        PRIME
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 48,
                      fontWeight: 900,
                      color: isCenter ? c.teal : 'rgba(255,255,255,0.4)',
                      fontFamily: 'DM Sans, sans-serif',
                      lineHeight: 1,
                      marginBottom: 10,
                    }}
                  >
                    {bank.rate}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: 8,
                        padding: '4px 10px',
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.45)',
                      }}
                    >
                      {bank.term}
                    </span>
                    <span
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: 8,
                        padding: '4px 10px',
                        fontSize: 11,
                        color: 'rgba(255,255,255,0.45)',
                      }}
                    >
                      {bank.models}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* nav arrows */}
          {[{ dir: 'left', fn: prev }, { dir: 'right', fn: next }].map(({ dir, fn }) => (
            <button
              key={dir}
              className={css.rateArrow}
              aria-label={dir === 'left' ? 'Previous rate' : 'Next rate'}
              onClick={() => {
                handleUserActivity();
                fn();
              }}
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                [dir]: dir === 'left' ? 0 : 0,
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#fff',
                fontSize: 18,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 200,
              }}
            >
              {dir === 'left' ? '‹' : '›'}
            </button>
          ))}
        </div>

        {/* DOT INDICATORS */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 52 }}>
          {rates.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                handleUserActivity();
                setActiveIdx(i);
              }}
              style={{
                width: i === activeIdx ? 20 : 6,
                height: 6,
                borderRadius: 3,
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.25s',
                background: i === activeIdx ? c.teal : 'rgba(255,255,255,0.18)',
              }}
            />
          ))}
        </div>

        {/* LENDER COUNT STRIP */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 32,
            flexWrap: 'wrap',
            marginBottom: 52,
          }}
        >
          {[
            { label: 'Credit Unions', count: 2 },
            { label: 'Big 5 Banks', count: 5 },
            { label: 'OEM & Captive', count: 3 },
            { label: 'Alt & Non-Prime', count: 3 },
            { label: 'RV & Marine', count: 3 },
          ].map((g, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: c.teal,
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                {g.count}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.4)',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                }}
              >
                {g.label}
              </div>
            </div>
          ))}
        </div>

        {/* CTA BUTTONS */}
        <div
          style={{
            display: 'flex',
            gap: 14,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: 40,
          }}
        >
          <button
            onClick={() => setShowForm(f => !f)}
            style={{
              padding: '15px 32px',
              background: c.teal,
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              boxShadow: '0 4px 20px rgba(26,158,133,0.35)',
            }}
          >
            See If That Rate Is In Your Postal Code
          </button>
          <button
            onClick={() => setShowForm(f => !f)}
            style={{
              padding: '15px 32px',
              background: 'rgba(255,255,255,0.07)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 12,
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            Get Pre-Approved For That Rate
          </button>
        </div>

        {/* INLINE FORM */}
        {showForm && !submitted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              background: 'rgba(255,255,255,0.04)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20,
              padding: '36px 40px',
              marginBottom: 32,
            }}
          >
            <h3
              style={{
                color: '#fff',
                fontWeight: 700,
                fontSize: 18,
                margin: '0 0 24px',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Check Your Rate — No Hard Credit Pull
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 14,
              }}
            >
              {[
                { key: 'postal', label: 'Postal Code', placeholder: 'e.g. M5V 2T6', type: 'text' },
                { key: 'name', label: 'Full Name', placeholder: 'Your name', type: 'text' },
                { key: 'phone', label: 'Phone', placeholder: '(555) 000-0000', type: 'tel' },
                { key: 'email', label: 'Email', placeholder: 'you@email.com', type: 'email' },
              ].map(f => (
                <div key={f.key}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'rgba(255,255,255,0.5)',
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    {f.label}
                  </label>
                  <input
                    type={f.type}
                    placeholder={f.placeholder}
                    value={form[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 10,
                      color: '#fff',
                      fontSize: 14,
                      fontFamily: 'DM Sans, sans-serif',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ))}
              {[
                {
                  key: 'vehicleType',
                  label: 'Vehicle Type',
                  options: ['Auto', 'Truck', 'SUV', 'RV', 'Marine', 'Motorcycle'],
                },
                {
                  key: 'credit',
                  label: 'Credit Range',
                  options: [
                    'Excellent (720+)',
                    'Good (660—719)',
                    'Fair (600—659)',
                    'Rebuilding (<600)',
                  ],
                },
                {
                  key: 'province',
                  label: 'Province',
                  options: [
                    'AB',
                    'BC',
                    'MB',
                    'NB',
                    'NL',
                    'NS',
                    'ON',
                    'PE',
                    'QC',
                    'SK',
                    'NT',
                    'NU',
                    'YT',
                  ],
                },
              ].map(f => (
                <div key={f.key}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'rgba(255,255,255,0.5)',
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    {f.label}
                  </label>
                  <select
                    value={form[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      background: '#0A1A2E',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 10,
                      color: '#fff',
                      fontSize: 14,
                      fontFamily: 'DM Sans, sans-serif',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Select…</option>
                    {f.options.map(o => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {submitError && (
              <p style={{ color: '#ff6b6b', fontSize: 13, marginTop: 14, fontWeight: 600 }}>
                {submitError}
              </p>
            )}
            <button
              disabled={submitting}
              onClick={async () => {
                if (!form.name || !form.email || !form.postal) {
                  setSubmitError('Please fill in your name, email, and postal code.');
                  return;
                }
                setSubmitError(null);
                setSubmitting(true);
                try {
                  const res = await fetch('/api/rate-inquiry', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form),
                  });
                  const data = await res.json();
                  if (data.success) setSubmitted(true);
                  else setSubmitError('Something went wrong. Please try again.');
                } catch {
                  setSubmitError('Network error. Please try again.');
                } finally {
                  setSubmitting(false);
                }
              }}
              style={{
                marginTop: 24,
                padding: '14px 36px',
                background: submitting ? 'rgba(26,158,133,0.5)' : c.teal,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 15,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                boxShadow: '0 4px 20px rgba(26,158,133,0.35)',
                transition: 'all 0.2s',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit — Check My Rate'}
            </button>
          </motion.div>
        )}

        {/* SUCCESS SCREEN */}
        {showForm && submitted && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{
              background:
                'linear-gradient(135deg, rgba(26,158,133,0.18) 0%, rgba(15,32,51,0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1.5px solid rgba(26,158,133,0.4)',
              borderRadius: 20,
              padding: '52px 40px',
              marginBottom: 32,
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 56, marginBottom: 20 }}>🎉</div>
            <h3
              style={{
                color: '#fff',
                fontWeight: 800,
                fontSize: 26,
                margin: '0 0 12px',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Congratulations, {form.name.split(' ')[0]}!
            </h3>
            <p
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 17,
                maxWidth: 480,
                margin: '0 auto 20px',
                lineHeight: 1.7,
              }}
            >
              We are working to match you with the best available rate from our network of{' '}
              <strong style={{ color: c.teal }}>16 Canadian lending partners</strong>.
            </p>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                background: 'rgba(26,158,133,0.15)',
                border: '1px solid rgba(26,158,133,0.35)',
                borderRadius: 12,
                padding: '12px 24px',
                marginBottom: 28,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: c.teal,
                  display: 'inline-block',
                  animation: 'pulse 2s infinite',
                }}
              />
              <span style={{ color: c.teal, fontWeight: 700, fontSize: 14 }}>
                A financing specialist will contact you within 24 hours
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 14,
                maxWidth: 480,
                margin: '0 auto 28px',
              }}
            >
              {[
                { label: 'Province', value: form.province || '—' },
                { label: 'Vehicle Type', value: form.vehicleType || 'Auto' },
                { label: 'Credit Profile', value: form.credit || '—' },
              ].map(item => (
                <div
                  key={item.label}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 10,
                    padding: '12px',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.4)',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      marginBottom: 4,
                    }}
                  >
                    {item.label}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{item.value}</div>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                setSubmitted(false);
                setShowForm(false);
                setForm({
                  postal: '',
                  vehicleType: 'auto',
                  credit: '',
                  province: '',
                  name: '',
                  phone: '',
                  email: '',
                });
              }}
              style={{
                padding: '11px 28px',
                background: 'transparent',
                color: 'rgba(255,255,255,0.5)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Submit another inquiry
            </button>
          </motion.div>
        )}

        {/* DISCLAIMER */}
        <p
          style={{
            textAlign: 'center',
            color: 'rgba(255,255,255,0.22)',
            fontSize: 11,
            lineHeight: 1.7,
            maxWidth: 680,
            margin: '0 auto',
          }}
        >
          Rates are subject to lender approval, credit profile, vehicle year, term, province, postal
          code, and program availability. SkipTheDealerships.ai is a SaaS technology platform, not a
          lender. All financing is arranged through licensed Canadian financial institutions. Rates
          shown are effective July 2026 and subject to change without notice.
        </p>
      </div>
    </section>
  );
}

function useHeroSpotlight() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = e => {
      const r = el.getBoundingClientRect();
      const x = (((e.clientX - r.left) / r.width) * 100).toFixed(2);
      const y = (((e.clientY - r.top) / r.height) * 100).toFixed(2);
      el.style.setProperty('--sx', x + '%');
      el.style.setProperty('--sy', y + '%');
    };
    el.addEventListener('mousemove', onMove, { passive: true });
    return () => el.removeEventListener('mousemove', onMove);
  }, []);
  return ref;
}

// ── Shared hook: load real listings on mount ──────────────────────────────────
function useListings(count = 4) {
  const [listings, setListings] = useState([]);
  useEffect(() => {
    fetch(`/api/listings?perPage=${count}`)
      .then(r => r.json())
      .then(d => setListings(d.data || []))
      .catch(() => {});
  }, [count]);
  return listings;
}

function listingImg(l) {
  return l?.images?.[0]?.attributes?.variants?.['landscape-crop']?.url || null;
}
function listingPrice(l) {
  const cents = l?.attributes?.price?.amount;
  return cents ? '$' + Math.round(cents / 100).toLocaleString('en-CA') : null;
}
function listingTitle(l) {
  return l?.attributes?.title || 'Verified Vehicle';
}
function listingYear(l) {
  return l?.attributes?.publicData?.year || new Date().getFullYear();
}

// ── Card 1: Market Value — shows real listing, fetches real CMV on click ──────
// ── Shared image-zoom wrapper (SSR-safe — no window in render) ────────────────
/**
 * `href` is the vehicle's canonical URL (see util/vehicleLinks). When given, the
 * whole image area is a real link to that vehicle — an overlay <a>, a sibling of
 * the overlaid children, so no interactive element is nested inside another.
 * When absent (no listing yet, or a purely decorative backdrop) no link is
 * rendered: never "#", never another page standing in for the vehicle.
 */
function ZoomImg({ src, alt, brightness = 1, children, href = null }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ height: 160, overflow: 'hidden', position: 'relative', flexShrink: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: `brightness(${brightness})`,
            transform: hovered ? 'scale(1.07)' : 'scale(1)',
            transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg,#DFF0ED,#E8F3F8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
          }}
        >
          <svg viewBox="0 0 80 40" width="80" height="40" fill="none">
            <path
              d="M6 30 Q20 8 40 10 Q60 12 74 30"
              stroke="#1A9E85"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
            <ellipse cx="40" cy="31" rx="34" ry="5" fill="rgba(26,158,133,0.12)" />
            <circle cx="18" cy="31" r="5" fill="#1A9E85" opacity="0.7" />
            <circle cx="62" cy="31" r="5" fill="#1A9E85" opacity="0.7" />
          </svg>
        </div>
      )}
      {children}
      {href ? (
        <a
          href={href}
          aria-label={alt ? `View ${alt}` : 'View this vehicle'}
          style={{ position: 'absolute', inset: 0, zIndex: 2 }}
        />
      ) : null}
    </div>
  );
}

function CardMarketValue({ listing }) {
  const [cmv, setCmv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const img = listingImg(listing);
  const price = listingPrice(listing);
  const title = listingTitle(listing);
  const vin = listing?.attributes?.publicData?.vin;
  const km = listing?.attributes?.publicData?.mileage || listing?.attributes?.publicData?.odometer;

  async function fetchCmv() {
    if (!vin || vin.length !== 17) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/cmv?vin=${encodeURIComponent(vin)}&mileage=${km || 80000}`);
      const data = await res.json();
      if (res.ok) setCmv(data);
    } catch {
    } finally {
      setLoading(false);
    }
  }

  const fmt = n => (n ? '$' + Number(n).toLocaleString('en-CA') : null);

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55 }}
      whileHover={{ y: -8, scale: 1.015 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: c.white,
        borderRadius: 18,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
        boxShadow: hovered
          ? `0 8px 40px rgba(26,158,133,0.28), 0 2px 12px rgba(0,0,0,0.08)`
          : '0 4px 24px rgba(0,0,0,0.07)',
        border: hovered ? `1.5px solid rgba(26,158,133,0.4)` : `1px solid ${c.border}`,
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
      }}
    >
      <ZoomImg src={img} alt={title} brightness={1} href={vehiclePath(listing)}>
        {price && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 12,
              background: 'rgba(15,32,51,0.82)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 6,
            }}
          >
            {price}
          </div>
        )}
        {verificationState(listing).verified ? (
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              background: c.teal,
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 5,
            }}
          >
            ✓ VERIFIED
          </div>
        ) : null}
      </ZoomImg>
      <div style={{ padding: '18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: c.teal,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          Know Your Vehicle Value
        </div>
        <div
          style={{ fontSize: 14, fontWeight: 700, color: c.navy, marginBottom: 8, lineHeight: 1.3 }}
        >
          {title}
        </div>
        {!cmv ? (
          <button
            onClick={fetchCmv}
            disabled={loading || !vin}
            style={{
              marginTop: 'auto',
              background: c.teal,
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '10px 0',
              fontWeight: 700,
              fontSize: 13,
              cursor: !vin || loading ? 'not-allowed' : 'pointer',
              opacity: !vin ? 0.6 : 1,
            }}
          >
            {loading ? 'Fetching CMV…' : vin ? 'Check Market Value →' : 'Browse for CMV →'}
          </button>
        ) : (
          <div style={{ marginTop: 'auto' }}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: c.teal,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {fmt(cmv.estimatedValue)}
            </div>
            <div style={{ fontSize: 11, color: c.textMid, marginBottom: 8 }}>
              Range: {fmt(cmv.fairRangeLow)} – {fmt(cmv.fairRangeHigh)} · {cmv.confidenceScore}%
              confidence
            </div>
            <a
              href="/l/new"
              style={{
                display: 'inline-block',
                background: c.teal,
                color: '#fff',
                textDecoration: 'none',
                fontSize: 12,
                fontWeight: 700,
                padding: '8px 14px',
                borderRadius: 8,
              }}
            >
              List at This Value →
            </a>
          </div>
        )}
      </div>
    </motion.article>
  );
}

function CardSellSmarter({ listing }) {
  const [hovered, setHovered] = useState(false);
  const img = listingImg(listing);
  const price = listingPrice(listing);
  const title = listingTitle(listing);

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: 0.1 }}
      whileHover={{ y: -8, scale: 1.015 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: c.navy,
        borderRadius: 18,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
        boxShadow: hovered
          ? `0 8px 40px rgba(26,158,133,0.22), 0 2px 12px rgba(0,0,0,0.18)`
          : '0 4px 24px rgba(0,0,0,0.18)',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <ZoomImg src={img} alt={title} brightness={0.75} href={vehiclePath(listing)}>
        {price && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 12,
              background: 'rgba(26,158,133,0.9)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 6,
            }}
          >
            {price}
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            padding: '3px 8px',
            borderRadius: 5,
            backdropFilter: 'blur(4px)',
          }}
        >
          JUST LISTED
        </div>
      </ZoomImg>
      <div style={{ padding: '18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: c.teal,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          Sell Smarter
        </div>
        <div
          style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6, lineHeight: 1.3 }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.6)',
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          AI writes your listing. Verified buyers only. Average seller keeps $6,500 more.
        </div>
        <a
          href="/l/new"
          style={{
            marginTop: 'auto',
            display: 'inline-block',
            background: '#fff',
            color: c.navy,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 700,
            padding: '10px 20px',
            borderRadius: 9,
            alignSelf: 'flex-start',
          }}
        >
          List My Vehicle →
        </a>
      </div>
    </motion.article>
  );
}

function CardPaymentCalc({ listing }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);
  const img = listingImg(listing);
  const title = listingTitle(listing);
  const priceCAD = listing ? Math.round((listing?.attributes?.price?.amount || 0) / 100) : 0;
  const year = listingYear(listing);
  const listingId = listing?.id?.uuid;

  useEffect(() => {
    if (!priceCAD || priceCAD < 1000) return;
    setLoading(true);
    fetch('/api/lender-rates/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category: 'auto',
        vehicleYear: year,
        vehiclePrice: priceCAD,
        downPayment: 0,
        province: 'MB',
        creditTier: 'B',
        frequency: 'monthly',
      }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.eligible) setQuote(d.quote);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [listingId]);

  const fmt = n =>
    n ? '$' + Number(n).toLocaleString('en-CA', { maximumFractionDigits: 0 }) : '—';
  const monthly = quote?.payment?.monthly ?? quote?.monthlyPayment;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: 0.2 }}
      whileHover={{ y: -8, scale: 1.015 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: '#0B1B2B',
        borderRadius: 18,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
        boxShadow: hovered
          ? `0 8px 40px rgba(26,158,133,0.25), 0 2px 12px rgba(0,0,0,0.22)`
          : '0 4px 24px rgba(0,0,0,0.22)',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <ZoomImg src={img} alt={title} brightness={0.6} href={vehiclePath(listing)}>
        {priceCAD > 0 && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              left: 12,
              background: 'rgba(15,32,51,0.85)',
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 6,
            }}
          >
            {fmt(priceCAD)}
          </div>
        )}
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(11,27,43,0.6)',
              color: c.teal,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Calculating payment…
          </div>
        )}
      </ZoomImg>
      <div style={{ padding: '18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: c.teal,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          Pick a Car. Pick a Payment.
        </div>
        <div
          style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6, lineHeight: 1.3 }}
        >
          {title}
        </div>
        {monthly ? (
          <div style={{ marginTop: 'auto' }}>
            <div
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: c.teal,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {fmt(monthly)}
              <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                /mo
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>
              {quote?.audit?.lender || quote?.lenderName} · {quote?.audit?.rate ?? quote?.rate}% ·{' '}
              {quote?.audit?.term ?? quote?.term} mo · OAC
            </div>
            {vehiclePath(listing) ? (
              <a
                href={vehiclePath(listing)}
                style={{
                  display: 'inline-block',
                  background: c.teal,
                  color: '#fff',
                  textDecoration: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '8px 14px',
                  borderRadius: 8,
                  position: 'relative',
                  zIndex: 3,
                }}
              >
                View This Vehicle →
              </a>
            ) : null}
          </div>
        ) : (
          <div style={{ marginTop: 'auto' }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>
              Real payments from 28+ lenders. No hard credit check.
            </div>
            <a
              href="/s"
              style={{
                display: 'inline-block',
                background: c.teal,
                color: '#fff',
                textDecoration: 'none',
                fontSize: 12,
                fontWeight: 700,
                padding: '8px 14px',
                borderRadius: 8,
              }}
            >
              Explore Vehicles →
            </a>
          </div>
        )}
      </div>
    </motion.article>
  );
}

function CardDelivery({ listing }) {
  const [total, setTotal] = useState(null);
  const [displayCount, setDisplayCount] = useState(0);
  const [hovered, setHovered] = useState(false);
  const img = listingImg(listing);
  const title = listingTitle(listing);

  useEffect(() => {
    fetch('/api/listings?perPage=1')
      .then(r => r.json())
      .then(d => setTotal(d.meta?.totalItems ?? d.meta?.total ?? null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!total || typeof total !== 'number') return;
    let start = 0;
    const duration = 1800;
    const steps = 60;
    const inc = total / steps;
    const timer = setInterval(() => {
      start += inc;
      if (start >= total) {
        setDisplayCount(total);
        clearInterval(timer);
      } else setDisplayCount(Math.round(start));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [total]);

  function scrollToNearYou() {
    const el =
      document.querySelector('[id*="near"]') ||
      document
        .querySelector('input[placeholder*="postal"], input[placeholder*="Postal"]')
        ?.closest('section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: 0.3 }}
      whileHover={{ y: -8, scale: 1.015 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{
        background: '#10375C',
        borderRadius: 18,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
        boxShadow: hovered
          ? `0 8px 40px rgba(26,158,133,0.22), 0 2px 12px rgba(0,0,0,0.22)`
          : '0 4px 24px rgba(0,0,0,0.22)',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      {/* Decorative backdrop for a network-wide statistic ("N verified listings"), not an
          offer on this one vehicle — so it carries no vehicle name and no vehicle link. */}
      <ZoomImg src={img} alt="" brightness={0.55}>
        {total !== null && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                fontSize: 38,
                fontWeight: 800,
                color: '#fff',
                fontFamily: 'DM Sans, sans-serif',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {typeof total === 'number' ? displayCount.toLocaleString('en-CA') : total}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
              verified listings coast to coast
            </div>
          </div>
        )}
      </ZoomImg>
      <div style={{ padding: '18px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.55)',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 4,
          }}
        >
          Canada-Wide Delivery
        </div>
        <div
          style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6, lineHeight: 1.3 }}
        >
          Buy from anywhere. Delivered to you.
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.55)',
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          From Victoria to St. John's — every listing ships Canada-wide.
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
          <button
            onClick={scrollToNearYou}
            style={{
              flex: 1,
              background: '#fff',
              color: '#10375C',
              border: 'none',
              borderRadius: 9,
              padding: '10px 0',
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Find Near Me →
          </button>
          <a
            href="/s"
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              textDecoration: 'none',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 9,
            }}
          >
            Browse All →
          </a>
        </div>
      </div>
    </motion.article>
  );
}

function ExploreVehicles() {
  const listings = useListings(4);
  const loading = listings.length === 0;
  return (
    <section
      style={{ padding: '72px 32px', background: c.bg, borderBottom: `1px solid ${c.border}` }}
    >
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: c.teal,
              marginBottom: 10,
            }}
          >
            SKIPTHEDEALERSHIPS.AI
          </div>
          <h2
            style={{
              fontSize: 'clamp(24px, 3.5vw, 40px)',
              fontWeight: 800,
              color: c.navy,
              margin: 0,
              fontFamily: 'DM Sans, sans-serif',
              lineHeight: 1.15,
            }}
          >
            Explore vehicles you'll love
          </h2>
        </div>
        {loading ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 20,
            }}
          >
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                style={{
                  borderRadius: 18,
                  overflow: 'hidden',
                  minHeight: 300,
                  background: c.border,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)`,
                    backgroundSize: '200% 100%',
                    animation: 'evShimmer 1.5s infinite',
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 20,
            }}
          >
            <CardMarketValue listing={listings[0]} />
            <CardSellSmarter listing={listings[1]} />
            <CardPaymentCalc listing={listings[2]} />
            <CardDelivery listing={listings[3]} />
          </div>
        )}
      </div>
      <style>{`@keyframes evShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </section>
  );
}

const NAV_LINKS = {
  consumer: [
    { label: 'Browse Vehicles', href: '#inventory' },
    { label: 'Sell My Car', href: '/l/new' },
    { label: 'How It Works', href: '#how' },
    { label: 'Financing', href: '#financing' },
    { label: 'Warranty & Protection', href: '#warranty' },
  ],
  dealer: [
    { label: 'Dealer Partner Program', href: '/dealers', cta: true },
    { label: 'Dealer Login', href: '/login?dealer=true' },
    { label: 'Dealer Dashboard', href: '/dealer/dashboard' },
    { label: 'Register as Dealer', href: '/dealer/register' },
    { label: 'List Dealer Inventory', href: '/dealer/dashboard#inventory' },
  ],
};

export function NavDrawer({ open, onClose }) {
  const account = useAccountLink();
  return (
    <>
      {/* Drawer overlay */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 9998,
            backdropFilter: 'blur(3px)',
          }}
        />
      )}

      {/* Drawer panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 300,
          background: '#0d1e30',
          zIndex: 9999,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition:
            'transform 0.28s cubic-bezier(0.4,0,0.2,1), box-shadow 0.28s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          // The closed panel is only translated off-screen, not unmounted, and a
          // shadow cast leftward from it lands back on the visible page as a grey
          // band down the right edge — so it only casts one while open.
          boxShadow: open ? '-8px 0 40px rgba(0,0,0,0.5)' : 'none',
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.2em',
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
            }}
          >
            🍁 Menu
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              fontSize: 22,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '24px 24px 32px', flex: 1 }}>
          {/* Consumer section */}
          <div style={{ marginBottom: 32 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: '#1A9E85',
                textTransform: 'uppercase',
                marginBottom: 14,
              }}
            >
              Buy &amp; Sell
            </div>
            {NAV_LINKS.consumer.map(link => (
              <a
                key={link.label}
                href={link.href}
                onClick={onClose}
                style={{
                  display: 'block',
                  color: 'rgba(255,255,255,0.82)',
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 500,
                  padding: '11px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Dealer section */}
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: '#1A9E85',
                textTransform: 'uppercase',
                marginBottom: 14,
              }}
            >
              Dealer Portal
            </div>
            {NAV_LINKS.dealer.map(link =>
              link.cta ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={onClose}
                  style={{
                    display: 'block',
                    background: '#1A9E85',
                    color: '#fff',
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: 700,
                    padding: '13px 18px',
                    borderRadius: 9,
                    textAlign: 'center',
                    marginBottom: 12,
                  }}
                >
                  {link.label} →
                </a>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={onClose}
                  style={{
                    display: 'block',
                    color: 'rgba(255,255,255,0.75)',
                    textDecoration: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    padding: '11px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {link.label}
                </a>
              )
            )}
          </div>
        </div>

        {/* Drawer footer */}
        <div style={{ padding: '20px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <a
            href={account.href}
            onClick={e => {
              account.onClick(e);
              onClose();
            }}
            style={{
              display: 'block',
              color: 'rgba(255,255,255,0.5)',
              textDecoration: 'none',
              fontSize: 13,
              fontWeight: 500,
              marginBottom: 8,
            }}
          >
            {account.label}
          </a>
          {!account.signedIn && (
            <a
              href="/signup"
              onClick={onClose}
              style={{
                display: 'block',
                color: 'rgba(255,255,255,0.5)',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Sign Up
            </a>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * What the header's account control should say, and where it should point.
 *
 * This page renders its own navigation — HeroNav, StickyNav and NavDrawer — and
 * never TopbarContainer, so none of the three had any idea whether a customer
 * was signed in: all three offered an unconditional "Log In".
 *
 * That is worse than cosmetic, because of where a successful login lands. A
 * customer who signs in from this header has no `from`, so
 * AuthenticationPage sends them to the LandingPage — this page. The first thing
 * they saw after logging in was a header still inviting them to log in, with no
 * account menu and nothing leading to their garage. It reads as a login that
 * did not work, and the real topbar that would have shown otherwise renders on
 * every route except this one.
 *
 * Reuses the existing /listings route and the "My Garage" wording the real
 * topbar's panel already uses, rather than inventing a destination.
 */
function useAccountLink() {
  const intl = useIntl();
  const history = useHistory();
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);

  // Deliberately withheld until after mount.
  //
  // `server/dataLoader.js` never dispatches `authInfo`, so the server-rendered
  // store always says signed out. The client does the opposite: `src/index.js`
  // awaits `authInfo()` *before* `hydrateRoot`, so by the first client render
  // the session is known. Reading it directly would therefore have the server
  // emit "Log In" and the first client render emit "My Garage" — a hydration
  // mismatch, which React reports as a recoverable error and recovers from by
  // throwing away and re-rendering this entire landing page tree.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const signedIn = mounted && Boolean(isAuthenticated);

  const href = signedIn ? '/listings' : '/login';
  const label = signedIn
    ? intl.formatMessage({ id: 'SkipLandingPage.myGarage', defaultMessage: 'My Garage' })
    : intl.formatMessage({ id: 'SkipLandingPage.logIn', defaultMessage: 'Log In' });

  /**
   * Navigates through the client router instead of letting the anchor do a
   * full-page GET.
   *
   * `/listings` requires authentication. A full-page request is answered by the
   * server, which has no session, so `Routes.js` redirects it to
   * `/login?returnTo=/listings` — and `AuthenticationPage` takes its return
   * path from router state, not from that query parameter. It therefore finds
   * the live session, has no `from`, and sends the customer back to the landing
   * page. The control would loop them home, which is the very symptom this
   * change exists to fix.
   *
   * The anchor and its href stay, so middle-click, ctrl-click and "open in new
   * tab" still work and the link is still a link.
   */
  const onClick = e => {
    if (!signedIn || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    history.push(href);
  };

  return { href, label, signedIn, onClick };
}

function HamburgerButton({ onClick, dark }) {
  return (
    <button
      onClick={onClick}
      aria-label="Open menu"
      style={{
        background: dark ? 'rgba(15,32,51,0.06)' : 'rgba(255,255,255,0.1)',
        border: dark ? '1px solid rgba(15,32,51,0.14)' : '1px solid rgba(255,255,255,0.2)',
        borderRadius: 9,
        width: 44,
        height: 44,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {[0, 1, 2].map(i => (
        <span
          key={i}
          style={{
            display: 'block',
            width: 20,
            height: 2,
            background: dark ? 'rgba(15,32,51,0.85)' : 'rgba(255,255,255,0.9)',
            borderRadius: 2,
          }}
        />
      ))}
    </button>
  );
}

export function StickyNav() {
  const account = useAccountLink();
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > 90);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const tabs = [
    { label: 'Search Cars', href: '#inventory', id: 'buy' },
    { label: 'Sell / Trade', href: '/l/new', id: 'sell' },
    { label: 'Financing', href: '#financing', id: 'finance' },
    { label: 'Dealer Partners', href: '/dealers', id: 'dealer' },
    { label: account.label, href: account.href, id: 'login', onClick: account.onClick },
  ];
  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 8000,
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1.5px solid #e8edf2',
          boxShadow: '0 2px 18px rgba(0,0,0,0.09)',
          transform: visible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 5%',
        }}
      >
        {/* Brand — hidden on mobile to save space */}
        <div
          className="stickyNavBrand"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.15em',
            color: '#0F2033',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            paddingRight: 28,
          }}
        >
          <span style={{ color: '#D52B1E', fontSize: 18 }}>🍁</span>
          <span>
            Skip<span style={{ color: '#1A9E85' }}>The</span>Dealerships
          </span>
        </div>

        {/* Mobile quick-nav — 4 pill tabs, shown only on small screens */}
        <div
          className="stickyNavMobile"
          style={{
            display: 'none',
            width: '100%',
            justifyContent: 'space-around',
            alignItems: 'center',
          }}
        >
          {[
            { label: 'Buy', href: '#inventory', icon: '🔍' },
            { label: 'Sell', href: '/l/new', icon: '🚗' },
            { label: 'Finance', href: '#financing', icon: '💰' },
            { label: 'Dealers', href: '/dealers', icon: '🏢', teal: true },
          ].map(t => (
            <a
              key={t.label}
              href={t.href}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                textDecoration: 'none',
                padding: '8px 10px',
                color: t.teal ? '#1A9E85' : '#1a2533',
                fontSize: 10,
                fontWeight: t.teal ? 700 : 600,
              }}
            >
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              {t.label}
            </a>
          ))}
          <a
            href="/l/new"
            style={{
              background: '#1A9E85',
              color: '#fff',
              textDecoration: 'none',
              fontSize: 11,
              fontWeight: 700,
              padding: '7px 14px',
              borderRadius: 7,
              whiteSpace: 'nowrap',
            }}
          >
            List →
          </a>
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              padding: '8px 6px',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {[0, 1, 2].map(i => (
              <span
                key={i}
                style={{
                  display: 'block',
                  width: 16,
                  height: 2,
                  background: '#1a2533',
                  borderRadius: 2,
                }}
              />
            ))}
          </button>
        </div>
        {/* Tabs — hidden on mobile, shown on desktop */}
        <div
          className="stickyNavTabs"
          style={{ display: 'flex', alignItems: 'stretch', gap: 0, flex: 1 }}
        >
          {tabs.map(t => (
            <a
              key={t.id}
              href={t.href}
              onClick={e => {
                setActive(t.id);
                if (t.onClick) t.onClick(e);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 16px',
                fontSize: 13.5,
                fontWeight: t.id === 'dealer' ? 700 : 550,
                color: t.id === 'dealer' ? '#1A9E85' : t.id === 'login' ? '#6b7a8d' : '#1a2533',
                textDecoration: 'none',
                borderBottom: active === t.id ? '2.5px solid #1A9E85' : '2.5px solid transparent',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
                letterSpacing: '-0.01em',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.color = '#1A9E85';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color =
                  t.id === 'dealer' ? '#1A9E85' : t.id === 'login' ? '#6b7a8d' : '#1a2533';
              }}
            >
              {t.label}
            </a>
          ))}
        </div>
        {/* CTA — desktop only */}
        <a
          href="/l/new"
          className="stickyNavCta"
          style={{
            background: '#1A9E85',
            color: '#fff',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 700,
            padding: '9px 20px',
            borderRadius: 8,
            whiteSpace: 'nowrap',
            marginLeft: 16,
            flexShrink: 0,
          }}
        >
          List My Vehicle →
        </a>
        <div className="stickyNavCta" style={{ marginLeft: 8 }}>
          <HamburgerButton dark onClick={() => setMenuOpen(true)} />
        </div>
      </div>

      <NavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

export function HeroNav() {
  const account = useAccountLink();
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Nav bar */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 5%',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* LEFT — logo + brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <a
            href="/"
            style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}
          >
            <span style={{ fontSize: 22 }}>🍁</span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: '#fff',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              SkipTheDealerships<span style={{ color: '#1A9E85' }}>.ai</span>
            </span>
          </a>
          <a
            href="#inventory"
            style={{
              color: 'rgba(255,255,255,0.85)',
              textDecoration: 'none',
              fontSize: 15,
              fontWeight: 600,
              display: 'none',
            }}
            className="nav-browse"
          >
            Browse Vehicles
          </a>
        </div>

        {/* RIGHT — nav links + hamburger */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div className="desktopNav" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <a
              href="#inventory"
              style={{
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
              }}
            >
              Buy
            </a>
            <a
              href="/l/new"
              style={{
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
              }}
            >
              Sell / Trade
            </a>
            <a
              href="#financing"
              style={{
                color: 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
              }}
            >
              Financing
            </a>
            <a
              href="/dealers"
              style={{
                color: '#1A9E85',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 700,
                border: '1.5px solid rgba(26,158,133,0.6)',
                padding: '8px 16px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
                background: 'rgba(26,158,133,0.08)',
              }}
            >
              Dealer Partners
            </a>
            <a
              href={account.href}
              onClick={account.onClick}
              style={{
                color: 'rgba(255,255,255,0.7)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 8,
              }}
            >
              {account.label}
            </a>
          </div>

          {/* Hamburger */}
          <div style={{ marginLeft: 6 }}>
            <HamburgerButton onClick={() => setOpen(true)} />
          </div>
        </div>
      </div>

      <NavDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

const FAQ_GROUPS = [
  {
    category: 'How It Works',
    items: [
      {
        q: 'How does SkipTheDealerships.ai work?',
        a:
          'You create a free account, list your vehicle or browse listings, connect directly with verified buyers or sellers, and complete the transaction — all without a dealership involved. Our AI tools handle pricing, financing, vehicle checks, and document guidance at every step.',
      },
      {
        q: 'Is SkipTheDealerships.ai only for private sellers?',
        a:
          'No. Private sellers, buyers, and licensed dealer partners all use the platform. Dealers get their own channel page, inventory dashboard, and lead management tools. Private sellers pay no monthly fees — only a small flat fee when their vehicle sells.',
      },
      {
        q: 'How do I know a listing is legitimate?',
        a:
          'Every listing is run through a stolen vehicle database check automatically. Sellers are identity-verified, and buyers are pre-screened before they can contact you. Flagged listings are suspended immediately.',
      },
      {
        q: 'What vehicles can I list on SkipTheDealerships.ai?',
        a:
          'Cars, trucks, SUVs, vans, motorcycles, ATVs, side-by-sides, boats, RVs, trailers, and powersports equipment. If it has a VIN, HIN, or serial number and can be titled in Canada, you can list it.',
      },
      {
        q: 'How long does it take to list my vehicle?',
        a:
          'About 5 minutes. Enter your VIN and our AI auto-fills the year, make, model, and trim. Upload photos, set your price using our Market Value tool, and publish. Your listing is live immediately.',
      },
    ],
  },
  {
    category: 'Pricing & Fees',
    items: [
      {
        q: 'How much does it cost to list a vehicle?',
        a:
          'Listing is free. We charge a small flat success fee only when your vehicle sells — no monthly subscription, no upfront cost, no hidden charges. You keep the full negotiated price minus the platform fee.',
      },
      {
        q: 'How much do dealer partners pay?',
        a:
          'Dealer partners start at $0/month during our Early Bird program (first 100 dealers). Standard pricing is $999.99/month which includes up to 1,000 active listings, a dedicated channel page, lead management, and analytics.',
      },
      {
        q: 'Are there any hidden fees for buyers?',
        a:
          'No. Buyers pay the agreed price between them and the seller. There are no buyer premiums, admin fees, or doc fees — the costs that dealerships typically add $2,000–$5,000 to the purchase price.',
      },
    ],
  },
  {
    category: 'Legal & Safety',
    items: [
      {
        q: 'Is selling a vehicle privately legal in Canada?',
        a:
          'Yes. Private vehicle sales are completely legal in every Canadian province and territory. You do not need a dealer licence to sell your own personally-owned vehicle. Provincial regulations vary on the number of vehicles you can sell per year before requiring a dealer licence — typically 3 to 6 vehicles annually for private individuals.',
      },
      {
        q: 'What documents do I need to complete a private sale in Canada?',
        a:
          "You need the vehicle's ownership certificate (pink slip), a bill of sale signed by both parties, a safety standards certificate (required in Ontario, Manitoba, and some other provinces), and an emissions test where applicable. SkipTheDealerships.ai provides province-specific document checklists and a digital bill of sale template.",
      },
      {
        q: 'Who pays the sales tax on a private vehicle sale in Canada?',
        a:
          'The buyer pays provincial sales tax (PST or HST depending on the province) at the time of registration — not at the point of sale. In most provinces, tax is calculated on the greater of the actual sale price or the Canadian Red Book value. The seller does not collect or remit tax.',
      },
      {
        q: 'Is the seller liable if the vehicle has problems after the sale?',
        a:
          'Private vehicle sales in Canada are generally sold "as-is" unless you provide a written warranty. Once the bill of sale is signed and the buyer accepts the vehicle, the seller is typically not liable for mechanical issues discovered afterward. We recommend being transparent in your listing description and including an "as-is, where-is" clause in your bill of sale. SkipTheDealerships.ai provides a template that includes this protection.',
      },
      {
        q: 'What if the vehicle has an outstanding loan?',
        a:
          'You must disclose any existing lien to the buyer. Before transferring ownership, the lien must be paid out — typically from the sale proceeds. The buyer can verify lien status through a PPSA search, which our platform facilitates. Selling a vehicle with an undisclosed lien is fraud.',
      },
      {
        q: 'Do I need a safety inspection to sell my vehicle?',
        a:
          'Requirements vary by province. Ontario and Manitoba require a Safety Standards Certificate. BC, Alberta, and Saskatchewan do not require a pre-sale inspection but the buyer may request one. Quebec requires a mechanical inspection for older vehicles. Our platform provides province-specific requirements based on where your vehicle is registered.',
      },
      {
        q: 'What happens if I buy a stolen vehicle?',
        a:
          'If you unknowingly purchase a stolen vehicle, law enforcement can seize it and you lose both the vehicle and the money paid. This is why every listing on SkipTheDealerships.ai includes an automatic stolen vehicle database check. Buy only from verified listings with a green Verified Clean badge.',
      },
      {
        q: 'Can a dealer sell vehicles on SkipTheDealerships.ai?',
        a:
          "Yes. Licensed dealers apply through our Dealer Partner program. They must provide their Motor Vehicle Dealers Act (MVDA) licence number or provincial equivalent. Dealer listings are clearly marked so buyers always know whether they're buying from a private seller or a licensed dealer.",
      },
    ],
  },
  {
    category: 'Financing & Insurance',
    items: [
      {
        q: 'Can I get financing for a privately purchased vehicle?',
        a:
          'Yes. SkipTheDealerships.ai works with 28+ Canadian lenders who finance private vehicle purchases. Get pre-qualified in 60 seconds with no impact to your credit score. Rates start at 5.99% OAC.',
      },
      {
        q: 'Can I get insurance coverage on a vehicle I buy privately?',
        a:
          'Yes. You can obtain standard auto insurance, GAP coverage, and extended warranty protection on privately purchased vehicles through our insurance partners. Coverage options appear on every listing page.',
      },
      {
        q: 'Does SkipTheDealerships.ai offer extended warranties?',
        a:
          'Yes. Extended warranty plans are available for eligible vehicles purchased through the platform. Coverage can be added at the time of purchase and financed into your loan if applicable.',
      },
    ],
  },
];

function FaqSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section
      style={{ padding: '80px 32px', background: c.white, borderTop: `1px solid ${c.border}` }}
    >
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <AnimatedSection>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
            <Pill teal>FAQ</Pill>
            <h2
              style={{
                fontSize: 'clamp(26px, 3.5vw, 42px)',
                fontWeight: 700,
                color: c.navy,
                margin: '16px 0 10px',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Common Questions
            </h2>
            <p style={{ color: c.textMid, fontSize: 17 }}>
              Everything you need to know about buying and selling vehicles privately in Canada.
            </p>
          </motion.div>

          {FAQ_GROUPS.map((group, gi) => (
            <motion.div key={gi} variants={fadeUp} style={{ marginBottom: 48 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: c.teal,
                  marginBottom: 16,
                }}
              >
                {group.category}
              </div>
              <div
                style={{ border: `1px solid ${c.border}`, borderRadius: 14, overflow: 'hidden' }}
              >
                {group.items.map((item, ii) => {
                  const key = `${gi}-${ii}`;
                  const open = openIdx === key;
                  return (
                    <div
                      key={ii}
                      style={{ borderTop: ii === 0 ? 'none' : `1px solid ${c.border}` }}
                    >
                      <button
                        onClick={() => setOpenIdx(open ? null : key)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '20px 24px',
                          background: open ? '#F0FDF9' : c.white,
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                          gap: 16,
                          transition: 'background 0.2s',
                        }}
                      >
                        <span
                          style={{ fontWeight: 600, fontSize: 15, color: c.navy, lineHeight: 1.4 }}
                        >
                          {item.q}
                        </span>
                        <span
                          style={{ fontSize: 20, color: c.teal, flexShrink: 0, fontWeight: 300 }}
                        >
                          {open ? '−' : '+'}
                        </span>
                      </button>
                      {open && (
                        <div
                          style={{
                            padding: '0 24px 20px',
                            color: c.textMid,
                            fontSize: 15,
                            lineHeight: 1.7,
                            background: '#F0FDF9',
                          }}
                        >
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}

          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginTop: 16 }}>
            <p style={{ color: c.textMid, fontSize: 14 }}>
              Still have questions?{' '}
              <a
                href="mailto:support@skipthedealerships.ai"
                style={{ color: c.teal, fontWeight: 600, textDecoration: 'none' }}
              >
                Contact our team →
              </a>
            </p>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

export default function SkipLandingPage() {
  const heroRef = useHeroSpotlight();

  // Flying leaf across the screen — triggered every 60s when idle
  React.useEffect(() => {
    let lastActivity = Date.now();
    const IDLE_THRESHOLD = 4000;
    const onActivity = () => {
      lastActivity = Date.now();
    };
    ['mousemove', 'scroll', 'touchstart', 'keydown', 'click'].forEach(e =>
      document.addEventListener(e, onActivity, { passive: true })
    );

    // Create the flying leaf element once
    const flyLeaf = document.createElement('div');
    flyLeaf.textContent = '🍁';
    flyLeaf.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'font-size:32px',
      'pointer-events:none',
      'z-index:99999',
      'will-change:transform',
      'display:none',
      'filter:drop-shadow(0 2px 6px rgba(213,43,30,0.4))',
    ].join(';');
    document.body.appendChild(flyLeaf);

    const fireFly = () => {
      if (Date.now() - lastActivity < IDLE_THRESHOLD) return;
      if (flyLeaf.style.display !== 'none') return; // already flying

      const goRight = Math.random() > 0.5;
      const startX = goRight ? -60 : window.innerWidth + 20;
      const endX = goRight ? window.innerWidth + 60 : -80;
      const startY = 80 + Math.random() * (window.innerHeight * 0.6);
      const duration = 5000 + Math.random() * 3000; // 5–8 sec
      const size = 24 + Math.random() * 24; // 24–48 px
      const spinDir = goRight ? 1 : -1;

      flyLeaf.style.fontSize = size + 'px';
      flyLeaf.style.display = 'block';
      flyLeaf.style.transform = `translate(${startX}px, ${startY}px)`;

      const startTime = performance.now();

      function frame(now) {
        const elapsed = now - startTime;
        const p = Math.min(elapsed / duration, 1);

        // X: linear travel across screen
        const x = startX + (endX - startX) * p;

        // Y: gentle sine wave drift — like tumbling in wind gusts
        const y =
          startY +
          Math.sin(p * Math.PI * 3) * 60 + // slow wave
          Math.sin(p * Math.PI * 9) * 18; // faster flutter

        // Rotation: continuous spin + wobble
        const rot = spinDir * p * 720 + Math.sin(p * Math.PI * 5) * 25;

        flyLeaf.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;

        if (p < 1) {
          requestAnimationFrame(frame);
        } else {
          flyLeaf.style.display = 'none';
        }
      }

      requestAnimationFrame(frame);
    };

    // Also trigger 3D nav-leaf spin every 45s when idle
    const fireSpin = () => {
      if (Date.now() - lastActivity < IDLE_THRESHOLD) return;
      document.body.classList.add('leaf-spinning');
      setTimeout(() => document.body.classList.remove('leaf-spinning'), 3200);
    };

    const idFly = setInterval(fireFly, 60000);
    const idSpin = setInterval(fireSpin, 45000);

    return () => {
      clearInterval(idFly);
      clearInterval(idSpin);
      flyLeaf.remove();
      ['mousemove', 'scroll', 'touchstart', 'keydown', 'click'].forEach(e =>
        document.removeEventListener(e, onActivity)
      );
    };
  }, []);

  return (
    <div
      style={{
        fontFamily: 'DM Sans, sans-serif',
        color: c.text,
        background: c.white,
        overflowX: 'hidden',
      }}
    >
      {/* HERO — full-bleed photo, floating cards */}
      <section
        ref={heroRef}
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Cursor spotlight — teal glow that follows mouse, sits above photo but below all content */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle 380px at var(--sx,30%) var(--sy,50%), rgba(26,158,133,0.13) 0%, transparent 70%)',
          }}
        />

        {/* Background photo */}
        <img
          src={require('../../assets/hero-smarter.jpg')}
          alt="Sell or Buy Smarter — Canadian Marketplace"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'right center',
            zIndex: 0,
          }}
          onError={e => {
            e.target.style.display = 'none';
          }}
        />

        {/* Gradient overlay — strong left dark, fades right to reveal lake+CN Tower */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to right, rgba(8,20,36,0.92) 0%, rgba(8,20,36,0.78) 30%, rgba(8,20,36,0.38) 58%, rgba(8,20,36,0.08) 78%)',
            zIndex: 1,
          }}
        />
        {/* Top dark band for nav readability */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(8,20,36,0.55) 0%, rgba(8,20,36,0.0) 20%)',
            zIndex: 1,
          }}
        />
        {/* Bottom dark gradient for cards */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(8,20,36,0.80) 0%, rgba(8,20,36,0.0) 40%)',
            zIndex: 1,
          }}
        />
        {/* Fallback background when photo missing */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #0e1f32 0%, #1a2d42 50%, #0d1b28 100%)',
            zIndex: -1,
          }}
        />

        {/* ── STICKY SECONDARY NAV (scroll-triggered) ── */}
        <StickyNav />

        {/* ── TOP NAV BAR ── */}
        <HeroNav />

        {/* ── MAIN CONTENT ── */}
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '40px 5% 20px',
          }}
        >
          <AnimatedSection>
            {/* Trust badge row */}
            <motion.div
              variants={fadeUp}
              style={{
                display: 'flex',
                gap: 8,
                marginBottom: 24,
                flexWrap: 'wrap',
                maxWidth: '100%',
              }}
            >
              {[
                { label: 'Verified Data', sub: 'Accurate & reliable vehicle insights.' },
                { label: 'Pre-Approved Buyers', sub: 'Serious buyers. Lower risk.' },
                { label: 'Secure & Private', sub: 'Your info stays protected.' },
              ].map((b, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 10,
                    padding: '7px 12px',
                    backdropFilter: 'blur(12px)',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: 'rgba(26,158,133,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ color: '#1A9E85', fontSize: 13, fontWeight: 700 }}>✓</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.95)' }}>
                      {b.label}
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                      {b.sub}
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              style={{
                fontSize: 'clamp(44px, 6vw, 80px)',
                fontWeight: 800,
                lineHeight: 1.06,
                letterSpacing: '-0.025em',
                color: '#fff',
                marginBottom: 20,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Canada's First
              <br />
              <span
                style={{
                  background:
                    'linear-gradient(90deg,#1A9E85 0%,#1A9E85 20%,#5DCAA5 42%,#A8E6DA 50%,#5DCAA5 58%,#1A9E85 80%,#1A9E85 100%)',
                  backgroundSize: '300% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'heroShimmer 3.5s linear infinite',
                }}
              >
                All-In-One AI-Powered
              </span>
              <br />
              <span
                style={{
                  background:
                    'linear-gradient(90deg,#5DCAA5 0%,#A8E6DA 18%,#ffffff 35%,#A8E6DA 50%,#ffffff 65%,#A8E6DA 82%,#5DCAA5 100%)',
                  backgroundSize: '250% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'heroShimmer 2.8s linear infinite',
                  filter: 'drop-shadow(0 0 18px rgba(168,230,218,0.5))',
                }}
              >
                Vehicle Marketplace.
              </span>
              <br />
              <span
                style={{
                  fontSize: 'clamp(22px, 2.8vw, 40px)',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.7)',
                  letterSpacing: '-0.2px',
                }}
              >
                Buy · Sell · Trade · Finance · Delivered.
              </span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeUp}
              style={{
                fontSize: 20,
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.75,
                marginBottom: 36,
                maxWidth: 480,
                fontWeight: 400,
              }}
            >
              Data-driven. Trust-first. Built for Canadians.
              <br />
              Every buyer verified. Every vehicle vetted.
              <br />
              Zero dealership markup.
            </motion.p>

            {/* CTA — situational qualifier */}
            <motion.div variants={fadeUp} style={{ marginBottom: 60 }}>
              <HeroCTA />
            </motion.div>

            {/* ── FLOATING BOTTOM CARDS ── */}
            <motion.div
              variants={fadeUp}
              className="heroCards"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr auto',
                gap: 14,
                alignItems: 'end',
              }}
            >
              {/* Card 1: Market Value */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  background: 'rgba(255,255,255,0.97)',
                  borderRadius: 18,
                  padding: '18px 20px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#1A9E85',
                    marginBottom: 4,
                  }}
                >
                  Market Value
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: '#0F2033',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  $28,450
                </div>
                {/* Sparkline */}
                <svg
                  viewBox="0 0 180 38"
                  width="180"
                  height="38"
                  fill="none"
                  style={{ margin: '8px 0 4px', display: 'block' }}
                >
                  <defs>
                    <linearGradient id="hsg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1A9E85" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="#1A9E85" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0 34 C30 30 60 24 90 17 C120 10 150 6 180 2"
                    stroke="#1A9E85"
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                  />
                  <path
                    d="M0 34 C30 30 60 24 90 17 C120 10 150 6 180 2 L180 38 L0 38Z"
                    fill="url(#hsg)"
                  />
                  <circle cx="180" cy="2" r="4" fill="#1A9E85" />
                </svg>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 9, color: '#9aabba' }}>Fair Market Range</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#4A5E6E' }}>
                      $26,900 – $29,900
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      color: '#1A9E85',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                    }}
                  >
                    <span style={{ fontSize: 10 }}>↑</span> Good Time to Sell
                  </div>
                </div>
              </motion.div>

              {/* Card 2: Vehicle Overview */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1.3 }}
                style={{
                  background: 'rgba(255,255,255,0.97)',
                  borderRadius: 18,
                  padding: '18px 20px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#1A9E85',
                    marginBottom: 8,
                  }}
                >
                  Vehicle Overview
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 62,
                      height: 40,
                      background: '#e8eef3',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 22,
                    }}
                  >
                    🚙
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0F2033' }}>
                      2022 SUV
                    </div>
                    <div style={{ fontSize: 10.5, color: '#7A8C99' }}>AWD · 2.5L · 38,500 km</div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    background: 'rgba(26,158,133,0.1)',
                    border: '1px solid rgba(26,158,133,0.3)',
                    borderRadius: 20,
                    padding: '4px 10px',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#1A9E85',
                    marginTop: 10,
                  }}
                >
                  <span>✓</span> Verified
                </div>
              </motion.div>

              {/* Card 3: Pre-Approved Buyers */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 2.6 }}
                style={{
                  background: 'rgba(255,255,255,0.97)',
                  borderRadius: 18,
                  padding: '18px 20px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#1A9E85',
                    marginBottom: 8,
                  }}
                >
                  Pre-Approved Buyers
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  {['#c0956e', '#7aaa7a', '#6a9cc8'].map((bg, i) => (
                    <div
                      key={i}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: bg,
                        border: '2px solid #fff',
                        marginLeft: i === 0 ? 0 : -8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                      }}
                    >
                      👤
                    </div>
                  ))}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: '#0F2033',
                      border: '2px solid #fff',
                      marginLeft: -8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    +24
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#7A8C99',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#1A9E85',
                      display: 'inline-block',
                      animation: 'heroPulse 2s ease-in-out infinite',
                    }}
                  />
                  Active buyers in your area
                </div>
              </motion.div>

              {/* Card 4: Coast to Coast map */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.7 }}
                style={{
                  background: 'rgba(255,255,255,0.97)',
                  borderRadius: 18,
                  padding: '14px 16px',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  minWidth: 160,
                }}
              >
                <div
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#1A9E85',
                    marginBottom: 6,
                  }}
                >
                  Coast to Coast
                </div>
                <svg viewBox="0 0 160 95" width="144" height="86" fill="none">
                  <path
                    d="M18 50 Q28 18 65 13 Q88 7 118 16 Q148 10 162 32 Q172 52 158 70 Q138 86 108 84 Q78 90 58 80 Q32 74 18 50Z"
                    fill="#e8f0ec"
                    stroke="#c8d8d0"
                    strokeWidth="1"
                  />
                  <line x1="56" y1="18" x2="56" y2="84" stroke="#c8d8d0" strokeWidth="0.5" />
                  <line x1="96" y1="13" x2="96" y2="84" stroke="#c8d8d0" strokeWidth="0.5" />
                  <line x1="128" y1="16" x2="128" y2="78" stroke="#c8d8d0" strokeWidth="0.5" />
                  <circle cx="142" cy="58" r="4.5" fill="#1A9E85" opacity="0.9" />
                  <circle
                    cx="142"
                    cy="58"
                    r="9"
                    stroke="#1A9E85"
                    strokeWidth="1"
                    opacity="0.3"
                    fill="none"
                  />
                  <circle cx="28" cy="50" r="3.5" fill="#1A9E85" opacity="0.8" />
                  <circle cx="90" cy="38" r="3" fill="#1A9E85" opacity="0.7" />
                  <circle cx="132" cy="46" r="2.5" fill="#1A9E85" opacity="0.6" />
                  <circle cx="76" cy="34" r="2.5" fill="#1A9E85" opacity="0.6" />
                  <circle cx="148" cy="46" r="3" fill="#1A9E85" opacity="0.7" />
                  <line
                    x1="28"
                    y1="50"
                    x2="90"
                    y2="38"
                    stroke="#1A9E85"
                    strokeWidth="0.6"
                    opacity="0.35"
                  />
                  <line
                    x1="90"
                    y1="38"
                    x2="142"
                    y2="58"
                    stroke="#1A9E85"
                    strokeWidth="0.6"
                    opacity="0.35"
                  />
                  <line
                    x1="76"
                    y1="34"
                    x2="90"
                    y2="38"
                    stroke="#1A9E85"
                    strokeWidth="0.6"
                    opacity="0.35"
                  />
                </svg>
                <div style={{ fontSize: 9.5, color: '#0F2033', fontWeight: 600, marginTop: 4 }}>
                  Trusted network · all provinces
                </div>
              </motion.div>
            </motion.div>
          </AnimatedSection>
        </div>

        {/* ── BOTTOM TRUST BAR ── */}
        <div
          className={css.trustBar}
          style={{
            position: 'relative',
            zIndex: 10,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            padding: '16px 5%',
            display: 'flex',
            alignItems: 'center',
            gap: 0,
          }}
        >
          {[
            { icon: '🍁', label: 'Built for Canadians' },
            { icon: '🔒', label: 'Privacy First' },
            { icon: '🇨🇦', label: 'Canada-wide support' },
            { icon: '✓', label: 'Verified marketplace' },
          ].map((item, i) => (
            <React.Fragment key={i}>
              <div
                className={css.trustItem}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flex: 1,
                  justifyContent: 'center',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.6)',
                }}
              >
                {item.icon && (
                  <span className={item.icon === '🍁' ? 'maple' : undefined}>{item.icon}</span>
                )}
                {item.label}
              </div>
              {i < 3 && (
                <div
                  className={css.trustDivider}
                  style={{
                    width: 1,
                    height: 22,
                    background: 'rgba(255,255,255,0.1)',
                    flexShrink: 0,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <style>{`
          @keyframes heroPulse  { 0%,100%{opacity:0.5} 50%{opacity:1} }
          @keyframes pulse      { 0%,100%{opacity:0.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
          @keyframes heroShimmer{ 0%{background-position:100% center} 100%{background-position:-100% center} }
          @keyframes goldShimmer{ 0%{background-position:130% center} 100%{background-position:-130% center} }
          @keyframes shimmer    { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
          @keyframes drawOval   { to{stroke-dashoffset:0} }

          @keyframes flagCycle {
            0%   { color: #D52B1E; }
            25%  { color: #FF0000; }
            50%  { color: #FF6B6B; }
            75%  { color: #003087; }
            100% { color: #D52B1E; }
          }

          /* Gentle breathe — scale up slightly + soft glow */
          @keyframes leafBreathe {
            0%, 100% {
              transform: scale(1);
              filter: drop-shadow(0 1px 3px rgba(213,43,30,0.35));
            }
            50% {
              transform: scale(1.35);
              filter: drop-shadow(0 4px 14px rgba(213,43,30,0.75)) drop-shadow(0 0 6px rgba(213,43,30,0.5));
            }
          }

          /* 3D pop spin — Y-axis flip with translateZ burst */
          @keyframes leafSpin3D {
            0%   { transform: perspective(500px) rotateY(0deg)   scale(1);   filter: drop-shadow(0 1px 3px rgba(213,43,30,0.35)); }
            20%  { transform: perspective(500px) rotateY(90deg)  scale(2.2) translateZ(80px); filter: drop-shadow(0 8px 28px rgba(213,43,30,0.9)) drop-shadow(0 0 16px rgba(213,43,30,0.7)); }
            40%  { transform: perspective(500px) rotateY(180deg) scale(1);   filter: drop-shadow(0 1px 3px rgba(213,43,30,0.35)); }
            60%  { transform: perspective(500px) rotateY(270deg) scale(2.2) translateZ(80px); filter: drop-shadow(0 8px 28px rgba(213,43,30,0.9)) drop-shadow(0 0 16px rgba(213,43,30,0.7)); }
            80%  { transform: perspective(500px) rotateY(360deg) scale(1.1); filter: drop-shadow(0 2px 6px rgba(213,43,30,0.5)); }
            100% { transform: perspective(500px) rotateY(360deg) scale(1);   filter: drop-shadow(0 1px 3px rgba(213,43,30,0.35)); }
          }

          @media(max-width:768px){
            :root{--nav-link-display:none;--nav-label-display:none;}
            .desktopNav{display:none!important;}
            .heroCards{display:none!important;}
            .stickyNavTabs{display:none!important;}
            .stickyNavCta{display:none!important;}
            .stickyNavBrand{display:none!important;}
            .stickyNavMobile{display:flex!important;}
          }

          /* Base state — color cycle + breathe */
          .maple {
            display: inline-block;
            color: #D52B1E;
            transform-origin: center center;
            animation:
              flagCycle 480s steps(4, end) infinite,
              leafBreathe 9s ease-in-out infinite;
          }

          /* 3D spin burst — overrides breathe transform for 3.2s */
          body.leaf-spinning .maple {
            animation:
              flagCycle 480s steps(4, end) infinite,
              leafSpin3D 3.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }
        `}</style>
      </section>

      {/* STATS */}
      <section
        style={{
          background: c.bg,
          padding: '60px 32px',
          borderTop: `1px solid ${c.border}`,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <AnimatedSection
            className={css.phoneTwoColumns}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}
          >
            {stats.map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                whileHover={{
                  y: -6,
                  boxShadow: '0 12px 32px rgba(26,158,133,0.13)',
                  borderColor: 'rgba(26,158,133,0.3)',
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                style={{
                  textAlign: 'center',
                  padding: '28px 16px',
                  borderRadius: 14,
                  background: c.white,
                  border: `1px solid ${c.border}`,
                  boxShadow: '0 2px 8px rgba(15,32,51,0.04)',
                  cursor: 'default',
                }}
              >
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: c.navy,
                    fontFamily: 'DM Sans, sans-serif',
                    lineHeight: 1.3,
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: c.textMid,
                    marginTop: 8,
                    fontWeight: 500,
                  }}
                >
                  {s.label}
                </div>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* EXPLORE VEHICLES — 4-card action grid */}
      <ExploreVehicles />

      {/* BROWSE BY CATEGORY + BODY TYPE */}
      <section
        style={{
          padding: '80px 32px 64px',
          background: '#F4F7FA',
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <AnimatedSection>
            {/* Category pills */}
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2
                style={{
                  fontSize: 'clamp(22px, 3vw, 34px)',
                  fontWeight: 800,
                  color: c.navy,
                  margin: '0 0 28px',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                Browse by category
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
                {[
                  'Fuel Efficient Cars',
                  'Small and Sporty',
                  'AWD Cars',
                  '4x4 Trucks',
                  'Affordable Automatic',
                  'Reliable',
                  'Electric',
                  'Small Trucks',
                  'Family SUVs',
                  'Low Mileage',
                  'Under $15,000',
                  'Under $25,000',
                ].map((cat, i) => (
                  <button
                    key={i}
                    style={{
                      padding: '9px 20px',
                      borderRadius: 50,
                      border: `1.5px solid ${c.border}`,
                      background: '#fff',
                      color: c.navy,
                      fontWeight: 600,
                      fontSize: 13.5,
                      cursor: 'pointer',
                      fontFamily: 'DM Sans, sans-serif',
                      transition: 'all 0.15s',
                      boxShadow: '0 1px 4px rgba(15,32,51,0.06)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = c.teal;
                      e.currentTarget.style.color = c.teal;
                      e.currentTarget.style.background = c.tealLight;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = c.border;
                      e.currentTarget.style.color = c.navy;
                      e.currentTarget.style.background = '#fff';
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Body type cards */}
            <motion.div variants={fadeUp}>
              <h2
                style={{
                  fontSize: 'clamp(22px, 3vw, 34px)',
                  fontWeight: 800,
                  color: c.navy,
                  margin: '0 0 28px',
                  textAlign: 'center',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                Explore vehicles &amp; equipment
              </h2>
              <div className={css.categoryGrid}>
                {VEHICLE_CATEGORIES.map(type => {
                  const Icon = type.Icon;
                  return (
                    <a
                      key={type.id}
                      href={type.href}
                      className={css.categoryTile}
                      style={{
                        background: type.bg,
                        '--tile-accent': type.accent,
                        '--tile-border': c.border,
                      }}
                    >
                      <Icon a={type.accent} />
                      <span className={css.categoryLabel}>{type.label}</span>
                      <span className={css.categoryCta} style={{ color: type.accent }}>
                        Browse &rarr;
                      </span>
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* NEAR YOU */}
      <section
        id="inventory"
        style={{
          padding: '100px 32px',
          background: c.white,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <AnimatedSection>
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
              <Pill>Vehicles Near You</Pill>
              <h2
                style={{
                  fontSize: 'clamp(26px, 3.5vw, 42px)',
                  fontWeight: 700,
                  margin: '16px 0 12px',
                  fontFamily: 'DM Sans, sans-serif',
                  color: c.navy,
                }}
              >
                Private sellers in your area.
              </h2>
              <p style={{ color: c.textMid, fontSize: 16, maxWidth: 500, margin: '0 auto' }}>
                Every listing is AI-vetted and seller-verified. No dealership markup. No guesswork.
              </p>
            </motion.div>
            {/* REAL VALUE — Interactive market value section */}
            {(() => {
              const [gaugeVal, setGaugeVal] = React.useState(28450);
              const low = 20161,
                high = 32425,
                rangeMin = 26900,
                rangeMax = 29900;
              const pct = Math.round(((gaugeVal - low) / (high - low)) * 100);
              // 3 zones: below range = underpriced, in range = sweet spot, above range = overpriced
              const sellZone = gaugeVal < rangeMin ? 'low' : gaugeVal > rangeMax ? 'high' : 'sweet';
              const sellLabel =
                sellZone === 'low'
                  ? 'Underpriced — Leave Money on the Table'
                  : sellZone === 'high'
                  ? 'Overpriced — Buyers Will Walk Away'
                  : 'Sweet Spot — Best Time to Sell';
              const sellIcon = sellZone === 'low' ? '↓' : sellZone === 'high' ? '↑' : '✓';
              const sellColor =
                sellZone === 'low' ? '#f59e0b' : sellZone === 'high' ? '#ef4444' : '#1A9E85';
              const sparkPts = [
                0,
                12,
                8,
                18,
                14,
                22,
                19,
                28,
                24,
                32,
                30,
                38,
                36,
                44,
                42,
                50,
                48,
                56,
                54,
                62,
                60,
                68,
                66,
                74,
                70,
                80,
                78,
                86,
                84,
                92,
                90,
                100,
              ];
              const sparkPath = sparkPts
                .map(
                  (y, i) =>
                    `${i === 0 ? 'M' : 'L'}${(i / (sparkPts.length - 1)) * 280},${80 - y * 0.72}`
                )
                .join(' ');
              return (
                <motion.div
                  variants={fadeUp}
                  style={{
                    position: 'relative',
                    borderRadius: 24,
                    overflow: 'hidden',
                    marginBottom: 48,
                    minHeight: 520,
                  }}
                >
                  {/* Background photo */}
                  <img
                    src={require('../../assets/seller-value.jpg')}
                    alt="Real Value — Canadian Marketplace"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'right center',
                    }}
                  />
                  {/* Gradient overlay */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'linear-gradient(to right, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.92) 40%, rgba(255,255,255,0.55) 65%, rgba(255,255,255,0.0) 85%)',
                    }}
                  />

                  {/* Content */}
                  <div
                    className={css.phoneOneColumn}
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      minHeight: 520,
                    }}
                  >
                    {/* LEFT panel */}
                    <div
                      style={{
                        padding: '44px 40px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* Logo */}
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}
                      >
                        <span className="maple" style={{ fontSize: 16 }}>
                          🍁
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: '0.12em',
                            color: '#0F2033',
                            textTransform: 'uppercase',
                          }}
                        >
                          SkipTheDealerships.ai
                        </span>
                      </div>
                      {/* Headline */}
                      <div style={{ flex: 1 }}>
                        <h3
                          style={{
                            fontSize: 'clamp(28px, 3vw, 44px)',
                            fontWeight: 900,
                            color: '#0F2033',
                            fontFamily: 'DM Sans, sans-serif',
                            margin: '0 0 8px',
                            lineHeight: 1.1,
                          }}
                        >
                          Real Value.
                          <br />
                          <span style={{ color: c.teal }}>Verified for Canada.</span>
                        </h3>
                        <p
                          style={{
                            fontSize: 14,
                            color: '#556677',
                            margin: '0 0 24px',
                            lineHeight: 1.6,
                          }}
                        >
                          Know your vehicle's true market value with confidence.
                        </p>

                        {/* Interactive Market Value Card */}
                        <div
                          style={{
                            background: '#fff',
                            borderRadius: 16,
                            padding: '20px 22px',
                            boxShadow: '0 4px 24px rgba(15,32,51,0.12)',
                            marginBottom: 14,
                            border: '1px solid #E8EDF2',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4,
                            }}
                          >
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#0F2033' }}>
                              Canadian Market Value
                            </span>
                            <span
                              style={{
                                fontSize: 11,
                                background: '#E8F7F4',
                                color: c.teal,
                                borderRadius: 20,
                                padding: '2px 8px',
                                fontWeight: 700,
                              }}
                            >
                              ✓ Verified Data
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 26,
                              fontWeight: 900,
                              color: '#0F2033',
                              marginBottom: 16,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            ${gaugeVal.toLocaleString()}
                          </div>
                          {/* Gauge slider */}
                          <div style={{ position: 'relative', marginBottom: 8 }}>
                            {/* Tooltip */}
                            <div
                              style={{
                                position: 'absolute',
                                bottom: 28,
                                left: `calc(${pct}% - 52px)`,
                                background: '#0F2033',
                                color: '#fff',
                                borderRadius: 8,
                                padding: '4px 10px',
                                fontSize: 11,
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              }}
                            >
                              ${gaugeVal.toLocaleString()}{' '}
                              <span style={{ color: '#aac' }}>Est. Value</span>
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: -5,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '5px solid transparent',
                                  borderRight: '5px solid transparent',
                                  borderTop: '5px solid #0F2033',
                                }}
                              />
                            </div>
                            {/* Track */}
                            <div
                              style={{
                                height: 10,
                                borderRadius: 10,
                                background:
                                  'linear-gradient(to right, #f5c842 0%, #8dc63f 40%, #1A9E85 100%)',
                                position: 'relative',
                              }}
                            >
                              <input
                                type="range"
                                min={low}
                                max={high}
                                value={gaugeVal}
                                onChange={e => setGaugeVal(Number(e.target.value))}
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  width: '100%',
                                  height: '100%',
                                  opacity: 0,
                                  cursor: 'pointer',
                                  margin: 0,
                                }}
                              />
                              {/* Thumb */}
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: `${pct}%`,
                                  transform: 'translate(-50%, -50%)',
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  background: '#fff',
                                  border: '2px solid #0F2033',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                  pointerEvents: 'none',
                                }}
                              />
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 11,
                                color: '#889',
                                marginTop: 6,
                              }}
                            >
                              <span>
                                ${low.toLocaleString()}
                                <br />
                                <span style={{ color: '#aab', fontSize: 10 }}>Low Market</span>
                              </span>
                              <span style={{ textAlign: 'right' }}>
                                ${high.toLocaleString()}
                                <br />
                                <span style={{ color: '#aab', fontSize: 10 }}>High Market</span>
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontSize: 11,
                              color: sellColor,
                              fontWeight: 600,
                            }}
                          >
                            <span>{sellIcon}</span>
                            <span>{sellLabel}</span>
                            <span style={{ color: '#bbb', fontWeight: 400, marginLeft: 4 }}>
                              · Based on verified Canadian market data
                            </span>
                          </div>
                        </div>

                        {/* Fair Market Range sparkline card */}
                        <div
                          style={{
                            background: '#fff',
                            borderRadius: 14,
                            padding: '16px 20px',
                            boxShadow: '0 2px 12px rgba(15,32,51,0.08)',
                            border: '1px solid #E8EDF2',
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 12,
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#556677',
                                marginBottom: 4,
                              }}
                            >
                              Fair Market Range
                            </div>
                            <div
                              style={{
                                fontSize: 18,
                                fontWeight: 900,
                                color: '#0F2033',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              ${rangeMin.toLocaleString()} – ${rangeMax.toLocaleString()}
                            </div>
                            <div style={{ fontSize: 11, color: '#889', marginTop: 4 }}>
                              Based on 1,247 similar listings
                            </div>
                            <div
                              style={{ fontSize: 11, color: c.teal, fontWeight: 700, marginTop: 4 }}
                            >
                              ✓ +8.3% vs last 30 days
                            </div>
                          </div>
                          <svg viewBox="0 0 280 80" style={{ width: '100%', height: 60 }}>
                            <defs>
                              <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#1A9E85" stopOpacity="0.25" />
                                <stop offset="100%" stopColor="#1A9E85" stopOpacity="0.02" />
                              </linearGradient>
                            </defs>
                            <path d={`${sparkPath} L280,80 L0,80 Z`} fill="url(#spark-fill)" />
                            <path
                              d={sparkPath}
                              stroke="#1A9E85"
                              strokeWidth="2.5"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle
                              cx="280"
                              cy={80 - sparkPts[sparkPts.length - 1] * 0.72}
                              r="4"
                              fill="#1A9E85"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Trust badges */}
                      <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
                        {[
                          {
                            icon: '🛡️',
                            label: 'Verified Data',
                            sub: 'Accurate & reliable vehicle insights.',
                          },
                          {
                            icon: '👤',
                            label: 'Pre-Approved Buyers',
                            sub: 'Serious buyers. Lower risk.',
                          },
                          {
                            icon: '🔒',
                            label: 'Secure & Private',
                            sub: 'Your info stays protected.',
                          },
                        ].map((b, i) => (
                          <div
                            key={i}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}
                          >
                            <span style={{ fontSize: 18, flexShrink: 0 }}>{b.icon}</span>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#0F2033' }}>
                                {b.label}
                              </div>
                              <div style={{ fontSize: 11, color: '#889' }}>{b.sub}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* RIGHT — floating vehicle card */}
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        padding: 24,
                      }}
                    >
                      <div
                        style={{
                          background: '#fff',
                          borderRadius: 14,
                          padding: '14px 18px',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                          minWidth: 200,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: '#0F2033',
                            marginBottom: 2,
                          }}
                        >
                          2022 SUV EX AWD
                        </div>
                        <div style={{ fontSize: 12, color: '#889', marginBottom: 8 }}>
                          38,500 km · Single Owner
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 12,
                            color: c.teal,
                            fontWeight: 700,
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              background: c.teal,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <svg viewBox="0 0 12 9" width="9" height="7" fill="none">
                              <path
                                d="M1 4.5L4.5 8 11 1"
                                stroke="#fff"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          Verified Listing
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom trust bar */}
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      background: 'rgba(15,32,51,0.88)',
                      backdropFilter: 'blur(8px)',
                      padding: '12px 40px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span style={{ color: c.teal, fontSize: 14 }}>✓</span>
                    <span style={{ fontSize: 13, color: '#fff', fontWeight: 500 }}>
                      Built on verified data. Trusted by Canadians coast to coast.
                    </span>
                    <span className="maple" style={{ marginLeft: 'auto', fontSize: 18 }}>
                      🍁
                    </span>
                  </div>
                </motion.div>
              );
            })()}

            <LiveInventory />
          </AnimatedSection>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <HowItWorksSection />

      {/* PRICING */}
      <section className={css.pricingSection} style={{ padding: '100px 32px', background: c.bg }}>
        <div style={{ maxWidth: 1320, margin: '0 auto' }}>
          <AnimatedSection>
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
              <Pill>Pricing</Pill>
              <h2
                style={{
                  fontSize: 'clamp(28px, 4vw, 52px)',
                  fontWeight: 800,
                  margin: '16px 0 12px',
                  fontFamily: 'DM Sans, sans-serif',
                  color: c.navy,
                }}
              >
                Pay only for what you need.
              </h2>
              <p style={{ color: c.textMid, fontSize: 16, maxWidth: 480, margin: '0 auto' }}>
                Flexible AI tools for sellers. Buyers browse for free.
              </p>
            </motion.div>

            <motion.div variants={fadeUp}>
              <PricingPlans />

              <div style={{ textAlign: 'center', marginTop: 32 }}>
                <span style={{ fontSize: 13, color: c.textLight }}>
                  &#10003; Add the tools you need. Upgrade anytime. No lock-in.
                </span>
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* MAP SEARCH */}
      <MapSearchSection />

      {/* BUSES & COMMERCIAL PASSENGER — "Looking for a bus? Connect with Skip."
          Placed straight after the map: a buyer who has just browsed what is
          listed near them and not found a coach or a school bus is exactly who
          this is for. Most commercial passenger vehicles never reach a public
          listing, so the next step is to ask rather than to search again. */}
      <section style={{ padding: '72px 32px', background: c.white }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <AnimatedSection>
            <motion.div variants={fadeUp}>
              <FindABusCta />
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* OEM REBATE ALERTS */}
      <OemRebateSection />

      {/* FINANCING — PREMIUM 3D GLASSMORPHISM */}
      <FinanceSection />

      {/* WARRANTY */}
      <WarrantySection />

      {/* COMPARISON */}
      <section style={{ padding: '100px 32px', background: c.white }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <AnimatedSection>
            {/* Header */}
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
              <Pill>Why Skip The Dealership</Pill>
              <h2
                style={{
                  fontSize: 'clamp(28px, 4vw, 52px)',
                  fontWeight: 800,
                  margin: '16px 0 20px',
                  fontFamily: 'DM Sans, sans-serif',
                  color: c.navy,
                  lineHeight: 1.1,
                }}
              >
                The old model is{' '}
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  broken.
                  <svg
                    viewBox="0 0 160 10"
                    style={{ position: 'absolute', bottom: -6, left: 0, width: '100%', height: 10 }}
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 8 Q40 2 80 6 Q120 10 160 4"
                      stroke="#1A9E85"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h2>
              <p
                style={{
                  fontSize: 16,
                  color: c.textMid,
                  lineHeight: 1.8,
                  maxWidth: 680,
                  margin: '0 auto',
                }}
              >
                We are not a dealership. We are a Canadian SaaS technology platform. We own zero
                inventory. We triage every deal using <strong style={{ color: c.teal }}>AI</strong>{' '}
                so every buyer is verified, every vehicle is vetted, and every transaction is safe
                and secure for both sides.
              </p>
            </motion.div>

            {/* Trust badge row */}
            <motion.div
              variants={fadeUp}
              style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: 48,
              }}
            >
              {[
                { icon: '🚫', label: 'Zero inventory owned' },
                { icon: '🪪', label: 'Every buyer KYC verified' },
                { icon: '✅', label: 'Every vehicle vetted' },
                { icon: '🤖', label: 'AI-triaged transactions' },
                { icon: '🤝', label: 'Safe and secure for both sides' },
              ].map((b, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: c.white,
                    border: `1.5px solid ${c.border}`,
                    borderRadius: 40,
                    padding: '10px 20px',
                    boxShadow: '0 2px 8px rgba(15,32,51,0.06)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: c.navy,
                  }}
                >
                  <span style={{ fontSize: 18 }}>{b.icon}</span>
                  {b.label}
                </div>
              ))}
            </motion.div>

            {/* Comparison table */}
            <motion.div
              variants={fadeUp}
              style={{
                background: c.white,
                borderRadius: 20,
                border: `1px solid ${c.border}`,
                overflow: 'hidden',
                boxShadow: '0 6px 32px rgba(15,32,51,0.08)',
                marginBottom: 0,
              }}
            >
              {/* Table header */}
              <div
                className={css.phoneComparison}
                style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.2fr' }}
              >
                <div
                  style={{
                    padding: '16px 24px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: c.textLight,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    background: '#F0F2F5',
                  }}
                >
                  Category
                </div>
                <div
                  style={{
                    padding: '16px 24px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6B7A8D',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    background: '#E8EAED',
                    textAlign: 'center',
                  }}
                >
                  Old Way
                  <br />
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: 10,
                      textTransform: 'none',
                      letterSpacing: 0,
                    }}
                  >
                    (The Dealership Model)
                  </span>
                </div>
                <div
                  style={{
                    padding: '16px 24px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#fff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    background: c.teal,
                    textAlign: 'center',
                  }}
                >
                  SkipTheDealerships.ai
                  <br />
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: 10,
                      textTransform: 'none',
                      letterSpacing: 0,
                    }}
                  >
                    (The New Way)
                  </span>
                </div>
              </div>
              {/* Table rows */}
              {comparison.map((row, i) => (
                <div
                  key={i}
                  className={css.phoneComparison}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 1fr 1.2fr',
                    borderTop: `1px solid ${c.border}`,
                    background: i % 2 === 0 ? c.white : '#FAFBFC',
                  }}
                >
                  <div
                    style={{
                      padding: '16px 24px',
                      fontSize: 14,
                      fontWeight: 500,
                      color: c.navy,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{row.icon}</span>
                    {row.aspect}
                  </div>
                  <div
                    style={{
                      padding: '16px 24px',
                      fontSize: 14,
                      color: '#8A9BAC',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {row.old}
                  </div>
                  <div
                    style={{
                      padding: '16px 24px',
                      fontSize: 14,
                      color: c.teal,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: 'rgba(26,158,133,0.04)',
                    }}
                  >
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: c.teal,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg viewBox="0 0 12 9" width="10" height="8" fill="none">
                        <path
                          d="M1 4.5L4.5 8 11 1"
                          stroke="#fff"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    {row.neu}
                  </div>
                </div>
              ))}

              {/* Bottom footer bar */}
              <div
                style={{
                  background: c.navy,
                  padding: '18px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      background: c.teal,
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 900,
                      fontSize: 18,
                      color: '#fff',
                    }}
                  >
                    S
                  </div>
                  <div style={{ lineHeight: 1.2 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: '#fff',
                        letterSpacing: '0.02em',
                      }}
                    >
                      SKIP
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        color: 'rgba(255,255,255,0.5)',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      THE DEALERSHIPS.AI
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    width: 1,
                    height: 32,
                    background: 'rgba(255,255,255,0.15)',
                    flexShrink: 0,
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.75)',
                    lineHeight: 1.5,
                    flex: 1,
                  }}
                >
                  <strong style={{ color: c.teal }}>A Canadian technology platform.</strong> Zero
                  inventory. 100% trust. Built for a better way to buy and sell vehicles.
                </p>
                <span className="maple" style={{ fontSize: 22 }}>
                  🍁
                </span>
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* SECURITY & COMPLIANCE */}
      <section style={{ padding: '80px 32px', background: c.navy }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <AnimatedSection>
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
              <span
                style={{
                  display: 'inline-block',
                  background: 'rgba(26,158,133,0.15)',
                  border: '1px solid rgba(26,158,133,0.35)',
                  borderRadius: 20,
                  padding: '5px 16px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: c.teal,
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  marginBottom: 20,
                }}
              >
                Security & Compliance
              </span>
              <h2
                style={{
                  fontSize: 'clamp(24px, 3vw, 38px)',
                  fontWeight: 800,
                  color: '#fff',
                  margin: '0 0 14px',
                  fontFamily: 'DM Sans, sans-serif',
                }}
              >
                Built to SOC2 standards.
                <br />
                Your data is protected at every layer.
              </h2>
              <p
                style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: 15,
                  maxWidth: 500,
                  margin: '0 auto',
                  lineHeight: 1.7,
                }}
              >
                Enterprise-grade security controls protecting every buyer, seller, and transaction
                on our platform.
              </p>
            </motion.div>

            {/* Safe, Private, Effortless — security visual */}
            <motion.div
              variants={fadeUp}
              style={{
                borderRadius: 20,
                overflow: 'hidden',
                marginBottom: 40,
                boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
              }}
            >
              <img
                src={require('../../assets/img-safe-private.png')}
                alt="Safe. Private. Effortless."
                style={{ width: '100%', display: 'block' }}
              />
            </motion.div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 16,
                marginBottom: 40,
              }}
            >
              {[
                {
                  icon: '🔒',
                  title: 'End-to-End Encryption',
                  desc:
                    'TLS 1.3 in transit. All data encrypted at rest. Zero plaintext credentials stored.',
                },
                {
                  icon: '📋',
                  title: 'Audit Logging',
                  desc:
                    'Every API request logged with timestamp, IP, and user ID. PII automatically redacted from logs.',
                },
                {
                  icon: '🛡️',
                  title: 'CSP + Helmet',
                  desc:
                    'Content Security Policy in block mode. HSTS, X-Frame-Options, MIME-sniff prevention active.',
                },
                {
                  icon: '⚡',
                  title: 'Rate Limiting',
                  desc:
                    'AI endpoints: 10 req/min. General API: 30 req/min. Brute-force and bot protection enforced.',
                },
                {
                  icon: '💳',
                  title: 'PCI-DSS Level 1',
                  desc:
                    'All payments through Stripe — the highest level of PCI compliance. We never touch raw card data.',
                },
                {
                  icon: '🪪',
                  title: 'KYC Verified',
                  desc:
                    'Every seller identity-verified via Stripe Identity. Government ID + liveness check required to list.',
                },
                {
                  icon: '🔐',
                  title: 'Stripe Connect Escrow',
                  desc:
                    'Funds held in escrow until both parties confirm. No direct bank transfers. Full dispute protection.',
                },
                {
                  icon: '🔓',
                  title: 'SOC2 Controls Active',
                  desc:
                    'Security, availability, confidentiality, and privacy controls implemented. Audit-ready infrastructure.',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 16,
                    padding: '22px 20px',
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 12 }}>{item.icon}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                    {item.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.65 }}>
                    {item.desc}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              variants={fadeUp}
              style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}
            >
              {[
                'SSL Secured',
                'PCI-DSS Level 1',
                'PIPEDA Compliant',
                'SOC2 Controls',
                'HSTS Enforced',
                'Zero Data Brokers',
              ].map((badge, i) => (
                <span
                  key={i}
                  style={{
                    background: 'rgba(26,158,133,0.12)',
                    border: '1px solid rgba(26,158,133,0.3)',
                    borderRadius: 20,
                    padding: '7px 16px',
                    fontSize: 12,
                    fontWeight: 700,
                    color: c.teal,
                    letterSpacing: '0.4px',
                  }}
                >
                  ✓ {badge}
                </span>
              ))}
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* DATA TRUST SECTION */}
      <section
        style={{
          padding: '100px 32px',
          background: c.white,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Maple leaf watermark */}
        <svg
          viewBox="0 0 500 500"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '110%',
            height: '110%',
            opacity: 0.055,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <path
            d="M250 30 L267 95 L320 60 L303 115 L365 108 L330 152 L390 172 L340 190 L370 240 L310 225 L320 290 L270 260 L275 340 L250 320 L225 340 L230 260 L180 290 L190 225 L130 240 L160 190 L110 172 L170 152 L135 108 L197 115 L180 60 L233 95 Z M235 340 L250 490 L265 340 Z"
            fill="#D52B1E"
          />
        </svg>
        <div style={{ maxWidth: 1160, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <AnimatedSection>
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 60 }}>
              <Pill teal>Data You Can Trust</Pill>
              <h2
                style={{
                  fontSize: 'clamp(26px, 3.5vw, 42px)',
                  fontWeight: 700,
                  margin: '16px 0 14px',
                  fontFamily: 'DM Sans, sans-serif',
                  color: c.navy,
                }}
              >
                Official Canadian government data.
                <br />
                Every province. Every territory.
              </h2>
              <p
                style={{
                  fontSize: 16,
                  color: c.textMid,
                  lineHeight: 1.8,
                  maxWidth: 580,
                  margin: '0 auto',
                }}
              >
                Our vehicle reports are sourced directly from provincial departments of
                transportation and official government agencies — covering 12 provinces and
                territories across Canada.
              </p>
            </motion.div>
            {/* KYC Verified — real people visual */}
            <motion.div
              variants={fadeUp}
              style={{
                borderRadius: 20,
                overflow: 'hidden',
                marginBottom: 40,
                boxShadow: '0 8px 40px rgba(15,32,51,0.10)',
              }}
            >
              <img
                src={require('../../assets/img-kyc-verified.png')}
                alt="Real people. Verified. Protected."
                style={{ width: '100%', display: 'block' }}
              />
            </motion.div>

            <div
              className={css.phoneOneColumn}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}
            >
              {[
                {
                  icon: '🏛️',
                  title: 'Official Government Data',
                  body:
                    'Reports sourced directly from provincial departments of transportation and official agencies across 12 provinces and territories (excluding NT).',
                },
                {
                  icon: '💲',
                  title: 'Transparent Pricing',
                  body:
                    'Simple, honest pricing with no hidden fees. For once, feel the difference of not getting extorted on price — straightforward agreements, no surprises.',
                },
                {
                  icon: '🔍',
                  title: 'Independent Coverage',
                  body:
                    "Catch critical issues that other sources miss with our independent data coverage — built to surface what dealerships don't want you to see.",
                },
                {
                  icon: '📱',
                  title: '24/7 Online and Mobile',
                  body:
                    'Get instant access to full vehicle reports through our web and mobile interfaces — anytime, anywhere across Canada.',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  style={{
                    background: c.bg,
                    borderRadius: 16,
                    padding: 28,
                    border: `1px solid ${c.border}`,
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 16 }}>{item.icon}</div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: c.navy }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: 13, color: c.textMid, lineHeight: 1.7, margin: 0 }}>
                    {item.body}
                  </p>
                </motion.div>
              ))}
            </div>
            <motion.div
              variants={fadeUp}
              style={{
                marginTop: 40,
                background: c.tealLight,
                border: `1px solid ${c.tealBorder}`,
                borderRadius: 16,
                padding: '24px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 20,
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.teal, marginBottom: 4 }}>
                  Coverage across Canada
                </div>
                <div style={{ fontSize: 13, color: c.textMid }}>
                  Alberta · British Columbia · Manitoba · New Brunswick · Newfoundland · Nova Scotia
                  · Ontario · PEI · Quebec · Saskatchewan · Yukon · Nunavut
                </div>
              </div>
              <span
                style={{
                  background: c.teal,
                  color: '#fff',
                  borderRadius: 20,
                  padding: '6px 18px',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                12 Provinces & Territories
              </span>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* DEALER SECTION */}
      <section
        style={{
          padding: '100px 32px',
          background: c.bgAlt,
          borderTop: `1px solid ${c.border}`,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <div className={css.dealerGrid} style={{ maxWidth: 1160, margin: '0 auto' }}>
          <AnimatedSection>
            <motion.div variants={fadeUp}>
              <Pill teal>Dealer Partner Program</Pill>
            </motion.div>
            <motion.h2
              variants={fadeUp}
              style={{
                fontSize: 'clamp(26px, 3.5vw, 42px)',
                fontWeight: 700,
                margin: '18px 0 18px',
                fontFamily: 'DM Sans, sans-serif',
                color: c.navy,
              }}
            >
              Built for dealers who want to move faster.
            </motion.h2>
            <motion.p
              variants={fadeUp}
              style={{ color: c.textMid, fontSize: 16, lineHeight: 1.75, marginBottom: 32 }}
            >
              Get a dedicated dashboard, bulk inventory tools, financing integration, and priority
              placement — all under your verified dealer badge.
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href="/dealers"
                className={css.dealerCta}
                style={{
                  background: c.teal,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  padding: '14px 28px',
                  borderRadius: 10,
                  textDecoration: 'none',
                }}
              >
                Become a Dealer Partner →
              </a>
            </motion.div>
          </AnimatedSection>
          <AnimatedSection>
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}
            >
              {[
                'Dealer dashboard',
                'Bulk inventory',
                'Lead routing',
                'Financing partners',
                'Verified badge',
                'Priority placement',
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  style={{
                    background: c.white,
                    border: `1px solid ${c.border}`,
                    borderRadius: 12,
                    padding: '16px 18px',
                    boxShadow: '0 2px 8px rgba(15,32,51,0.04)',
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: c.teal,
                      marginBottom: 8,
                    }}
                  />
                  <div style={{ fontSize: 14, color: c.navy, fontWeight: 500 }}>{item}</div>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* SHOP NEW BY BRAND — under the Dealer Partner Program: brand-only slots linking to
          partner dealers' new inventory. */}
      <section style={{ padding: '56px 32px', background: c.white }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <ShopNewByBrand />
        </div>
      </section>

      {/* AI INFRASTRUCTURE */}
      <section
        style={{
          padding: '100px 32px',
          background: c.white,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Giant maple leaf watermark — AI + Canadian identity */}
        <svg
          viewBox="0 0 500 500"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '110%',
            height: '110%',
            opacity: 0.055,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <path
            d="M250 30
               L267 95 L320 60 L303 115 L365 108 L330 152 L390 172
               L340 190 L370 240 L310 225 L320 290 L270 260 L275 340
               L250 320 L225 340 L230 260 L180 290 L190 225 L130 240
               L160 190 L110 172 L170 152 L135 108 L197 115 L180 60
               L233 95 Z
               M235 340 L250 490 L265 340 Z"
            fill="#D52B1E"
          />
        </svg>
        <div style={{ maxWidth: 1160, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <AnimatedSection>
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 60 }}>
              <Pill>Canadian AI — Built Here, For Canadians</Pill>
              <h2
                style={{
                  fontSize: 'clamp(26px, 3.5vw, 42px)',
                  fontWeight: 700,
                  margin: '16px 0 12px',
                  fontFamily: 'DM Sans, sans-serif',
                  color: c.navy,
                }}
              >
                Canadian AI. Canadian data. Canadian jobs.
              </h2>
              <p
                style={{
                  color: c.textMid,
                  fontSize: 16,
                  maxWidth: 600,
                  margin: '0 auto',
                  lineHeight: 1.75,
                }}
              >
                Every listing on SkipTheDealerships.ai is powered by a Canadian-built AI stack —
                Canadian Vehicle Market Value for structured vehicle valuation, AI Market
                Intelligence for 90-day Canadian market intelligence, and AI for professional
                listing language. Built in Canada. Trained on Canadian data. Creating Canadian jobs.
              </p>
            </motion.div>

            {/* AI Marketplace Match — Canada map visual */}
            <motion.div
              variants={fadeUp}
              style={{
                borderRadius: 20,
                overflow: 'hidden',
                marginBottom: 40,
                boxShadow: '0 8px 40px rgba(15,32,51,0.10)',
              }}
            >
              <img
                src={require('../../assets/img-ai-match.png')}
                alt="AI Marketplace Match."
                style={{ width: '100%', display: 'block' }}
              />
            </motion.div>

            <div
              className={css.phoneOneColumn}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}
            >
              {[
                {
                  title: 'Canadian Vehicle Market Value',
                  body:
                    'Real market value, vehicle history, and spec decode — automatically applied to every listing.',
                },
                {
                  title: 'AI descriptions',
                  body:
                    'Professional listing descriptions generated by AI. Consistent tone across every vehicle.',
                },
                {
                  title: 'Identity layer (KYC)',
                  body:
                    'Every seller is identity-verified before going live. Buyers know who they are dealing with.',
                },
                {
                  title: 'Pre-qualified buyers',
                  body:
                    'AI Buyer Matching-powered buyer qualification. Financing-ready buyers matched to eligible listings.',
                },
                {
                  title: 'Protected payment workflow',
                  body:
                    'Structured transaction support with digital bill of sale preparation and payment coordination.',
                },
                {
                  title: '60-second buyer alerts',
                  body:
                    'The moment a listing goes live, pre-qualified buyers are notified instantly.',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  style={{
                    background: c.bg,
                    borderRadius: 16,
                    padding: 28,
                    border: `1px solid ${c.border}`,
                  }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: c.tealLight,
                      border: `1px solid ${c.tealBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: c.teal }} />
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: c.navy }}>
                    {item.title}
                  </h3>
                  <p style={{ fontSize: 13, color: c.textMid, lineHeight: 1.65, margin: 0 }}>
                    {item.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* COMMUNITY IMPACT */}
      <section
        style={{
          padding: '100px 32px',
          background: 'linear-gradient(145deg, #0F2033 0%, #1A3A2A 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage:
              'radial-gradient(circle at 20% 50%, rgba(26,158,133,0.12) 0%, transparent 60%), radial-gradient(circle at 80% 20%, rgba(26,158,133,0.08) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />
        <div style={{ maxWidth: 1160, margin: '0 auto', position: 'relative' }}>
          <AnimatedSection>
            <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 64 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'rgba(26,158,133,0.15)',
                  border: '1px solid rgba(26,158,133,0.3)',
                  color: c.teal,
                  borderRadius: 20,
                  padding: '5px 14px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  marginBottom: 20,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: c.teal,
                    display: 'inline-block',
                  }}
                />
                Bridging the Gap
              </span>
              <h2
                style={{
                  fontSize: 'clamp(28px, 4vw, 48px)',
                  fontWeight: 700,
                  color: '#fff',
                  margin: '0 0 20px',
                  fontFamily: 'DM Sans, sans-serif',
                  lineHeight: 1.15,
                }}
              >
                Vehicle access for every
                <br />
                <span style={{ color: c.teal }}>Canadian community.</span>
              </h2>
              <p
                style={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: 17,
                  maxWidth: 660,
                  margin: '0 auto',
                  lineHeight: 1.75,
                }}
              >
                SkipTheDealerships.ai is closing the vehicle access gap for First Nations, Métis,
                and Inuit communities — and remote Canadians who have been overlooked by traditional
                dealership models for generations.
              </p>
            </motion.div>

            <div
              className={css.phoneOneColumn}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 20,
                marginBottom: 56,
              }}
            >
              {[
                {
                  icon: '🌍',
                  title: 'Remote Community Access',
                  body:
                    'Canada-wide digital marketplace gives remote and fly-in communities equal access to verified vehicles — no dealership within 500km required.',
                },
                {
                  icon: '🤝',
                  title: 'Indigenous Economic Inclusion',
                  body:
                    'Verified, transparent transactions protect First Nations buyers and sellers from predatory pricing and unfair dealer practices.',
                },
                {
                  icon: '📱',
                  title: 'Digital-First, Mobile-First',
                  body:
                    'Built for communities where a phone is the primary device. No showroom needed — buy or sell a vehicle entirely from your device.',
                },
                {
                  icon: '⚖️',
                  title: 'Fair Market Value for All',
                  body:
                    'Canadian Market Value Verified (VCMV) ensures every buyer pays fair market price — not an inflated rural premium.',
                },
                {
                  icon: '🏦',
                  title: 'Financing Without Barriers',
                  body:
                    'Pre-qualified buyer matching and financing partnerships built to serve communities traditionally excluded from mainstream auto financing.',
                },
                {
                  icon: '🛡️',
                  title: 'Protected Transactions',
                  body:
                    'Identity-verified sellers and structured payment workflows protect vulnerable buyers in communities where there is no local recourse.',
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 16,
                    padding: 28,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 14 }}>{item.icon}</div>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 10 }}>
                    {item.title}
                  </h3>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.55)',
                      lineHeight: 1.7,
                      margin: 0,
                    }}
                  >
                    {item.body}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Job Creation & Canadian AI R&D */}
            <motion.div variants={fadeUp} style={{ marginBottom: 20 }}>
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(26,158,133,0.15)',
                    border: '1px solid rgba(26,158,133,0.3)',
                    color: c.teal,
                    borderRadius: 20,
                    padding: '5px 14px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                  }}
                >
                  Canadian AI — Built Here
                </span>
                <h3
                  style={{
                    fontSize: 'clamp(22px, 3vw, 36px)',
                    fontWeight: 700,
                    color: '#fff',
                    margin: '16px 0 12px',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  High-value job creation.
                  <br />
                  Canadian AI R&D. Built to scale.
                </h3>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: 15,
                    lineHeight: 1.75,
                    maxWidth: 620,
                    margin: '0 auto',
                  }}
                >
                  SkipTheDealerships.ai is not just a marketplace — it is a Canadian AI research and
                  development platform creating high-skilled employment in software engineering,
                  data science, machine learning, and Indigenous community outreach across Canada.
                </p>
              </div>
              <div
                className={css.phoneTwoColumns}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 16,
                  marginBottom: 40,
                }}
              >
                {[
                  {
                    num: '40+',
                    label: 'Projected high-skill jobs',
                    sub: 'Engineering, AI, operations',
                  },
                  {
                    num: 'R&D',
                    label: 'Canadian AI platform',
                    sub: 'Automotive intelligence layer',
                  },
                  {
                    num: '10+',
                    label: 'Indigenous community partnerships',
                    sub: 'Targeted outreach program',
                  },
                  {
                    num: '$CAD',
                    label: 'Revenue stays in Canada',
                    sub: 'Manitoba-incorporated company',
                  },
                ].map((s, i) => (
                  <div
                    key={i}
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 14,
                      padding: '22px 20px',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 30,
                        fontWeight: 700,
                        color: c.teal,
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                    >
                      {s.num}
                    </div>
                    <div
                      style={{ fontSize: 13, fontWeight: 600, color: '#fff', margin: '6px 0 4px' }}
                    >
                      {s.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{s.sub}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className={css.phoneOneColumn}
              style={{
                background: 'rgba(26,158,133,0.1)',
                border: '1px solid rgba(26,158,133,0.25)',
                borderRadius: 20,
                padding: '40px 48px',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 40,
                alignItems: 'center',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: c.teal,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    marginBottom: 12,
                  }}
                >
                  Government & Grant Partners
                </div>
                <h3
                  style={{
                    fontSize: 'clamp(20px, 2.5vw, 28px)',
                    fontWeight: 700,
                    color: '#fff',
                    margin: '0 0 12px',
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  Aligned with Canada's Indigenous economic development mandate.
                </h3>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: 15,
                    lineHeight: 1.7,
                    margin: 0,
                    maxWidth: 580,
                  }}
                >
                  Seeking partnerships with Indigenous Services Canada, Canada Economic Development,
                  ISED, and provincial Indigenous business development programs to expand
                  marketplace access across Canada — including SR&ED tax credit eligible R&D
                  activities.
                </p>
              </div>
              <div style={{ textAlign: 'center', minWidth: 180 }}>
                <Btn primary style={{ fontSize: 15, padding: '14px 28px', whiteSpace: 'nowrap' }}>
                  Partner With Us
                </Btn>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 10 }}>
                  Government & grant inquiries
                </div>
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>

      {/* VIDEO SECTION */}
      <section
        style={{ padding: '80px 32px', background: '#fff', borderTop: `1px solid ${c.border}` }}
      >
        <AnimatedSection>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span
              style={{
                display: 'inline-block',
                background: c.tealLight,
                border: `1px solid ${c.tealBorder}`,
                borderRadius: 20,
                padding: '5px 16px',
                fontSize: 11,
                fontWeight: 700,
                color: c.teal,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                marginBottom: 16,
              }}
            >
              Watch & Learn
            </span>
            <h2
              style={{
                fontSize: 'clamp(26px, 3.5vw, 42px)',
                fontWeight: 800,
                color: c.navy,
                margin: '0 0 12px',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Real Stories. Real Savings.
            </h2>
            <p style={{ fontSize: 16, color: c.textMid, maxWidth: 480, margin: '0 auto' }}>
              Watch how Canadians are skipping the dealership and buying smarter — straight from our
              community.
            </p>
          </motion.div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
              gap: 28,
              maxWidth: 1100,
              margin: '0 auto 40px',
            }}
          >
            {[
              { id: 'Vx71KYdGKZY', title: 'I Bought an $85,000 Truck in 5 Minutes' },
              { id: 'lEvvC9grga4', title: 'Watch This Before You Go to a Dealership!' },
              { id: '-3DK3zgf0GQ', title: 'The Complete Used Car Buying Checklist' },
            ].map((video, i) => (
              <motion.a
                key={i}
                variants={fadeUp}
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  borderRadius: 16,
                  overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(15,32,51,0.10)',
                  background: '#000',
                  display: 'block',
                  textDecoration: 'none',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    paddingTop: '56.25%',
                    background: '#1a1a1a',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                    alt={video.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'scale(1.04)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  />
                  {/* Dark overlay + YouTube play button */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'rgba(0,0,0,0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        background: '#FF0000',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(255,0,0,0.5)',
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '14px 16px', background: '#fff' }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: c.navy,
                      fontFamily: 'DM Sans, sans-serif',
                    }}
                  >
                    {video.title}
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          <motion.div variants={fadeUp} style={{ textAlign: 'center' }}>
            <a
              href="https://www.youtube.com/@skipthedealerships"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                background: '#FF0000',
                color: '#fff',
                fontWeight: 700,
                fontSize: 15,
                padding: '12px 28px',
                borderRadius: 50,
                textDecoration: 'none',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              <span style={{ fontSize: 20 }}>▶</span> View All Videos on YouTube
            </a>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* FAQ SECTION */}
      <FaqSection />

      {/* FINAL CTA */}
      <section
        style={{
          padding: '100px 32px',
          background: c.heroBg,
          textAlign: 'center',
          borderTop: `1px solid ${c.border}`,
        }}
      >
        <AnimatedSection>
          <motion.div variants={fadeUp}>
            <Pill teal>Ready to start</Pill>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            style={{
              fontSize: 'clamp(30px, 4.5vw, 52px)',
              fontWeight: 700,
              color: c.navy,
              margin: '20px 0 14px',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            Sell smarter.
            <br />
            Buy with confidence.
          </motion.h2>
          <motion.p variants={fadeUp} style={{ color: c.textMid, fontSize: 17, marginBottom: 40 }}>
            No dealerships. No guesswork. Just verified transactions.
          </motion.p>
          <motion.div
            variants={fadeUp}
            style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}
          >
            <Btn
              primary
              style={{ fontSize: 16, padding: '15px 32px' }}
              onClick={() =>
                document.getElementById('inventory')?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              Browse Listings
            </Btn>
            <Btn style={{ fontSize: 16, padding: '15px 32px' }}>List My Vehicle</Btn>
            <a
              href="/dealers"
              style={{
                fontSize: 16,
                padding: '15px 32px',
                color: c.teal,
                borderColor: c.tealBorder,
                border: `1px solid ${c.tealBorder}`,
                borderRadius: 10,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Become a Dealer Partner
            </a>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* FOOTER */}
      <footer
        style={{
          background: '#080C12',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          // Bottom padding clears the fixed Hannah button (62px + up to 28px
          // offset), which otherwise sits on the last footer line at full scroll.
          padding: '60px 32px 112px',
        }}
      >
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <div
            className={css.phoneOneColumn}
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr',
              gap: 48,
              marginBottom: 56,
            }}
          >
            {/* Brand column */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <img
                  src={require('../../assets/logo-skip.png')}
                  alt="SkipTheDealerships"
                  style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'contain' }}
                />
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: '#fff',
                      letterSpacing: '-0.3px',
                      fontFamily: 'DM Sans, sans-serif',
                    }}
                  >
                    SkipTheDealerships
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#1A9E85',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                    }}
                  >
                    .ai
                  </div>
                </div>
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.45)',
                  lineHeight: 1.75,
                  maxWidth: 280,
                  margin: '0 0 24px',
                }}
              >
                Canada's AI-powered private vehicle marketplace. Every vehicle verified. Every
                person real. No dealerships.
              </p>
              {/* Social icons */}
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  {
                    name: 'Facebook',
                    url: 'https://www.facebook.com/skipthedealerships.ca/',
                    svg: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Instagram',
                    url: 'https://www.instagram.com/skip_the_dealerships_canada/',
                    svg: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'LinkedIn',
                    url: 'https://www.linkedin.com/company/skipthedealerships/',
                    svg: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'TikTok',
                    url: 'https://www.tiktok.com/@skipthedealership.ca',
                    svg: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.17 8.17 0 004.77 1.52V6.82a4.85 4.85 0 01-1-.13z" />
                      </svg>
                    ),
                  },
                ].map(({ name, url, svg }) => (
                  <a
                    key={name}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={name}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.5)',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(26,158,133,0.2)';
                      e.currentTarget.style.color = '#1A9E85';
                      e.currentTarget.style.borderColor = 'rgba(26,158,133,0.4)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    }}
                  >
                    {svg}
                  </a>
                ))}
              </div>
            </div>

            {/* Platform links */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  marginBottom: 18,
                }}
              >
                Platform
              </p>
              {[
                { label: 'Browse Listings', href: '#inventory' },
                { label: 'Sell My Vehicle', href: '/l/new' },
                { label: 'Leasing & Financing', href: '/leasing-financing' },
                { label: 'Get Pre-Qualified', href: '#hannah-widget' },
                { label: 'Dealer Partners', href: '/dealers' },
                { label: 'FAQ', href: '/faq' },
                // Descriptive anchor text rather than "read more": it is what
                // the link is worth to a reader and to a crawler.
                {
                  label: 'Used-Car Price Trends',
                  href: '/blog/used-car-prices-easing-canada-2026',
                },
                { label: 'Blog', href: '/blog' },
              ].map(({ label, href, external }) => (
                <div key={label} style={{ marginBottom: 12 }}>
                  <a
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#1A9E85')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
                  >
                    {label}
                  </a>
                </div>
              ))}
            </div>

            {/* Company links */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  marginBottom: 18,
                }}
              >
                Company
              </p>
              {/*
                None of these has a page yet. They stay visible — pointing them
                somewhere plausible would hide the gap — but without href, so
                they are not links that silently scroll to the top. Give one a
                page and SkipLandingPageFooterLinks.test.js asks for it here.
              */}
              {['About Us', 'Careers', 'Press', 'Indigenous Partnerships', 'Contact'].map(link => (
                <div key={link} style={{ marginBottom: 12 }}>
                  <a
                    style={{
                      fontSize: 13,
                      color: 'rgba(255,255,255,0.5)',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#1A9E85')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
                  >
                    {link}
                  </a>
                </div>
              ))}
            </div>

            {/* Legal links */}
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '1.2px',
                  textTransform: 'uppercase',
                  marginBottom: 18,
                }}
              >
                Legal
              </p>
              {/*
                Privacy Policy and Terms of Service were href="#" in the Legal
                column as well as in the bottom bar, so neither page could be
                reached from the landing page at all, though both routes exist
                (routeConfiguration.js:484-491). The three below them have no
                page yet and stay as plain text rather than links that silently
                do nothing.
              */}
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: null },
                { label: 'PIPEDA Compliance', href: null },
                { label: 'Security', href: null },
              ].map(({ label: link, href }) => (
                <div key={link} style={{ marginBottom: 12 }}>
                  <a
                    href={href || undefined}
                    style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#1A9E85')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
                  >
                    {link}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div
            style={{
              borderTop: '1px solid rgba(255,255,255,0.07)',
              paddingTop: 24,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
              © 2026 Drivana Technologies Inc. — Manitoba, Canada. All rights reserved.
            </p>
            {/*
              Privacy and Terms were href="#", so the two pages a reader is
              entitled to reach from any page were unreachable from the landing
              page even though both routes exist (routeConfiguration.js:484-491).
              They are real links now. "Security" has no page, so it is text
              rather than a link that silently does nothing — the same reasoning
              FooterContainer.js applies to its destination-less entries, which
              keeps the gap visible instead of pointing it somewhere plausible.
            */}
            <div style={{ display: 'flex', gap: 20 }}>
              {[
                { label: 'Privacy', href: '/privacy-policy' },
                { label: 'Terms', href: '/terms-of-service' },
                { label: 'Security', href: null },
              ].map(({ label, href }) => {
                const style = {
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.25)',
                  textDecoration: 'none',
                };
                return href ? (
                  <a
                    key={label}
                    href={href}
                    style={style}
                    onMouseEnter={e => (e.currentTarget.style.color = '#1A9E85')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.25)')}
                  >
                    {label}
                  </a>
                ) : (
                  <span key={label} style={style}>
                    {label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </footer>

      {/* FLOATING ASK SUNNY WIDGET — Chat + Voice */}
      <HannahWidget />
    </div>
  );
}

// ── Live inventory grid ──────────────────────────────────────────────────────

function LiveInventory() {
  const [listings, setListings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    fetch('/api/listings?perPage=6&sort=createdAt&state=published')
      .then(r => r.json())
      .then(data => {
        const items = data?.data || data?.listings || [];
        setListings(items.slice(0, 6));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  function formatPrice(attrs) {
    const cents = attrs?.price?.amount;
    if (!cents) return null;
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  function cardTitle(attrs) {
    const pd = attrs?.publicData || {};
    const year = pd.year || '';
    const make = pd.make || '';
    const model = pd.model || '';
    const trim = pd.trim || '';
    if (year || make || model) return `${year} ${make} ${model}${trim ? ' ' + trim : ''}`.trim();
    return attrs?.title || 'Vehicle';
  }

  function cardSub(attrs) {
    const pd = attrs?.publicData || {};
    const km = pd.odometer ? `${Number(pd.odometer).toLocaleString('en-CA')} km` : null;
    const color = pd.color || pd.exteriorColor || null;
    return [km, color].filter(Boolean).join(' · ') || null;
  }

  // Skeleton cards while loading
  if (loading) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 24,
          marginBottom: 48,
        }}
      >
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            style={{
              borderRadius: 16,
              border: `1px solid ${c.border}`,
              background: c.white,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: 160,
                background: 'linear-gradient(90deg, #f0f4f8 25%, #e4eaf0 50%, #f0f4f8 75%)',
                animation: 'shimmer 1.4s infinite',
              }}
            />
            <div style={{ padding: '16px 18px' }}>
              <div
                style={{
                  height: 16,
                  background: '#eef2f6',
                  borderRadius: 4,
                  marginBottom: 8,
                  width: '70%',
                }}
              />
              <div
                style={{
                  height: 12,
                  background: '#eef2f6',
                  borderRadius: 4,
                  marginBottom: 16,
                  width: '50%',
                }}
              />
              <div style={{ height: 20, background: '#eef2f6', borderRadius: 4, width: '40%' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Fallback if API fails or no listings
  if (error || listings.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0 48px', color: c.textMid }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🚗</div>
        <p style={{ fontSize: 15, marginBottom: 24 }}>New listings are being added daily.</p>
        <a
          href="/s"
          style={{
            display: 'inline-block',
            padding: '14px 36px',
            background: c.teal,
            color: '#fff',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 15,
            textDecoration: 'none',
          }}
        >
          Browse All Listings →
        </a>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 24,
          marginBottom: 48,
        }}
      >
        {listings.map((listing, i) => {
          const attrs = listing.attributes || {};
          const pd = attrs.publicData || {};
          const id = listing.id?.uuid || listing.id;
          const price = formatPrice(attrs);
          const title = cardTitle(attrs);
          const sub = cardSub(attrs);
          const img =
            listing.images?.[0]?.attributes?.variants?.['landscape-crop']?.url ||
            listing.images?.[0]?.attributes?.url ||
            null;
          // The vehicle card IS the link: a real <a href> to the canonical vehicle URL
          // (util/vehicleLinks), so click, Ctrl/Cmd-click, middle-click, "open in new
          // tab", copy-link and keyboard all behave like any link. Nothing inside it is
          // interactive, so no element is nested illegally. Without a usable id there
          // is no link at all — never "#", never a different page in its place.
          const href = vehiclePath(listing);
          const Card = href ? motion.a : motion.div;

          return (
            <Card
              key={id || i}
              variants={fadeUp}
              whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(26,158,133,0.12)' }}
              {...(href ? { href } : {})}
              style={{
                display: 'block',
                color: 'inherit',
                textDecoration: 'none',
                borderRadius: 16,
                border: `1px solid ${c.border}`,
                background: c.white,
                overflow: 'hidden',
                cursor: href ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s',
              }}
            >
              {/* Photo or placeholder */}
              <div
                style={{
                  height: 160,
                  background: `linear-gradient(135deg, ${c.bgAlt} 0%, #DFF0ED 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {img ? (
                  <img
                    src={img}
                    alt={title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      position: 'absolute',
                      inset: 0,
                    }}
                  />
                ) : (
                  <span style={{ fontSize: 48 }}>🚗</span>
                )}
                {verificationState(listing).verified ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: c.teal,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '4px 8px',
                      borderRadius: 6,
                      letterSpacing: '0.4px',
                    }}
                  >
                    ✓ VERIFIED
                  </div>
                ) : null}
              </div>

              <div style={{ padding: '16px 18px' }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: c.navy, marginBottom: 4 }}>
                  {title}
                </div>
                <div style={{ fontSize: 13, color: c.textMid, marginBottom: 12 }}>{sub}</div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  {price ? (
                    <span style={{ fontSize: 18, fontWeight: 700, color: c.teal }}>{price}</span>
                  ) : (
                    <span style={{ fontSize: 13, color: c.textMid }}>Price on request</span>
                  )}
                  {href ? (
                    <span
                      style={{
                        fontSize: 12,
                        color: c.textLight,
                        background: c.bg,
                        padding: '4px 10px',
                        borderRadius: 20,
                        border: `1px solid ${c.border}`,
                      }}
                    >
                      View listing
                    </span>
                  ) : null}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <motion.div variants={fadeUp} style={{ textAlign: 'center' }}>
        <a
          href="/s"
          style={{
            display: 'inline-block',
            padding: '14px 36px',
            background: c.teal,
            color: '#fff',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 15,
            textDecoration: 'none',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          Browse All Listings →
        </a>
      </motion.div>
    </>
  );
}

// ── Hero situational CTA ────────────────────────────────────────────────────

function HeroCTA() {
  const [step, setStep] = React.useState('intent'); // 'intent' | 'postal' | 'done'
  const [intent, setIntent] = React.useState(null); // 'buy' | 'sell'
  const [postal, setPostal] = React.useState('');
  const [postalError, setPostalError] = React.useState('');

  // Canada Post's actual format: D, F, I, O, Q, U are never valid in the
  // first position, and D, F, I, O, Q, U, W, Z never appear after it either
  // (W and Z are valid first-letter values, just not elsewhere) — a plain
  // [A-Za-z] pattern silently accepts postal codes that do not exist.
  const POSTAL_RE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

  function handlePostalSubmit() {
    const clean = postal
      .trim()
      .toUpperCase()
      .replace(/\s+/g, ' ');
    if (!POSTAL_RE.test(clean)) {
      setPostalError('Enter a valid Canadian postal code (e.g. R3C 1A4)');
      return;
    }
    setPostalError('');
    setStep('done');
    if (intent === 'sell') {
      window.location.href = '/l/new';
    } else {
      // Pre-qualification flow — open Hannah chat focused on pre-approval
      const event = new CustomEvent('skip:open-preapproval', { detail: { postal: clean } });
      window.dispatchEvent(event);
      // Fallback: scroll to Hannah
      const el = document.getElementById('hannah-widget');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const btnBase = {
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    transition: 'all 0.18s',
    padding: '14px 28px',
  };

  if (step === 'intent') {
    return (
      <div>
        <p
          style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: 13,
            marginBottom: 12,
            fontWeight: 500,
          }}
        >
          What brings you here today?
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            style={{
              ...btnBase,
              background: '#1A9E85',
              color: '#fff',
              boxShadow: '0 4px 20px rgba(26,158,133,0.4)',
            }}
            onClick={() => {
              setIntent('buy');
              setStep('postal');
            }}
          >
            🔍 I Want to Buy
          </button>
          <button
            style={{
              ...btnBase,
              background: '#fff',
              color: '#0F2033',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            }}
            onClick={() => {
              setIntent('sell');
              setStep('postal');
            }}
          >
            🚗 I Want to Sell
          </button>
          <a
            href="#inventory"
            style={{
              ...btnBase,
              background: 'transparent',
              color: 'rgba(255,255,255,0.75)',
              border: '1.5px solid rgba(255,255,255,0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              backdropFilter: 'blur(8px)',
            }}
          >
            Browse Listings
          </a>
        </div>
      </div>
    );
  }

  if (step === 'postal') {
    const isSell = intent === 'sell';
    return (
      <div>
        <p
          style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: 13,
            marginBottom: 10,
            fontWeight: 500,
          }}
        >
          {isSell
            ? "📍 What's your postal code? We'll show you comparable listings nearby."
            : "📍 What's your postal code? We'll find lenders and inventory in your area."}
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div
            className={css.heroPostalField}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          >
            <div
              className={css.heroPostalBox}
              style={{
                display: 'flex',
                gap: 0,
                background: '#fff',
                borderRadius: 10,
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                border: postalError ? '2px solid #e55' : '2px solid transparent',
              }}
            >
              <input
                autoFocus
                className={css.heroPostalInput}
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="postal-code"
                placeholder="e.g. R3C 4T3"
                value={postal}
                maxLength={10}
                onChange={e => {
                  setPostal(e.target.value);
                  setPostalError('');
                }}
                onKeyDown={e => e.key === 'Enter' && handlePostalSubmit()}
                style={{
                  border: 'none',
                  outline: 'none',
                  padding: '14px 16px',
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: 'DM Sans, sans-serif',
                  color: '#0F2033',
                  background: 'transparent',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              />
              <button
                onClick={handlePostalSubmit}
                style={{
                  ...btnBase,
                  borderRadius: 0,
                  padding: '14px 22px',
                  background: isSell ? '#0F2033' : '#1A9E85',
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                {isSell ? 'List My Car →' : 'Get Pre-Qualified →'}
              </button>
            </div>
            {postalError && (
              <span style={{ color: '#ff8a8a', fontSize: 11, fontWeight: 600 }}>{postalError}</span>
            )}
          </div>
          <button
            onClick={() => setStep('intent')}
            style={{
              ...btnBase,
              background: 'transparent',
              color: 'rgba(255,255,255,0.45)',
              border: 'none',
              padding: '14px 10px',
              fontSize: 13,
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // step === 'done' — briefly shown before redirect
  return (
    <div style={{ color: '#1A9E85', fontSize: 15, fontWeight: 700 }}>✓ Taking you there now…</div>
  );
}

function HowItWorksSection() {
  const [howTab, setHowTab] = React.useState('seller');
  const activeSteps = howTab === 'seller' ? sellerSteps : buyerSteps;
  return (
    <section id="how" style={{ padding: '100px 32px', background: c.white }}>
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <AnimatedSection>
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 40 }}>
            <Pill>How it works</Pill>
            <h2
              style={{
                fontSize: 'clamp(26px, 3.5vw, 42px)',
                fontWeight: 800,
                margin: '16px 0 12px',
                fontFamily: 'DM Sans, sans-serif',
                color: c.navy,
              }}
            >
              Simple. Secure. No dealership needed.
            </h2>
            <p style={{ color: c.textMid, fontSize: 16, maxWidth: 500, margin: '0 auto 32px' }}>
              Six guided steps — whether you're selling your car or buying one privately in Canada.
            </p>
            {/* Buyer / Seller toggle */}
            <div
              style={{
                display: 'inline-flex',
                background: c.bg,
                border: `1px solid ${c.border}`,
                borderRadius: 50,
                padding: 4,
                gap: 4,
              }}
            >
              {[['seller', "🚗 I'm Selling"], ['buyer', "🔍 I'm Buying"]].map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setHowTab(tab)}
                  style={{
                    padding: '10px 28px',
                    borderRadius: 50,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                    fontWeight: 700,
                    fontSize: 14,
                    background: howTab === tab ? c.teal : 'transparent',
                    color: howTab === tab ? '#fff' : c.textMid,
                    transition: 'all 0.2s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Steps grid */}
          <div
            className={css.phoneOneColumn}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}
          >
            {activeSteps.map((step, i) => (
              <motion.div
                key={`${howTab}-${i}`}
                variants={fadeUp}
                style={{
                  background: c.bg,
                  borderRadius: 18,
                  padding: '28px 24px 24px',
                  border: `1px solid ${c.border}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    fontSize: 64,
                    fontWeight: 800,
                    color: 'rgba(26,158,133,0.06)',
                    position: 'absolute',
                    top: 8,
                    right: 14,
                    fontFamily: 'DM Sans, sans-serif',
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                >
                  {step.num}
                </div>
                <div style={{ fontSize: 28, marginBottom: 14, lineHeight: 1 }}>{step.icon}</div>
                <div
                  style={{
                    display: 'inline-block',
                    background: c.tealLight,
                    border: `1px solid ${c.tealBorder}`,
                    color: c.teal,
                    borderRadius: 20,
                    padding: '2px 10px',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    marginBottom: 10,
                  }}
                >
                  {step.badge}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: c.navy, margin: '0 0 8px' }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: 13.5, color: c.textMid, lineHeight: 1.7, margin: 0 }}>
                  {step.body}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div variants={fadeUp} style={{ textAlign: 'center', marginTop: 48 }}>
            {howTab === 'seller' ? (
              <a
                href="/l/new"
                style={{
                  display: 'inline-block',
                  background: c.teal,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  padding: '14px 36px',
                  borderRadius: 50,
                  textDecoration: 'none',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 4px 20px rgba(26,158,133,0.35)',
                }}
              >
                Start Your Listing →
              </a>
            ) : (
              <a
                href="/s"
                style={{
                  display: 'inline-block',
                  background: c.teal,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  padding: '14px 36px',
                  borderRadius: 50,
                  textDecoration: 'none',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 4px 20px rgba(26,158,133,0.35)',
                }}
              >
                Browse Verified Vehicles →
              </a>
            )}
            <p style={{ marginTop: 14, fontSize: 13, color: c.textLight }}>
              No dealership. No markup. No pressure.
            </p>
          </motion.div>
        </AnimatedSection>
      </div>
    </section>
  );
}

const SUNNY_NEWS = {
  id: 'file-attachments-2025-07',
  message: `🎉 Quick update from the Skip team! You can now attach files directly in your transaction conversations — photos, PDFs, contracts, whatever you need to share with the other party, all without leaving the platform. No more back-and-forth emails! Any questions? Just ask me 😊`,
};

// Clock-based support hours only. This says when the team is scheduled to be reachable, not
// whether a person is free right now. Errors read as "outside hours", never as "online".
function isBusinessHours() {
  try {
    const h = parseInt(
      new Date().toLocaleString('en-US', {
        timeZone: 'America/Winnipeg',
        hour: 'numeric',
        hour12: false,
      }),
      10
    );
    return h >= 9 && h < 21;
  } catch {
    return false;
  }
}

function HannahWidget() {
  const [chatOpen, setChatOpen] = React.useState(false);
  const [mode, setMode] = React.useState('chat'); // 'chat' | 'voice'
  const [online] = React.useState(isBusinessHours);
  const [chatInput, setChatInput] = React.useState('');
  // Nothing is sent to a provider until this is true.
  //
  // Both answers are remembered between mounts, keyed to the contract version
  // and the locale it was read in — see util/aiConsent. This used to reset on
  // every mount, so a visitor met the notice on every page and every return
  // visit and learned to dismiss it unread. A reworded contract still re-asks
  // everybody, because the stored version stops matching.
  const [chatConsented, setChatConsented] = React.useState(() =>
    hasRememberedConsent(PURPOSES.AI_CHAT)
  );
  // A refusal is an answer, not a postponement. Remembering it is what lets the
  // notice close and stay closed while the chat itself stays switched off.
  const [chatDeclined, setChatDeclined] = React.useState(() =>
    hasRememberedDecline(PURPOSES.AI_CHAT)
  );
  // Separate from chat consent on purpose. "This chat sends your messages" does
  // not disclose that a recording of your voice is uploaded, so the microphone
  // asks for its own, and asks before the recorder starts rather than after.
  const [micConsented, setMicConsented] = React.useState(false);
  const [micPrompt, setMicPrompt] = React.useState(false);
  // The Voice tab is a third recipient again. It does not upload to us — it
  // calls the browser's Web Speech API, which in Chrome streams the microphone
  // to Google. The Whisper contract names the wrong vendor for it, so it gets
  // its own, and accepting the text-chat notice must not switch it on.
  const [speechConsented, setSpeechConsented] = React.useState(false);
  const [speechPrompt, setSpeechPrompt] = React.useState(false);
  // Being willing to talk to a chatbot is not being willing to become a sales
  // record, so the CRM asks separately too — at the point contact details are
  // collected, not retroactively.
  const [crmConsented, setCrmConsented] = React.useState(false);
  const [crmPromptDismissed, setCrmPromptDismissed] = React.useState(false);
  // Per-page-load, never persisted. It ties the receipts for one visit together
  // without identifying anybody who has not told us who they are.
  const chatSessionId = React.useRef(
    `s-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 10)}`
  );
  const [newsRead, setNewsRead] = React.useState(() => {
    try {
      return localStorage.getItem('skip_news_read') === SUNNY_NEWS.id;
    } catch {
      return false;
    }
  });
  const [chatHistory, setChatHistory] = React.useState([
    {
      role: 'assistant',
      content:
        "Hi, I'm Hannah. 🍁\n\nI'm your AI automotive advisor for SkipTheDealerships.ai. My job isn't to sell you a vehicle — my job is to help you make the smartest decision possible.\n\nI can help you buy, sell, finance, trade, refinance, compare vehicles, explain payments, estimate market values, verify VINs, explain warranties, or simply answer your questions.\n\nWhat brings you here today?",
      source: 'vehicle',
    },
  ]);
  const [chatLoading, setChatLoading] = React.useState(false);
  const [leadInfo, setLeadInfo] = React.useState(null);
  const [knownName, setKnownName] = React.useState(null); // first name if identified
  const [preApprovalStep, setPreApprovalStep] = React.useState(0);
  const [preApprovalData, setPreApprovalData] = React.useState({});
  const [sellerLeadStep, setSellerLeadStep] = React.useState(0);
  const [sellerLeadData, setSellerLeadData] = React.useState({});
  const [preApprovalDocs, setPreApprovalDocs] = React.useState({});
  const docInputRef = React.useRef(null);
  const [profanityWarned, setProfanityWarned] = React.useState(false);
  const [blocked, setBlocked] = React.useState(false);
  // 30-second undo window after application submit
  const [cancelCountdown, setCancelCountdown] = React.useState(0); // 0 = not active
  const submitTimeoutRef = React.useRef(null);
  const countdownIntervalRef = React.useRef(null);
  // Voice mode state
  const [voiceStatus, setVoiceStatus] = React.useState('idle'); // 'idle' | 'listening' | 'thinking' | 'speaking'
  const [voiceTranscript, setVoiceTranscript] = React.useState('');
  const [voiceReply, setVoiceReply] = React.useState('');
  const [voiceError, setVoiceError] = React.useState('');
  const [language, setLanguage] = React.useState('en');
  const recognitionRef = React.useRef(null);
  const chatEndRef = React.useRef(null);

  // On mount — try to identify the logged-in user from Sharetribe session
  React.useEffect(() => {
    fetch('/api/current-user', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        const firstName =
          data?.data?.attributes?.profile?.firstName ||
          data?.data?.attributes?.profile?.displayName?.split(' ')[0] ||
          null;
        if (firstName) {
          setKnownName(firstName);
          setLeadInfo(prev => (prev ? prev : { name: firstName }));
          // Personalise the opening greeting
          setChatHistory([
            {
              role: 'assistant',
              content: `Hey ${firstName}! 👋 I'm Hannah, your AI automotive advisor here at SkipTheDealerships.ai. What brings you here today?`,
              source: 'vehicle',
            },
          ]);
        }
      })
      .catch(() => {}); // not logged in — silent fail
  }, []);

  // Inline mic state (getUserMedia + Whisper)
  const [micRecording, setMicRecording] = React.useState(false);
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);

  // Supported languages: [code, BCP-47 for speech, display name, flag]
  // Order: English, French, then A–Z by English language name
  const LANGUAGES = [
    ['en', 'en-CA', 'English', '🇨🇦'], // 1st — primary
    ['fr', 'fr-CA', 'Français', '🇫🇷'], // 2nd — primary
    ['am', 'am-ET', 'አማርኛ', '🇪🇹'], // Amharic
    ['ar', 'ar-SA', 'العربية', '🇸🇦'], // Arabic
    ['zh', 'zh-CN', '中文 (简体)', '🇨🇳'], // Chinese Simplified
    ['zh-TW', 'zh-TW', '中文 (繁體)', '🇹🇼'], // Chinese Traditional
    ['de', 'de-DE', 'Deutsch', '🇩🇪'], // German
    ['es', 'es-ES', 'Español', '🇪🇸'], // Spanish
    ['fa', 'fa-IR', 'فارسی', '🇮🇷'], // Farsi / Persian
    ['tl', 'fil-PH', 'Filipino', '🇵🇭'], // Filipino
    ['hi', 'hi-IN', 'हिन्दी', '🇮🇳'], // Hindi
    ['it', 'it-IT', 'Italiano', '🇮🇹'], // Italian
    ['ja', 'ja-JP', '日本語', '🇯🇵'], // Japanese
    ['ko', 'ko-KR', '한국어', '🇰🇷'], // Korean
    ['pl', 'pl-PL', 'Polski', '🇵🇱'], // Polish
    ['pt', 'pt-BR', 'Português', '🇧🇷'], // Portuguese
    ['pa', 'pa-IN', 'ਪੰਜਾਬੀ', '🇮🇳'], // Punjabi
    ['ru', 'ru-RU', 'Русский', '🇷🇺'], // Russian
    ['sq', 'sq-AL', 'Shqip', '🇦🇱'], // Albanian
    ['so', 'so-SO', 'Soomaali', '🇸🇴'], // Somali
    ['vi', 'vi-VN', 'Tiếng Việt', '🇻🇳'], // Vietnamese
    ['tr', 'tr-TR', 'Türkçe', '🇹🇷'], // Turkish
    ['uk', 'uk-UA', 'Українська', '🇺🇦'], // Ukrainian
    ['ur', 'ur-PK', 'اردو', '🇵🇰'], // Urdu
  ];

  const currentLang = LANGUAGES.find(l => l[0] === language) || LANGUAGES[0];

  /**
   * Whether we may offer each AI feature in the language the person is using.
   *
   * Fail closed rather than falling back to English. A privacy notice somebody
   * cannot read is not a notice, so a locale without a reviewed translation
   * loses the feature rather than getting an English contract. The server
   * refuses the same locales, so this is the honest version of a rule that is
   * enforced either way — not the enforcement itself.
   */
  const chatConsentAvailable = isConsentAvailable(PURPOSES.AI_CHAT, language);
  const micConsentAvailable = isConsentAvailable(PURPOSES.TRANSCRIPTION, language);
  const speechConsentAvailable = isConsentAvailable(PURPOSES.SPEECH_RECOGNITION, language);

  /**
   * Switching language withdraws consent.
   *
   * Somebody who agreed while reading the English notice has agreed to that
   * notice. Carrying that agreement into another language would record consent
   * to wording they never saw — the same mistake as honouring a superseded
   * version, one axis over.
   *
   * The ref is load-bearing, not tidiness. An effect keyed on [language] also
   * runs on mount, where there has been no change to react to — and it would
   * clear the decision just restored from storage, so a returning visitor would
   * be asked again every time and the persistence above would do nothing.
   *
   * On a real change the chat decision is re-derived for the new locale rather
   * than blanked, because the stored record is keyed by the locale it was read
   * in: moving to a language with no approved wording yields nothing and the
   * notice returns, and moving back to the one they did read restores the
   * answer they actually gave. The session-scoped prompts below are simply
   * reset — none of them is remembered in the first place.
   */
  const previousLanguage = React.useRef(language);
  React.useEffect(() => {
    if (previousLanguage.current === language) return;
    previousLanguage.current = language;

    setChatConsented(hasRememberedConsent(PURPOSES.AI_CHAT, language));
    setChatDeclined(hasRememberedDecline(PURPOSES.AI_CHAT, language));
    setMicConsented(false);
    setMicPrompt(false);
    setSpeechConsented(false);
    setSpeechPrompt(false);
    setCrmConsented(false);
    setCrmPromptDismissed(false);
  }, [language]);

  // Profanity guard — common English profanity list
  const PROFANITY = [
    'fuck',
    'shit',
    'bitch',
    'asshole',
    'cunt',
    'cock',
    'dick',
    'pussy',
    'bastard',
    'motherfucker',
    'faggot',
    'nigger',
    'nigga',
    'whore',
    'slut',
    'piss',
    'crap',
    'damn',
    'ass',
  ];
  const hasProfanity = text => {
    const lower = text.toLowerCase().replace(/[^a-z\s]/g, ' ');
    return PROFANITY.some(w => lower.split(/\s+/).includes(w));
  };

  React.useEffect(() => {
    if (chatOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory, chatOpen, preApprovalStep]);

  // Stop speech when closing or switching modes
  React.useEffect(() => {
    if (!chatOpen || mode === 'chat') {
      window.speechSynthesis && window.speechSynthesis.cancel();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
      setVoiceStatus('idle');
    }
  }, [chatOpen, mode]);

  // Live-agent callback: if Hannah just offered to ring the customer's cell
  // and their reply contains a phone number, connect them to an agent.
  const tryConnectAgent = async msg => {
    const lastAssistant = [...chatHistory].reverse().find(m => m.role === 'assistant');
    const offeredCall =
      lastAssistant &&
      /ring your cell|connect you to a live agent|call you|live agent right now/i.test(
        lastAssistant.content
      );
    const phoneMatch = msg.replace(/\D/g, '');
    const hasPhone = phoneMatch.length === 10 || phoneMatch.length === 11;
    if (!offeredCall || !hasPhone) return false;

    setChatHistory(h => [...h, { role: 'user', content: msg }]);
    try {
      const res = await fetch('/api/connect-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: msg }),
      });
      const data = await res.json();
      const reply =
        data.message ||
        (data.success
          ? `📞 Calling you now — pick up and I'll connect you to a live agent!`
          : `I couldn't place the call. You can reach our team directly at (204) 996-1818.`);
      setChatHistory(h => [...h, { role: 'assistant', content: reply, source: 'vehicle' }]);
    } catch {
      setChatHistory(h => [
        ...h,
        {
          role: 'assistant',
          content: `I couldn't place the call right now. Please call us directly at (204) 996-1818.`,
          source: 'vehicle',
        },
      ]);
    }
    return true;
  };

  const askHannah = async msg => {
    if (!msg || chatLoading) return;

    // If Hannah offered a live-agent callback and they gave a number, connect them.
    if (await tryConnectAgent(msg)) return '';

    // Profanity guard
    if (hasProfanity(msg)) {
      const userMsg = { role: 'user', content: msg };
      if (blocked) return '';
      if (profanityWarned) {
        setBlocked(true);
        const refusal =
          "I'm going to end our conversation here. Please feel free to come back when you're ready to chat respectfully. Have a great day!";
        setChatHistory(h => [
          ...h,
          userMsg,
          { role: 'assistant', content: refusal, source: 'vehicle' },
        ]);
        if (mode === 'voice') speakText(refusal);
        return refusal;
      } else {
        setProfanityWarned(true);
        const warning =
          "I'd appreciate if we could keep our conversation respectful — I'm here to help you find your next vehicle! Please keep it professional and I'll do my best for you.";
        setChatHistory(h => [
          ...h,
          userMsg,
          { role: 'assistant', content: warning, source: 'vehicle' },
        ]);
        if (mode === 'voice') speakText(warning);
        return warning;
      }
    }

    if (blocked) return '';
    // The UI keeps the composer disabled until consent is given; this is the
    // backstop so no other caller of askHannah can reach a provider without it.
    if (!chatConsented || !chatConsentAvailable) return '';
    setChatLoading(true);
    const newHistory = [...chatHistory, { role: 'user', content: msg }];
    try {
      const apiHistory = newHistory.slice(-8).map(m => ({ role: m.role, content: m.content }));
      // Try to pick up their name from what they said (e.g. "I'm John" / "My name is Sarah")
      if (!knownName) {
        const nameMatch = msg.match(
          /(?:i['']?m|my name is|call me|it['']?s|this is)\s+([A-Z][a-z]{1,20})/i
        );
        if (nameMatch) {
          const detected = nameMatch[1];
          setKnownName(detected);
          setLeadInfo(prev => ({ ...(prev || {}), name: detected }));
        }
      }

      const res = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          history: apiHistory.slice(0, -1),
          // The CRM consent travels with the contact details it governs, so the
          // server can refuse the HubSpot write while still answering the chat.
          leadInfo: leadInfo
            ? {
                ...leadInfo,
                crmConsent: crmConsented,
                crmConsentVersion: crmConsented ? CRM_CONSENT_VERSION : null,
              }
            : null,
          language,
          userName: knownName,
          sessionId: chatSessionId.current,
          aiConsent: true,
          consentVersion: CHAT_CONSENT_VERSION,
          // Recorded on the receipt, and re-checked server-side: it says which
          // rendering of the contract this person actually read.
          consentLocale: language,
        }),
      });
      const data = await res.json();
      const reply = data.reply || "Sorry, I couldn't get a response. Please try again.";
      setChatHistory([...newHistory, { role: 'assistant', content: reply, source: data.source }]);
      if (data.askForContact && !leadInfo && preApprovalStep === 0 && sellerLeadStep === 0) {
        setPreApprovalStep(1);
        const openingMsg = `I can help you get **pre-approved for a vehicle loan** right now — it only takes a few minutes and there's no impact to your credit score! 🎉\n\nLet's get started. What is your **full name**? (first and last name)`;
        setChatHistory(h => [...h, { role: 'assistant', content: openingMsg, source: 'vehicle' }]);
      } else if (data.askToSell && !leadInfo && preApprovalStep === 0 && sellerLeadStep === 0) {
        setSellerLeadStep(1);
        const openingMsg = `Great — this is exciting! 🎉\n\nWhat do you have to sell? Year, make, and model to start.`;
        setChatHistory(h => [...h, { role: 'assistant', content: openingMsg, source: 'vehicle' }]);
      }
      // Note: when intent is unclear, no scripted message is injected —
      // Hannah's own reply already carries the conversation, and a bolted-on
      // clarifier was showing up as a redundant second question.
      return reply;
    } catch {
      const err = 'Connection error. Please try again.';
      setChatHistory([...newHistory, { role: 'assistant', content: err, source: 'vehicle' }]);
      return err;
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading || blocked) return;
    setChatInput('');
    if (sellerLeadStep > 0) {
      handleSellerLeadAnswer(msg);
    } else if (preApprovalStep > 0) {
      handlePreApprovalAnswer(msg);
    } else {
      await askHannah(msg);
    }
  };

  // ── VOICE ──────────────────────────────────────────────────
  const speakText = text => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    const bcp47 = currentLang[1]; // e.g. 'fr-CA', 'zh-CN'
    utt.lang = bcp47;
    const voices = window.speechSynthesis.getVoices();
    // 1. Female voice in the exact language
    const femaleExact = voices.find(
      v =>
        v.lang.startsWith(bcp47.slice(0, 2)) &&
        /female|woman|samantha|karen|victoria|moira|fiona|allison|ava|google/i.test(v.name)
    );
    // 2. Any voice in the language
    const anyExact = voices.find(v => v.lang.startsWith(bcp47.slice(0, 2)));
    const preferred = femaleExact || anyExact;
    if (preferred) utt.voice = preferred;
    utt.rate = 1.05;
    utt.pitch = 1.1;
    utt.onstart = () => setVoiceStatus('speaking');
    utt.onend = () => setVoiceStatus('idle');
    utt.onerror = () => setVoiceStatus('idle');
    window.speechSynthesis.speak(utt);
  };

  const startListening = () => {
    // Gate before the recogniser is constructed, not before the transcript is
    // used: SpeechRecognition starts streaming audio the moment it starts.
    // Both chat AND speech consent are required: chat for the AI backend,
    // speech for the microphone and transcription.
    if (!chatConsentAvailable || !chatConsented) {
      setChatPrompt(true);
      return;
    }
    if (!speechConsentAvailable) return;
    if (!speechConsented) {
      setSpeechPrompt(true);
      return;
    }
    setVoiceError('');
    setVoiceTranscript('');
    setVoiceReply('');
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceError('Voice input is not supported in this browser. Try Chrome or Edge.');
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = currentLang[1]; // BCP-47 code for selected language
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;
    setVoiceStatus('listening');
    rec.onresult = e => {
      const transcript = Array.from(e.results)
        .map(r => r[0].transcript)
        .join('');
      setVoiceTranscript(transcript);
    };
    rec.onerror = e => {
      setVoiceError(
        e.error === 'not-allowed'
          ? 'Microphone access denied. Please allow mic in your browser.'
          : 'Could not hear you. Try again.'
      );
      setVoiceStatus('idle');
    };
    rec.onend = async () => {
      const final = recognitionRef.current._lastTranscript || '';
      if (!final) {
        setVoiceStatus('idle');
        return;
      }
      setVoiceStatus('thinking');
      const reply = await askHannah(final);
      setVoiceReply(reply);
      speakText(reply);
    };
    // Capture final transcript before onend fires
    rec.onspeechend = () => {
      recognitionRef.current._lastTranscript = voiceTranscript;
      rec.stop();
    };
    rec.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current._lastTranscript = voiceTranscript;
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis && window.speechSynthesis.cancel();
    setVoiceStatus('idle');
  };

  // Inline mic: record → Whisper → fill text input
  //
  // Gated separately from the chat. The notice has to come before the recorder
  // starts, not before the upload — audio captured under a misunderstanding is
  // already captured, and "we asked before sending it" is not the promise we
  // want to be making about somebody's voice.
  const toggleMic = async () => {
    if (micRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    if (!micConsentAvailable) return;
    if (!micConsented) {
      setMicPrompt(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = e => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setMicRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (blob.size < 100) return;
        try {
          const resp = await fetch('/api/transcribe', {
            method: 'POST',
            headers: {
              'Content-Type': mimeType,
              'x-language': currentLang[1],
              // The body is the audio, so consent travels in headers. The
              // server checks it against the transcription contract; the chat
              // contract is refused here as a purpose mismatch.
              'x-ai-consent': 'true',
              'x-ai-consent-version': TRANSCRIPTION_CONSENT_VERSION,
              'x-ai-consent-locale': language,
            },
            body: blob,
          });
          const data = await resp.json();
          if (data.text) setChatInput(prev => (prev ? prev + ' ' + data.text : data.text));
        } catch {
          /* silently ignore transcription errors */
        }
      };

      recorder.start();
      setMicRecording(true);

      // Auto-stop after 30s max
      setTimeout(() => {
        if (recorder.state === 'recording') recorder.stop();
      }, 30000);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        alert('Microphone access denied. Please allow mic access in your browser settings.');
      }
    }
  };
  // ──────────────────────────────────────────────────────────

  const fireSubmitNow = async (data, docs) => {
    try {
      await fetch('/api/dealer-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealerName: data.fullName || '',
          email: data.email || '',
          phone: data.phone || '',
          company: (data.employerInfo || '').split(',')[0] || 'Buyer Pre-Approval',
          type: 'buyer-preapproval',
          notes: JSON.stringify(data),
          docs: docs
            ? {
                driversLicense: !!docs.driversLicense,
                statusCard: !!docs.statusCard,
                voidCheque: !!docs.voidCheque,
              }
            : {},
        }),
      });
    } catch (e) {
      /* non-blocking */
    }
    setLeadInfo({ name: data.fullName, email: data.email });
  };

  const cancelSubmit = () => {
    clearTimeout(submitTimeoutRef.current);
    clearInterval(countdownIntervalRef.current);
    setCancelCountdown(0);
    setChatHistory(h => [
      ...h,
      {
        role: 'assistant',
        content: `✅ Application cancelled — no worries! Your information has NOT been submitted. Whenever you're ready to try again, just let me know. 😊`,
        source: 'vehicle',
      },
    ]);
    // Reset pre-approval flow so they can start fresh
    setPreApprovalStep(0);
    setPreApprovalData({});
    setPreApprovalDocs({});
  };

  const submitPreApproval = (data, docs) => {
    // Start 30-second countdown — customer can cancel before it fires
    let secs = 30;
    setCancelCountdown(secs);
    countdownIntervalRef.current = setInterval(() => {
      secs -= 1;
      setCancelCountdown(secs);
      if (secs <= 0) clearInterval(countdownIntervalRef.current);
    }, 1000);
    submitTimeoutRef.current = setTimeout(() => {
      clearInterval(countdownIntervalRef.current);
      setCancelCountdown(0);
      fireSubmitNow(data, docs);
      setChatHistory(h => [
        ...h,
        {
          role: 'assistant',
          content: `📨 Application sent! Our team is reviewing your file now. You'll hear from us at **${data.email}** within 24 hours. Is there anything else I can help you with? 🚗`,
          source: 'vehicle',
        },
      ]);
    }, 30000);
  };

  // Steps 1–11: conversational text answers
  // Step 12: First Nation question
  // Step 6:  Phone OTP verification
  // Step 14: Driver's licence photo
  // Step 15: Status card photo (First Nations only)
  // Step 16: Void cheque photo → submit
  const DOC_STEPS = [14, 15, 16];
  const isCameraStep = DOC_STEPS.includes(preApprovalStep);

  const handleSellerLeadAnswer = answer => {
    const d = { ...sellerLeadData };
    let nextStep = sellerLeadStep + 1;
    let nextMsg = '';

    if (sellerLeadStep === 1) {
      d.vehicleInfo = answer;
      nextMsg = `Perfect! Here's what happens next: we'll get your ride in front of a network of pre-approved buyers who already have financing in place — that makes your journey so much easier. It'll also go up on skipthedealerships.ai, where both dealerships and private buyers will be looking for exactly what you have.\n\nLet's get the process going — what's your **first name**?`;
    } else if (sellerLeadStep === 2) {
      d.firstName = answer.trim();
      nextMsg = `Thanks, ${d.firstName}! And your **last name**?`;
    } else if (sellerLeadStep === 3) {
      d.lastName = answer.trim();
      nextMsg = `Last thing — what's the best **email address** to reach you? We'll send matched buyer interest here. 📧`;
    } else if (sellerLeadStep === 4) {
      d.email = answer.trim();
      const sellerName = `${d.firstName || ''} ${d.lastName || ''}`.trim();
      fetch('/api/dealer-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'seller-lead',
          sellerName,
          email: d.email,
          vehicleInfo: d.vehicleInfo,
        }),
      }).catch(() => {});
      setLeadInfo({ name: sellerName, email: d.email });
      nextStep = 0;
      nextMsg = `🌟 You're all set, ${d.firstName}! We're matching your **${d.vehicleInfo}** with pre-approved buyers and dealers right now. Keep an eye on **${d.email}** — you'll hear from us soon.\n\nWant to go ahead and create your free listing at skipthedealerships.ai too? It only takes a few minutes and gets you even more exposure.`;
    }

    setSellerLeadData(d);
    setSellerLeadStep(nextStep);
    setChatHistory(h => [
      ...h,
      { role: 'user', content: answer },
      { role: 'assistant', content: nextMsg, source: 'vehicle' },
    ]);
  };

  const handlePreApprovalAnswer = answer => {
    const d = { ...preApprovalData };
    let nextStep = preApprovalStep + 1;
    let nextMsg = '';

    if (preApprovalStep === 1) {
      d.fullName = answer;
      d.firstName = answer.trim().split(' ')[0];
      nextMsg = `Nice to meet you, ${d.firstName}! What is your **date of birth**? (e.g. January 15, 1985)`;
    } else if (preApprovalStep === 2) {
      d.dob = answer;
      nextMsg = `What is your **home address**? (street, city, province, and postal code)`;
    } else if (preApprovalStep === 3) {
      d.address = answer;
      nextMsg = `What is the best **email address** to reach you? We'll send your pre-approval results here. 📧`;
    } else if (preApprovalStep === 4) {
      d.email = answer;
      nextMsg = `What is your **cell phone number**? We'll send you an SMS confirmation once your application is submitted. 📱`;
    } else if (preApprovalStep === 5) {
      d.phone = answer;
      // Fire OTP immediately — non-blocking, best effort
      fetch('/api/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: answer }),
      }).catch(() => {});
      nextStep = 6;
      nextMsg = `Got it! I just sent a **6-digit verification code** to that number. Please enter it here to confirm your phone. 🔐`;
    } else if (preApprovalStep === 6) {
      // OTP verify step — also handle "call me" / "resend" keywords
      const lower = answer.toLowerCase().trim();

      if (/call\s*me|call\s*instead|phone\s*call|can.*call/i.test(answer)) {
        // Customer wants a voice call
        fetch('/api/call-otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: d.phone }),
        }).catch(() => {});
        setChatHistory(h => [
          ...h,
          { role: 'user', content: answer },
          {
            role: 'assistant',
            content: `📞 Calling you now at **${d.phone}**! Pick up — I'll read your code out loud. Then come back here and type it in. 😊`,
            source: 'vehicle',
          },
        ]);
        setPreApprovalData(d);
        return;
      }

      if (/resend|new code|send again|didn.t (get|receive)/i.test(lower)) {
        // Resend SMS
        fetch('/api/send-otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: d.phone }),
        }).catch(() => {});
        setChatHistory(h => [
          ...h,
          { role: 'user', content: answer },
          {
            role: 'assistant',
            content: `📲 New code sent! Check your messages. If it still doesn't arrive, type **"call me"** and I'll read it to you over the phone.`,
            source: 'vehicle',
          },
        ]);
        setPreApprovalData(d);
        return;
      }

      const enteredCode = answer.replace(/\D/g, '').trim();
      fetch('/api/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: d.phone, code: enteredCode }),
      })
        .then(r => r.json())
        .then(result => {
          if (result.verified) {
            setPreApprovalData(prev => ({ ...prev, phoneVerified: true }));
            setPreApprovalStep(7);
            setChatHistory(h => [
              ...h,
              { role: 'user', content: answer },
              {
                role: 'assistant',
                content: `✅ Phone verified! Are you currently employed? (reply: Employed, Self-Employed, or Not Currently Working)`,
                source: 'vehicle',
              },
            ]);
          } else {
            setChatHistory(h => [
              ...h,
              { role: 'user', content: answer },
              {
                role: 'assistant',
                content: `❌ ${result.error ||
                  'Incorrect code.'} Please try again — or type **"call me"** and I'll call your phone and read the code to you. 📞`,
                source: 'vehicle',
              },
            ]);
          }
        })
        .catch(() => {
          setPreApprovalData(prev => ({ ...prev, phoneVerified: false }));
          setPreApprovalStep(7);
          setChatHistory(h => [
            ...h,
            { role: 'user', content: answer },
            {
              role: 'assistant',
              content: `Are you currently employed? (reply: Employed, Self-Employed, or Not Currently Working)`,
              source: 'vehicle',
            },
          ]);
        });
      setPreApprovalData(d);
      return;
    } else if (preApprovalStep === 7) {
      d.employmentStatus = answer;
      const notWorking = /not|no\b|unemployed/i.test(answer);
      if (notWorking) {
        nextMsg = `No problem at all! Do you receive any income from government benefits, pension, investments, or other sources?`;
        nextStep = 9;
      } else {
        nextStep = 8;
        nextMsg = `Where do you work? Please share the **company name**, work address, phone number, and your **job title / position**.`;
      }
    } else if (preApprovalStep === 8) {
      d.employerInfo = answer;
      const co = answer.split(/,|\n/)[0].trim() || 'your company';
      nextStep = 9;
      nextMsg = `🎉 Congratulations on your position at ${co} — that's amazing! Now let's look at income. What is your **total gross income before taxes and deductions**? (monthly or annual, whichever is easier)`;
    } else if (preApprovalStep === 9) {
      d.income = answer;
      nextStep = 10;
      nextMsg = `Do you have any **additional income** to add? For example: rental income, investments, child benefits, pension, or part-time work. Even small amounts can significantly increase your buying power! 💪\n\nIf none, just type "No".`;
    } else if (preApprovalStep === 10) {
      d.additionalIncome = answer;
      nextStep = 11;
      nextMsg = `Will a **spouse or co-applicant** be joining you on this loan? (Yes or No)`;
    } else if (preApprovalStep === 11) {
      d.hasCoApplicant = answer;
      if (/^y/i.test(answer.trim())) {
        nextStep = 12;
        nextMsg = `What is your co-applicant's **full name**, their relationship to you, and their **gross income before taxes**?`;
      } else {
        d.coApplicantInfo = 'N/A';
        nextStep = 13;
        nextMsg = `Perfect! One more thing — are you a member of a **First Nations community**? (Yes or No)\n\nFirst Nations status may qualify you for additional financing benefits and lower rates.`;
      }
    } else if (preApprovalStep === 12) {
      d.coApplicantInfo = answer;
      nextStep = 13;
      nextMsg = `Are you a member of a **First Nations community**? (Yes or No)\n\nFirst Nations status may qualify you for additional financing benefits and lower rates.`;
    } else if (preApprovalStep === 13) {
      d.isFirstNation = answer;
      nextStep = 14;
      const fn = /^y/i.test(answer.trim());
      d._firstNationYes = fn;
      nextMsg = fn
        ? `Thank you for sharing that! As a First Nations member you may qualify for exclusive rates. 🙏\n\nNow let's verify your identity. Please tap the **camera button below** and take a clear photo of the **front of your Driver's Licence** (or any government-issued ID).`
        : `Almost there! To complete your application, we need to verify your identity. Please tap the **camera button below** and take a clear photo of the **front of your Driver's Licence** (or any government-issued ID).`;
    }

    setPreApprovalData(d);
    setPreApprovalStep(nextStep);
    setChatHistory(h => [
      ...h,
      { role: 'user', content: answer },
      { role: 'assistant', content: nextMsg, source: 'vehicle' },
    ]);
  };

  const handleDocumentCapture = e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Reset input so same file can be re-selected if needed
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = ev => {
      const base64 = ev.target.result;
      const docs = { ...preApprovalDocs };
      let nextStep;
      let nextMsg;
      let docLabel;

      if (preApprovalStep === 14) {
        docs.driversLicense = base64;
        docLabel = "Driver's Licence";
        if (preApprovalData._firstNationYes) {
          nextStep = 15;
          nextMsg = `✅ Got it — Driver's Licence received!\n\nNow please tap the camera button and take a clear photo of your **Status Card** (front side).`;
        } else {
          nextStep = 16;
          nextMsg = `✅ Got it — Driver's Licence received!\n\nLast document: please tap the camera button and take a clear photo of a **void cheque** (for setting up pre-authorized bank payments).`;
        }
      } else if (preApprovalStep === 15) {
        docs.statusCard = base64;
        docLabel = 'Status Card';
        nextStep = 16;
        nextMsg = `✅ Status Card received — thank you!\n\nLast document: please tap the camera button and take a clear photo of a **void cheque** (for setting up pre-authorized bank payments).`;
      } else if (preApprovalStep === 16) {
        docs.voidCheque = base64;
        docLabel = 'Void Cheque';
        nextStep = 0;
        submitPreApproval(preApprovalData, docs);
        nextMsg = `🌟 All documents received! Your application is being submitted in **30 seconds**.\n\nIf you made a mistake, tap the **Cancel** button below before the timer runs out. Otherwise sit tight — you'll hear from us at **${preApprovalData.email}** within 24 hours. 📱`;
      }

      setPreApprovalDocs(docs);
      setPreApprovalStep(nextStep);
      setChatHistory(h => [
        ...h,
        { role: 'user', content: `[📎 ${docLabel} uploaded]`, isDoc: true, docThumb: base64 },
        { role: 'assistant', content: nextMsg, source: 'vehicle' },
      ]);
    };
    reader.readAsDataURL(file);
  };

  const suggestions = [
    "What's a fair price for a 2020 RAV4?",
    'What should I check when buying privately?',
    'Are EVs worth it in Canada?',
    'How does buyer verification work?',
  ];

  const voiceStatusLabel = {
    idle: 'Tap the mic to speak',
    listening: 'Listening…',
    thinking: 'Hannah is thinking…',
    speaking: 'Hannah is speaking…',
  };
  const voiceStatusColor = {
    idle: c.textMid,
    listening: '#e53e3e',
    thinking: c.teal,
    speaking: c.navy,
  };
  const micPulse = voiceStatus === 'listening';

  return (
    <div className={css.aiWidgetContainer} style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {chatOpen && (
        <div
          className={css.aiWidgetPanel}
          style={{
            background: '#fff',
            borderRadius: 20,
            boxShadow: '0 20px 60px rgba(15,32,51,0.22)',
            marginBottom: 12,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: `1px solid ${c.border}`,
          }}
        >
          {/* Header */}
          <div
            style={{
              background: c.navy,
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            {/* Hannah avatar — sparkle icon */}
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg,#1A9E85,#0F6E5A)',
                flexShrink: 0,
                border: '2px solid rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              ✨
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                Hannah
                <span
                  style={{
                    background: 'rgba(26,158,133,0.25)',
                    color: '#1A9E85',
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 20,
                    letterSpacing: '0.05em',
                  }}
                >
                  AI
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: online ? '#22c55e' : '#6b7280',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>
                  {online
                    ? 'Team hours · 9 AM–9 PM CT · Hannah is here 24/7'
                    : 'Hannah is here 24/7 · Team hours 9 AM–9 PM CT'}
                </span>
              </div>
            </div>
            {/* Live agent buttons — only during business hours */}
            {online && (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <a
                  href="tel:+12049961818"
                  title="Call a live agent — 204-996-1818"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    background: 'rgba(26,158,133,0.2)',
                    border: '1px solid rgba(26,158,133,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    textDecoration: 'none',
                    color: '#fff',
                  }}
                >
                  📞
                </a>
                <button
                  title="Video call with a live agent"
                  onClick={() => {
                    const room = `https://meet.jit.si/SkipTheDealerships-${Math.random()
                      .toString(36)
                      .slice(2, 8)}`;
                    window.open(room, '_blank', 'width=900,height=700,noopener');
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    background: 'rgba(26,158,133,0.2)',
                    border: '1px solid rgba(26,158,133,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    cursor: 'pointer',
                    color: '#fff',
                    border: 'none',
                  }}
                >
                  🎥
                </button>
              </div>
            )}
            {/* Language selector */}
            <select
              aria-label="Chat language"
              data-testid="hannah-language"
              value={language}
              onChange={e => setLanguage(e.target.value)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 6,
                color: '#fff',
                fontSize: 11,
                padding: '3px 6px',
                cursor: 'pointer',
                maxWidth: 110,
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              {LANGUAGES.map(([code, , name, flag]) => (
                <option key={code} value={code} style={{ background: '#0F2033', color: '#fff' }}>
                  {flag} {name}
                </option>
              ))}
            </select>
            <button
              onClick={() => setChatOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                fontSize: 22,
                cursor: 'pointer',
                lineHeight: 1,
                padding: '0 0 0 4px',
              }}
            >
              ×
            </button>
          </div>

          {/* Mode Toggle */}
          <div
            style={{
              display: 'flex',
              borderBottom: `1px solid ${c.border}`,
              background: '#f8fafc',
            }}
          >
            {['chat', 'voice'].map(m => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  flex: 1,
                  padding: '9px 0',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  color: mode === m ? c.teal : c.textMid,
                  borderBottom: mode === m ? `2px solid ${c.teal}` : '2px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                {m === 'chat' ? '💬 Chat' : '🎙️ Voice'}
              </button>
            ))}
          </div>

          {/* ── CHAT MODE ── */}
          {mode === 'chat' && (
            <>
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  background: '#F8FAFC',
                }}
              >
                {chatHistory.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-end',
                      gap: 6,
                    }}
                  >
                    {msg.role === 'assistant' && (
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg,#1A9E85,#0F6E5A)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          flexShrink: 0,
                          marginBottom: 2,
                        }}
                      >
                        ✨
                      </div>
                    )}
                    <div
                      style={{
                        maxWidth: '78%',
                        padding: '10px 14px',
                        borderRadius:
                          msg.role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
                        background:
                          msg.role === 'user'
                            ? `linear-gradient(135deg, ${c.teal}, #157A68)`
                            : '#fff',
                        color: msg.role === 'user' ? '#fff' : c.navy,
                        fontSize: 13,
                        lineHeight: 1.6,
                        boxShadow:
                          msg.role === 'user'
                            ? '0 2px 8px rgba(26,158,133,0.3)'
                            : '0 1px 4px rgba(15,32,51,0.08)',
                        border: msg.role === 'assistant' ? `1px solid #E8EEF3` : 'none',
                      }}
                    >
                      {msg.isDoc && msg.docThumb ? (
                        <div>
                          <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.85 }}>
                            {msg.content}
                          </div>
                          <img
                            src={msg.docThumb}
                            alt="document"
                            style={{
                              width: '100%',
                              maxWidth: 160,
                              borderRadius: 8,
                              border: '2px solid rgba(255,255,255,0.3)',
                              display: 'block',
                            }}
                          />
                        </div>
                      ) : (
                        msg.content
                      )}
                      {msg.role === 'assistant' && msg.source && (
                        <div
                          style={{
                            fontSize: 10,
                            marginTop: 6,
                            color: c.textLight,
                            borderTop: '1px solid #EEF4F9',
                            paddingTop: 5,
                          }}
                        >
                          {msg.source === 'market' ? '📊 Market Intelligence' : '🚗 Vehicle Expert'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg,#1A9E85,#0F6E5A)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      ✨
                    </div>
                    <div
                      style={{
                        padding: '12px 16px',
                        background: '#fff',
                        borderRadius: '4px 18px 18px 18px',
                        border: '1px solid #E8EEF3',
                        boxShadow: '0 1px 4px rgba(15,32,51,0.08)',
                        display: 'flex',
                        gap: 5,
                        alignItems: 'center',
                      }}
                    >
                      {[0, 1, 2].map(i => (
                        <div
                          key={i}
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: c.teal,
                            animation: `hannahDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {chatHistory.length === 1 && (
                <div style={{ padding: '0 12px 8px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setChatInput(s)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 20,
                        border: `1px solid ${c.tealBorder}`,
                        background: c.tealLight,
                        color: c.teal,
                        fontSize: 11,
                        cursor: 'pointer',
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* 30-second cancel window after submission */}
              {cancelCountdown > 0 && (
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#fff8e1',
                    borderTop: '2px solid #f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: '#92400e',
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                        marginBottom: 4,
                      }}
                    >
                      ⏳ Submitting in {cancelCountdown}s — made a mistake?
                    </div>
                    <div
                      style={{
                        height: 4,
                        background: '#fde68a',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          borderRadius: 4,
                          background: '#f59e0b',
                          width: `${(cancelCountdown / 30) * 100}%`,
                          transition: 'width 1s linear',
                        }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={cancelSubmit}
                    style={{
                      background: '#dc2626',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '7px 14px',
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    ✕ Cancel
                  </button>
                </div>
              )}

              {preApprovalStep > 0 && (
                <div
                  style={{
                    padding: '8px 14px',
                    background: '#f0fbf8',
                    borderTop: `1px solid ${c.tealBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: c.teal,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        marginBottom: 4,
                      }}
                    >
                      {isCameraStep ? '📄 Document Verification' : '📋 Pre-Approval in Progress'}
                    </div>
                    <div
                      style={{
                        height: 4,
                        background: '#DDF0EC',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          background: isCameraStep ? '#0F6E5A' : c.teal,
                          borderRadius: 4,
                          width: `${Math.min((preApprovalStep / 16) * 100, 100)}%`,
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{ fontSize: 10, color: c.teal, fontWeight: 700, whiteSpace: 'nowrap' }}
                  >
                    Step {preApprovalStep} of 16
                  </div>
                </div>
              )}

              {blocked && (
                <div
                  style={{
                    padding: '10px 16px',
                    background: '#fff3f3',
                    borderTop: `1px solid #fecaca`,
                    textAlign: 'center',
                    fontSize: 12,
                    color: '#b91c1c',
                  }}
                >
                  This conversation has ended. Refresh to start a new one.
                </div>
              )}
              {/* Hidden file input for document capture — camera first on mobile */}
              <input
                ref={docInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleDocumentCapture}
                style={{ display: 'none' }}
              />

              {!blocked && isCameraStep && (
                <div
                  style={{
                    padding: '10px 12px',
                    borderTop: `1px solid ${c.border}`,
                    background: '#fff',
                  }}
                >
                  <div
                    style={{ fontSize: 11, color: c.textMid, textAlign: 'center', marginBottom: 8 }}
                  >
                    {preApprovalStep === 14 &&
                      "Take a clear photo of the front of your Driver's Licence or government ID"}
                    {preApprovalStep === 15 &&
                      'Take a clear photo of your Status Card (front side)'}
                    {preApprovalStep === 16 &&
                      'Take a clear photo of a void cheque (for bank payments)'}
                  </div>
                  <button
                    onClick={() => docInputRef.current && docInputRef.current.click()}
                    style={{
                      width: '100%',
                      padding: '13px',
                      borderRadius: 12,
                      border: `2px dashed ${c.teal}`,
                      background: c.tealLight,
                      color: c.teal,
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                      fontFamily: 'DM Sans, sans-serif',
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="22"
                      height="22"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                    Take Photo / Upload Image
                  </button>
                </div>
              )}

              {/* Shown before the composer, not after. A notice a person reads
                  once they have already typed is not a disclosure. Wording and
                  version come from the shared contract registry, so this is the
                  same sentence the server records agreement to. */}
              {!blocked &&
                !isCameraStep &&
                !chatConsented &&
                !chatDeclined &&
                chatConsentAvailable && (
                  <AiConsentGate
                    purpose={PURPOSES.AI_CHAT}
                    locale={language}
                    heading="Before we start chatting"
                    icon="✨"
                    acceptLabel="I understand — start chatting"
                    declineLabel="Not now"
                    onAccept={() => {
                      rememberConsent(PURPOSES.AI_CHAT, language);
                      setChatConsented(true);
                    }}
                    onDecline={() => {
                      rememberDecline(PURPOSES.AI_CHAT, language);
                      setChatDeclined(true);
                    }}
                  />
                )}

              {/* What "Not now" leaves behind.
                  The notice closes and stays closed, the rest of the site is
                  untouched, and the chat stays off — no composer, so nothing can
                  be typed and nothing can be sent. The way back is visible,
                  because a refusal that cannot be reversed is a trap rather than
                  a choice. */}
              {!blocked && !isCameraStep && chatDeclined && chatConsentAvailable && (
                <div
                  data-testid="ai-chat-declined"
                  style={{
                    padding: '14px 12px',
                    borderTop: `1px solid ${c.border}`,
                    background: '#fff',
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: c.textMid,
                  }}
                >
                  AI chat is off, so nothing you do here is sent to an AI provider. Browse away — or
                  call, or email sales@skipthedealerships.ca to reach a person.
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      data-testid="ai-chat-reconsider"
                      onClick={() => {
                        forgetConsent(PURPOSES.AI_CHAT);
                        setChatDeclined(false);
                      }}
                      style={{
                        padding: '9px 15px',
                        fontSize: 13,
                        fontWeight: 700,
                        color: c.textMid,
                        background: 'transparent',
                        border: `1px solid ${c.border}`,
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Turn on AI chat
                    </button>
                  </div>
                </div>
              )}

              {/* Fail closed. In a language with no reviewed translation of the
                  privacy notice, the feature is switched off rather than shown
                  behind an English contract the person did not choose to read. */}
              {!blocked && !isCameraStep && !chatConsentAvailable && (
                <div
                  data-testid="ai-consent-unavailable"
                  style={{
                    padding: 12,
                    borderTop: `1px solid ${c.border}`,
                    background: '#F7FAFB',
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: c.textMid,
                  }}
                >
                  This AI feature is only available in English right now, because our privacy notice
                  has not yet been reviewed in your language. Switch to English to use it, or email
                  sales@skipthedealerships.ca.
                </div>
              )}

              {/* Asked once contact details exist, before they are forwarded.
                  Declining leaves the chat working and the CRM write off — the
                  server refuses the HubSpot call without this consent, so
                  dismissing this is a real refusal, not a postponed yes. */}
              {!blocked &&
                !isCameraStep &&
                chatConsented &&
                leadInfo &&
                (leadInfo.email || leadInfo.phone) &&
                !crmConsented &&
                !crmPromptDismissed && (
                  <AiConsentGate
                    purpose={PURPOSES.CRM_LEAD}
                    locale={language}
                    compact
                    acceptLabel="Yes, follow up with me"
                    declineLabel="No, just chat"
                    onAccept={() => setCrmConsented(true)}
                    onDecline={() => setCrmPromptDismissed(true)}
                  />
                )}

              {/* The microphone's own notice, shown in place of the composer so
                  it cannot be missed, and before any recording starts. */}
              {!blocked && !isCameraStep && chatConsented && micPrompt && !micConsented && (
                <AiConsentGate
                  purpose={PURPOSES.TRANSCRIPTION}
                  locale={language}
                  acceptLabel="Allow voice input"
                  declineLabel="Keep typing instead"
                  onAccept={() => {
                    setMicConsented(true);
                    setMicPrompt(false);
                  }}
                  onDecline={() => setMicPrompt(false)}
                />
              )}

              {!blocked && !isCameraStep && chatConsented && chatConsentAvailable && (
                <div
                  style={{
                    padding: '10px 12px',
                    borderTop: `1px solid ${c.border}`,
                    display: 'flex',
                    gap: 8,
                    background: '#fff',
                    alignItems: 'center',
                  }}
                >
                  <input
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendMessage()}
                    placeholder={
                      micRecording ? 'Listening… tap mic to stop' : 'Ask Hannah anything…'
                    }
                    style={{
                      flex: 1,
                      padding: '10px 14px',
                      borderRadius: 12,
                      border: `1.5px solid ${
                        micRecording ? '#e53e3e' : chatInput.trim() ? c.tealBorder : c.border
                      }`,
                      fontSize: 13,
                      fontFamily: 'DM Sans, sans-serif',
                      outline: 'none',
                      color: c.navy,
                      background: micRecording ? '#fff5f5' : '#F8FAFC',
                      transition: 'all 0.15s',
                    }}
                  />
                  {/* Mic button */}
                  <button
                    onClick={toggleMic}
                    aria-label={micRecording ? 'Stop recording' : 'Speak to Hannah'}
                    data-testid="hannah-mic"
                    title={micRecording ? 'Stop recording' : 'Speak to Hannah'}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      border: 'none',
                      flexShrink: 0,
                      background: micRecording ? '#e53e3e' : '#F0F4F8',
                      color: micRecording ? '#fff' : '#6B8499',
                      cursor: 'pointer',
                      transition: 'all 0.18s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: micRecording ? '0 2px 8px rgba(229,62,62,0.4)' : 'none',
                      animation: micRecording ? 'hannahPulse 1.2s ease-in-out infinite' : 'none',
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill={micRecording ? '#fff' : '#6B8499'}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 1a4 4 0 0 1 4 4v7a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4z" />
                      <path
                        d="M19 10v2a7 7 0 0 1-14 0v-2"
                        stroke={micRecording ? '#fff' : '#6B8499'}
                        strokeWidth="2"
                        strokeLinecap="round"
                        fill="none"
                      />
                      <line
                        x1="12"
                        y1="19"
                        x2="12"
                        y2="23"
                        stroke={micRecording ? '#fff' : '#6B8499'}
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <line
                        x1="8"
                        y1="23"
                        x2="16"
                        y2="23"
                        stroke={micRecording ? '#fff' : '#6B8499'}
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  {/* Send button */}
                  <button
                    onClick={sendMessage}
                    disabled={chatLoading || !chatInput.trim()}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      border: 'none',
                      flexShrink: 0,
                      background: chatInput.trim()
                        ? `linear-gradient(135deg, ${c.teal}, #157A68)`
                        : '#E8EEF3',
                      color: chatInput.trim() ? '#fff' : '#9AB0C0',
                      fontWeight: 700,
                      fontSize: 18,
                      cursor: chatInput.trim() ? 'pointer' : 'default',
                      transition: 'all 0.18s',
                      boxShadow: chatInput.trim() ? '0 2px 8px rgba(26,158,133,0.35)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ↑
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── VOICE MODE ── */}
          {mode === 'voice' && (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 24px',
                gap: 20,
                background: '#f8fafc',
              }}
            >
              {/* Shown in place of the orb, before the recogniser can start.
                  Its own contract: this path streams audio to the browser
                  vendor's speech service, not to us and not to Whisper. */}
              {speechPrompt && !speechConsented && (
                <AiConsentGate
                  purpose={PURPOSES.SPEECH_RECOGNITION}
                  locale={language}
                  compact
                  acceptLabel="Allow voice input"
                  declineLabel="Go back to typing"
                  onAccept={() => {
                    setSpeechConsented(true);
                    setSpeechPrompt(false);
                  }}
                  onDecline={() => {
                    setSpeechPrompt(false);
                    setMode('chat');
                  }}
                />
              )}

              {/* Animated mic / status orb */}
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {micPulse && (
                  <div
                    style={{
                      position: 'absolute',
                      width: 110,
                      height: 110,
                      borderRadius: '50%',
                      background: 'rgba(229,62,62,0.15)',
                      animation: 'hannahPulse 1.2s ease-in-out infinite',
                    }}
                  />
                )}
                {voiceStatus === 'speaking' && (
                  <div
                    style={{
                      position: 'absolute',
                      width: 110,
                      height: 110,
                      borderRadius: '50%',
                      background: 'rgba(26,158,133,0.15)',
                      animation: 'hannahPulse 1s ease-in-out infinite',
                    }}
                  />
                )}
                <button
                  data-testid="hannah-voice-mic"
                  aria-label={voiceStatus === 'listening' ? 'Stop listening' : 'Start voice input'}
                  onClick={
                    voiceStatus === 'listening'
                      ? stopListening
                      : voiceStatus === 'speaking'
                      ? stopSpeaking
                      : startListening
                  }
                  disabled={voiceStatus === 'thinking'}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    border: 'none',
                    cursor: voiceStatus === 'thinking' ? 'default' : 'pointer',
                    background:
                      voiceStatus === 'listening'
                        ? '#e53e3e'
                        : voiceStatus === 'speaking'
                        ? c.teal
                        : c.navy,
                    color: '#fff',
                    fontSize: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                    transition: 'background 0.2s',
                  }}
                >
                  {voiceStatus === 'listening'
                    ? '⏹'
                    : voiceStatus === 'thinking'
                    ? '⏳'
                    : voiceStatus === 'speaking'
                    ? '⏸'
                    : '🎙️'}
                </button>
              </div>

              {/* Status label */}
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: voiceStatusColor[voiceStatus],
                  textAlign: 'center',
                }}
              >
                {voiceStatusLabel[voiceStatus]}
              </div>

              {/* What the user said */}
              {voiceTranscript && voiceStatus !== 'idle' && (
                <div
                  style={{
                    background: '#fff',
                    border: `1px solid ${c.border}`,
                    borderRadius: 12,
                    padding: '10px 14px',
                    width: '100%',
                    fontSize: 13,
                    color: c.navy,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{ fontSize: 10, color: c.textMid, display: 'block', marginBottom: 4 }}
                  >
                    You said
                  </span>
                  {voiceTranscript}
                </div>
              )}

              {/* Hannah's spoken reply (also shown as text) */}
              {voiceReply && (voiceStatus === 'speaking' || voiceStatus === 'idle') && (
                <div
                  style={{
                    background: c.tealLight,
                    border: `1px solid ${c.tealBorder}`,
                    borderRadius: 12,
                    padding: '10px 14px',
                    width: '100%',
                    fontSize: 13,
                    color: c.navy,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      color: c.teal,
                      fontWeight: 700,
                      display: 'block',
                      marginBottom: 4,
                    }}
                  >
                    ✨ Hannah
                  </span>
                  {voiceReply}
                </div>
              )}

              {voiceError && (
                <div
                  style={{ fontSize: 12, color: '#e53e3e', textAlign: 'center', padding: '0 8px' }}
                >
                  {voiceError}
                </div>
              )}

              <div
                style={{ fontSize: 11, color: c.textLight, textAlign: 'center', lineHeight: 1.5 }}
              >
                {voiceStatus === 'idle' && !voiceReply
                  ? 'Works in Chrome & Edge. Hannah listens, then speaks her answer.'
                  : ''}
              </div>

              <style>{`
                      @keyframes hannahPulse {
                        0% { transform: scale(1); opacity: 0.8; }
                        50% { transform: scale(1.3); opacity: 0.3; }
                        100% { transform: scale(1); opacity: 0.8; }
                      }
                      @keyframes hannahDot {
                        0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
                        30% { transform: translateY(-5px); opacity: 1; }
                      }
                    `}</style>
            </div>
          )}
        </div>
      )}

      <button
        aria-label={chatOpen ? 'Close Hannah chat' : 'Open Hannah chat'}
        data-testid="hannah-toggle"
        onClick={() => {
          const opening = !chatOpen;
          setChatOpen(o => !o);
          if (opening && !newsRead) {
            try {
              localStorage.setItem('skip_news_read', SUNNY_NEWS.id);
            } catch {}
            setNewsRead(true);
            setTimeout(() => {
              setChatHistory(h => [
                ...h,
                { role: 'assistant', content: SUNNY_NEWS.message, source: 'news' },
              ]);
            }, 400);
          }
        }}
        style={{
          width: 62,
          height: 62,
          borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.9)',
          background: chatOpen ? c.navy : 'linear-gradient(135deg,#1A9E85,#0F6E5A)',
          boxShadow: '0 8px 28px rgba(26,158,133,0.45)',
          cursor: 'pointer',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          marginLeft: 'auto',
          position: 'relative',
        }}
      >
        {chatOpen ? (
          <span style={{ color: '#fff', fontSize: 28, lineHeight: 1, paddingBottom: 2 }}>×</span>
        ) : (
          <span style={{ fontSize: 30, lineHeight: 1 }}>✨</span>
        )}
        {/* News unread dot */}
        {!chatOpen && !newsRead && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: '#ef4444',
              border: '2px solid #fff',
              animation: 'sunnyPulse 1.5s ease-in-out infinite',
            }}
          />
        )}
      </button>
    </div>
  );
}
